// This file is auto-generated by @hey-api/openapi-ts

export type ClientOptions = {
  baseUrl: string;
};

/**
 * EnrollmentEventResponse
 */
export type EnrollmentEventResponse = {
  /**
   * Recorded
   *
   * Whether the event was recorded.
   */
  recorded?: boolean;
};

/**
 * EnrollmentOptOutEventRequest
 */
export type EnrollmentOptOutEventRequest = {
  /**
   * Optional prospect identifiers. Omit or set to null when the partner does not yet have identifying data for this customer.
   */
  prospect?: EventProspectIdentity | null;
  /**
   * Account Uuid
   *
   * Light account UUID, if the partner has already created one for this prospect.
   */
  account_uuid?: string | null;
  /**
   * Occurred At
   *
   * When the event occurred on the partner's side (ISO 8601 with timezone). Defaults to the server receive time if omitted.
   */
  occurred_at?: string | null;
  /**
   * Plan Uuids
   *
   * UUIDs of plans shown to the customer, from the `plans/request` response.
   */
  plan_uuids?: Array<string> | null;
  /**
   * Properties
   *
   * Optional partner-defined key-value data attached to the event in analytics. When the customer has not yet created a Light account, include `esi_id` and `service_address` if you have them. Any additional keys are allowed.
   */
  properties?: {
    [key: string]: unknown;
  } | null;
  /**
   * Event
   *
   * Record when a customer declines to continue enrollment.
   */
  event: "enrollment_opt_out";
  /**
   * Reason
   *
   * Machine-readable reason code from `GET /accounts/enroll/opt-out/reasons`, `other`, or a partner-defined code.
   */
  reason?: string | null;
  /**
   * Label
   *
   * Human-readable reason text shown to the customer.
   */
  label?: string | null;
  /**
   * Which enrollment flow the customer opted out of. One of `enrollment_switch`, `enrollment_move_in`, or `renewal`.
   */
  flow?: OptOutFilter | null;
  /**
   * Detail
   *
   * Free-text follow-up the customer provided after selecting a reason (e.g. which competitor plan, what question they had). Corresponds to the `follow_up_placeholder` on the selected reason.
   */
  detail?: string | null;
};

/**
 * EventProspectIdentity
 */
export type EventProspectIdentity = {
  /**
   * Email
   *
   * Prospect's email address.
   */
  email?: string | null;
};

/**
 * OptOutFilter
 */
export enum OptOutFilter {
  EnrollmentSwitch = "enrollment_switch",
  EnrollmentMoveIn = "enrollment_move_in",
  Renewal = "renewal",
}

/**
 * PlansViewedEventRequest
 */
export type PlansViewedEventRequest = {
  /**
   * Optional prospect identifiers. Omit or set to null when the partner does not yet have identifying data for this customer.
   */
  prospect?: EventProspectIdentity | null;
  /**
   * Account Uuid
   *
   * Light account UUID, if the partner has already created one for this prospect.
   */
  account_uuid?: string | null;
  /**
   * Occurred At
   *
   * When the event occurred on the partner's side (ISO 8601 with timezone). Defaults to the server receive time if omitted.
   */
  occurred_at?: string | null;
  /**
   * Plan Uuids
   *
   * UUIDs of plans shown to the customer, from the `plans/request` response.
   */
  plan_uuids?: Array<string> | null;
  /**
   * Properties
   *
   * Optional partner-defined key-value data attached to the event in analytics. When the customer has not yet created a Light account, include `esi_id` and `service_address` if you have them. Any additional keys are allowed.
   */
  properties?: {
    [key: string]: unknown;
  } | null;
  /**
   * Event
   *
   * Record when plans are rendered in your UI.
   */
  event: "plans_viewed";
};

/**
 * AccountTokenResponse
 */
export type AccountTokenResponse = {
  /**
   * Token
   *
   * Short-lived client access token for the account.
   */
  token: string;
  /**
   * Expires At
   *
   * When the token expires.
   */
  expires_at: string;
};

/**
 * FlowLoginResponse
 */
export type FlowLoginResponse = {
  /**
   * Login Link
   *
   * Pre-authenticated URL that launches the enrollment flow for the customer.
   */
  login_link: string;
  /**
   * Scope
   *
   * Scope granted to the session.
   */
  scope: string;
  /**
   * Behavior
   *
   * How the pre-authenticated link is delivered.
   */
  behavior: string;
  /**
   * Expires At
   *
   * When the login link expires.
   */
  expires_at: string;
};

/**
 * CreateFlowLoginRequest
 */
export type CreateFlowLoginRequest = {
  /**
   * Scope of the session.
   */
  scope: FlowScope;
  /**
   * How the pre-authenticated link is delivered. "embedded" launches Embedded Flow and "hosted" launches Light Hosted Flow.
   */
  behavior?: FlowLoginBehavior;
  /**
   * Attribution
   *
   * Optional partner-defined attribution tag recorded with the session.
   */
  attribution?: string | null;
  /**
   * Plan Group
   *
   * UUID of the plan group to preselect for the session.
   */
  plan_group?: string | null;
};

/**
 * FlowLoginBehavior
 */
export enum FlowLoginBehavior {
  Embedded = "embedded",
  Hosted = "hosted",
}

/**
 * FlowScope
 */
export enum FlowScope {
  Enrollment = "enrollment",
  UpdatePaymentMethod = "update-payment-method",
  Documents = "documents",
  Billing = "billing",
  Renewal = "renewal",
  ConnectBattery = "connect-battery",
}

/**
 * AppLoginResponse
 */
export type AppLoginResponse = {
  /**
   * Login Link
   *
   * Pre-authenticated URL that logs the customer into the hosted application.
   */
  login_link: string;
  /**
   * Destination the customer lands on after login.
   */
  route: AppLoginRoute;
  /**
   * Expires At
   *
   * When the login link expires.
   */
  expires_at: string;
};

/**
 * AppLoginRoute
 *
 * Public API destinations for app-login links.
 *
 * These semantic values decouple partners and the backend from standalone
 * client URL paths, which are owned by prebuilt-ui.
 */
export enum AppLoginRoute {
  Home = "home",
  Enroll = "enroll",
  Billing = "billing",
  Documents = "documents",
  UpdatePaymentMethod = "update_payment_method",
}

/**
 * CreateAppLoginRequest
 */
export type CreateAppLoginRequest = {
  /**
   * Where to send the user after login in Light Hosted Application.
   */
  route?: AppLoginRoute;
  /**
   * Plan Group
   *
   * UUID of the plan group to preselect in the hosted application.
   */
  plan_group?: string | null;
};

/**
 * DeviceType
 */
export enum DeviceType {
  Battery = "BATTERY",
  Evse = "EVSE",
  Vehicle = "VEHICLE",
}

/**
 * Input
 */
export type Input = {
  /**
   * Limit
   *
   * Maximum number of results to return per page.
   */
  limit?: number;
  /**
   * Offset
   *
   * Number of results to skip before the returned page.
   */
  offset?: number;
};

/**
 * ListAccountsQuery
 */
export type ListAccountsQuery = {
  /**
   * Filter
   *
   * Account lifecycle states to filter by.
   */
  filter?: Array<string> | null;
  /**
   * Search
   *
   * Free-text search over account name and email.
   */
  search?: string | null;
  /**
   * Email
   *
   * Filters to accounts with this email address.
   */
  email?: string | null;
  /**
   * Plan Group Uuid
   *
   * Filters to accounts on these plans.
   */
  plan_group_uuid?: Array<string> | null;
  /**
   * External Id
   *
   * Filters to the account with this partner-assigned external ID.
   */
  external_id?: string | null;
  /**
   * Filters to accounts on plans of this type.
   */
  plan_type?: PlanType | null;
  /**
   * Include Fallback Plans
   *
   * Whether `plan_group_uuid` also matches accounts on the plan's fallback plan.
   */
  include_fallback_plans?: boolean;
  /**
   * Device Type
   *
   * Narrows `device_status` and `missing_program_device` to these device types. On its own it narrows both, so it returns accounts whose device is of these types as well as accounts on a program expecting one with nothing enrolled.
   */
  device_type?: Array<DeviceType> | null;
  /**
   * Device Status
   *
   * Only accounts whose device is in one of these statuses. `inactive` matches a device that was unenrolled or decommissioned, which is narrower than `missing_program_device` — that also covers accounts which never connected one.
   */
  device_status?: Array<PartnerDeviceStatus> | null;
  /**
   * Missing Program Device
   *
   * Only accounts on a device program with no device currently enrolled. Combines with `device_status` as a union, and with `device_type` by the types the program expects.
   */
  missing_program_device?: boolean;
  /**
   * Expiring In Days
   *
   * Only accounts whose active term contract ends within this many days and whose consumer has not accepted a renewal offer yet. Contracts already ended, or cut short because the location is leaving service, are excluded. Also orders the list by soonest end date first.
   */
  expiring_in_days?: number | null;
};

/**
 * PartnerDeviceStatus
 *
 * A device's status as the API reports it.
 *
 * Deliberately coarser than :class:`DeviceStatus`, which is a sync state
 * machine: partners need to know whether a device is doing its job and
 * whether anyone has to act, not which step of the lifecycle it is on. New
 * lifecycle states map into these without changing the API.
 */
export enum PartnerDeviceStatus {
  Active = "active",
  NeedsAttention = "needs_attention",
  Inactive = "inactive",
}

/**
 * PlanType
 *
 * * **TERM** - plan agreed to for a set term
 * * **MONTH_TO_MONTH** - plan who's rate changes every month
 * * **FALLBACK** - plan associated with a ``TERM`` plan that's used to
 * move expired contracts onto
 */
export enum PlanType {
  Term = "TERM",
  MonthToMonth = "MONTH_TO_MONTH",
  Fallback = "FALLBACK",
}

/**
 * AcceptanceRequirement
 */
export type AcceptanceRequirement = {
  value?: AcceptanceValue | null;
  unsatisfied?: Unsatisfied | null;
};

/**
 * AcceptanceValue
 */
export type AcceptanceValue = {
  /**
   * Rate Id
   */
  rate_id: string;
  /**
   * Accepted By
   */
  accepted_by?: string | null;
  /**
   * Accepted At
   */
  accepted_at?: string | null;
};

/**
 * AccountApp
 */
export type AccountApp = {
  /**
   * Uuid
   *
   * Unique identifier of the app.
   */
  uuid: string;
  /**
   * Name
   *
   * Display name of the app.
   */
  name: string;
  /**
   * Stripe Public Key
   *
   * Stripe publishable key for the app's payment environment.
   */
  stripe_public_key?: string | null;
  /**
   * Is Sandbox
   *
   * Whether the app is in sandbox mode.
   */
  is_sandbox: boolean;
  /**
   * Organization Uuid
   *
   * Unique identifier of the organization that owns the app.
   */
  organization_uuid: string;
  /**
   * Organization Name
   *
   * Name of the organization that owns the app.
   */
  organization_name: string;
};

/**
 * AccountEnrollment
 */
export type AccountEnrollment = {
  /**
   * Has Accepted Plan
   *
   * Whether the customer has accepted a plan.
   */
  has_accepted_plan?: boolean;
  /**
   * Has Payment Method
   *
   * Whether the customer has a payment method on file.
   */
  has_payment_method?: boolean;
  /**
   * Is Billing Address Confirmed
   *
   * Whether the customer confirmed their billing address.
   */
  is_billing_address_confirmed?: boolean;
  /**
   * Is Identity Verified
   *
   * Whether the customer's identity is verified.
   */
  is_identity_verified?: boolean;
  /**
   * Are Vehicles Enrolled
   *
   * Whether the customer has at least one vehicle enrolled.
   */
  are_vehicles_enrolled?: boolean;
  /**
   * Can Finalize Enrollment
   *
   * Whether enrollment can now be finalized.
   */
  can_finalize_enrollment?: boolean;
  /**
   * Is Enrollment Finalized
   *
   * Whether enrollment is finalized.
   */
  is_enrollment_finalized?: boolean;
  /**
   * Finalized At
   *
   * When enrollment was finalized.
   */
  finalized_at?: string | null;
  /**
   * Is Service Active
   *
   * Whether service is active at any of the account's locations.
   */
  is_service_active?: boolean;
  /**
   * Has Usage History
   *
   * Whether usage history is available for any of the account's locations.
   */
  has_usage_history?: boolean;
  /**
   * Requires Credit Verification
   *
   * Whether the customer must complete credit verification.
   */
  requires_credit_verification?: boolean;
  requirements?: Requirements | null;
};

/**
 * AccountLinks
 */
export type AccountLinks = {
  /**
   * Billing Url
   *
   * Custom billing URL for the account.
   */
  billing_url?: string | null;
  /**
   * Renewals Url
   *
   * Custom renewal URL for the account.
   */
  renewals_url?: string | null;
};

/**
 * AccountLocationMetadata
 */
export type AccountLocationMetadata = {
  /**
   * Solar System Size Watts
   *
   * Rated solar system size in watts.
   */
  solar_system_size_watts?: number | null;
  /**
   * Solar Estimated Annual Kwh
   *
   * Estimated annual solar production in kWh.
   */
  solar_estimated_annual_kwh?: number | null;
  /**
   * Battery Capacity Kwh
   *
   * Total energy storage capacity of all batteries at the service location, in kWh.
   */
  battery_capacity_kwh?: string | null;
};

/**
 * AccountOptOut
 */
export type AccountOptOut = {
  /**
   * What the customer opted out of.
   */
  stage: Stage;
  /**
   * Reason
   *
   * Reason code the customer selected (`unspecified` when the customer did not select a reason).
   */
  reason: string;
  /**
   * Detail
   *
   * Free-text follow-up the customer gave for the reason.
   */
  detail?: string | null;
  /**
   * Plan Uuids
   *
   * UUIDs of the plans shown to the customer before they opted out.
   */
  plan_uuids?: Array<string>;
  /**
   * Opted Out At
   *
   * When the customer opted out.
   */
  opted_out_at: string;
};

/**
 * AccountStatus
 *
 * Account statuses, in chronological order (account lifecycle order).
 */
export enum AccountStatus {
  Unresolved = "unresolved",
  MissingAddress = "missing_address",
  PlanNotAccepted = "plan_not_accepted",
  MissingIdentity = "missing_identity",
  MissingPaymentMethod = "missing_payment_method",
  EnrollmentNotFinalized = "enrollment_not_finalized",
  VerificationPending = "verification_pending",
  CreditCheckPending = "credit_check_pending",
  CreditNotFound = "credit_not_found",
  CreditDeclined = "credit_declined",
  CreditCheckFrozen = "credit_check_frozen",
  CreditCheckError = "credit_check_error",
  EnrollmentBlockedSwitchHold = "enrollment_blocked_switch_hold",
  EnrollmentBlockedVerificationHold = "enrollment_blocked_verification_hold",
  EnrollmentBlockedDeactivated = "enrollment_blocked_deactivated",
  UtilityBlocked = "utility_blocked",
  EnrollmentPendingClientAgent = "enrollment_pending_client_agent",
  EnrollmentPendingDeferred = "enrollment_pending_deferred",
  ServicePending = "service_pending",
  UtilitySubmitted = "utility_submitted",
  Active = "active",
  CancelledPreStart = "cancelled_pre_start",
  ExpirePreActive = "expire_pre_active",
  CancelledPostStart = "cancelled_post_start",
  DuplicateEnrollment = "duplicate_enrollment",
}

/**
 * AddressValue
 *
 * Some fields may be empty strings, use `has_full_address` to gate
 * functionality that requires a complete address.
 */
export type AddressValue = {
  /**
   * Address 1
   */
  address_1: string;
  /**
   * Address 2
   */
  address_2?: string | null;
  /**
   * City
   */
  city: string;
  /**
   * State
   */
  state: string;
  /**
   * Postal Code
   */
  postal_code: string;
};

/**
 * AppAccountResponse
 */
export type AppAccountResponse = {
  /**
   * Uuid
   *
   * Unique identifier of the account.
   */
  uuid: string;
  /**
   * External Id
   *
   * Partner-assigned identifier for the account.
   */
  external_id?: string | null;
  /**
   * Account Number
   *
   * Light account number for the account.
   */
  account_number?: string | null;
  /**
   * Current high-level status of the account.
   */
  status: StatusType;
  /**
   * Detailed status reason for the account.
   */
  status_reason: AccountStatus;
  /**
   * Created At
   *
   * When the account was created.
   */
  created_at: string;
  /**
   * First Name
   *
   * Customer's first name.
   */
  first_name: string;
  /**
   * Last Name
   *
   * Customer's last name.
   */
  last_name: string;
  /**
   * Email
   *
   * Customer's email address.
   */
  email: string;
  /**
   * Phone Number
   *
   * Customer's phone number.
   */
  phone_number?: string | null;
  /**
   * Enrollment progress for the account.
   */
  enrollment?: AccountEnrollment;
  /**
   * Has Client Agent
   *
   * Whether a power-of-attorney client agent is on file for the account.
   */
  has_client_agent?: boolean;
  /**
   * Preliminary service address provided for the account, before it is verified.
   */
  preliminary_address?: PreliminaryAddress | null;
  /**
   * Partner Payment Method On File
   *
   * Deprecated; always null.
   *
   * @deprecated
   */
  partner_payment_method_on_file?: null;
  /**
   * Additional metadata about the account's service location, such as solar and battery details.
   */
  location_metadata?: AccountLocationMetadata | null;
  /**
   * Custom billing and renewal URLs configured for the account.
   */
  account_links?: AccountLinks | null;
  /**
   * Information about a customer opting out of an offer.
   */
  opt_out?: AccountOptOut | null;
  /**
   * Term Month Options
   *
   * Contract terms (months) the customer may choose, resolved for the account's currently selected plan. Request one of these via /plans/request for that same plan; requesting a different plan may reject the term.
   */
  term_month_options?: Array<number>;
  /**
   * The app the account belongs to.
   */
  app: AccountApp;
  /**
   * Locations
   *
   * Service locations associated with the account.
   */
  locations: Array<Location>;
};

/**
 * BrokerTermsAgreementRequirement
 */
export type BrokerTermsAgreementRequirement = {
  value?: BrokerTermsAgreementValue | null;
  unsatisfied?: Unsatisfied | null;
};

/**
 * BrokerTermsAgreementValue
 */
export type BrokerTermsAgreementValue = {
  /**
   * Agreed At
   */
  agreed_at: string;
};

/**
 * CardPaymentMethodValue
 */
export type CardPaymentMethodValue = {
  /**
   * Brand
   */
  brand?: string | null;
  /**
   * Last Four
   */
  last_four?: string | null;
  /**
   * Exp Month
   */
  exp_month?: number | null;
  /**
   * Exp Year
   */
  exp_year?: number | null;
};

/**
 * DobRequirement
 */
export type DobRequirement = {
  value?: DobValue | null;
  unsatisfied?: Unsatisfied | null;
};

/**
 * DobValue
 */
export type DobValue = {
  /**
   * Year
   */
  year: number;
  /**
   * Month
   */
  month: number;
  /**
   * Day
   */
  day: number;
};

/**
 * EmailRequirement
 */
export type EmailRequirement = {
  value?: EmailValue | null;
  unsatisfied?: Unsatisfied | null;
};

/**
 * EmailValue
 */
export type EmailValue = {
  /**
   * Value
   */
  value: string;
};

/**
 * Location
 */
export type Location = {
  /**
   * Address 1
   *
   * First line of the street address.
   */
  address_1: string;
  /**
   * Address 2
   *
   * Second line of the street address, such as a unit or apartment.
   */
  address_2?: string | null;
  /**
   * City
   *
   * City of the address.
   */
  city: string;
  /**
   * State
   *
   * Two-letter state code of the address.
   */
  state: string;
  /**
   * Postal Code
   *
   * Five-digit postal code of the address.
   */
  postal_code: string;
  /**
   * Uuid
   *
   * Unique identifier of the service location.
   */
  uuid: string;
  /**
   * Utility Number
   *
   * Utility meter identifier (ESI ID) for the location.
   */
  utility_number: string;
  /**
   * Service Start Date
   *
   * Date service starts at the location.
   */
  service_start_date: string;
  /**
   * Final Service Date
   *
   * Date service ends at the location, when scheduled.
   */
  final_service_date: string | null;
  /**
   * Is Service Active
   *
   * Whether service is currently active at the location.
   */
  is_service_active: boolean;
  /**
   * Has Usage History
   *
   * Whether usage history is available for the location.
   */
  has_usage_history: boolean;
  /**
   * Estimated Next Reading Date
   *
   * Estimated date of the next meter reading.
   */
  estimated_next_reading_date: string | null;
  /**
   * Plan Name
   *
   * Name of the plan the location is enrolled on.
   */
  plan_name?: string | null;
  /**
   * Plan Uuid
   *
   * Unique identifier of the plan the location is enrolled on.
   */
  plan_uuid?: string | null;
  /**
   * The type of plan the consumer is on. `TERM` is a fixed-rate plan for a set number of months. `MONTH_TO_MONTH` is a variable-rate plan with no fixed term. `FALLBACK` is a plan that expired contracts are automatically moved onto.
   */
  plan_type?: PlanType | null;
  /**
   * Fallback Parent Name
   *
   * When `plan_type` is `FALLBACK`, the plan whose expired contracts roll onto this one — i.e. the plan the consumer originally signed up for. `null` otherwise.
   */
  fallback_parent_name?: string | null;
  /**
   * Fallback Parent Uuid
   *
   * UUID of the plan named by `fallback_parent_name`. `null` unless `plan_type` is `FALLBACK`.
   */
  fallback_parent_uuid?: string | null;
  /**
   * The unique identifier for the utility (TDSP) serving the location.
   */
  tdsp_id?: UtilityIdentifier | null;
  /**
   * Tdsp Name
   *
   * Full name of the utility (TDSP) serving the location.
   */
  tdsp_name?: string | null;
  /**
   * Tdsp Shortname
   *
   * Short name of the utility (TDSP) serving the location.
   */
  tdsp_shortname?: string | null;
  /**
   * Tdsp Phone
   *
   * Phone number for the utility (TDSP) serving the location.
   */
  tdsp_phone?: string | null;
  /**
   * Tdsp Outage Url
   *
   * URL of the utility's (TDSP) outage map.
   */
  tdsp_outage_url?: string | null;
  /**
   * Contract End
   *
   * End date of the active contract. `null` when there is no active contract, or when the plan has no fixed term.
   */
  contract_end?: string | null;
};

/**
 * NameRequirement
 */
export type NameRequirement = {
  value?: NameValue | null;
  unsatisfied?: Unsatisfied | null;
};

/**
 * NameValue
 */
export type NameValue = {
  /**
   * First
   */
  first: string;
  /**
   * Last
   */
  last: string;
};

/**
 * PagedAppAccountResponse
 */
export type PagedAppAccountResponse = {
  /**
   * Data
   *
   * The page of results, ordered as the endpoint returns them.
   */
  data: Array<AppAccountResponse>;
  /**
   * Has More
   *
   * Whether more results exist after this page.
   */
  has_more: boolean;
  /**
   * Limit
   *
   * Maximum number of results returned per page.
   */
  limit: number;
  /**
   * Offset
   *
   * Number of results skipped before this page.
   */
  offset: number;
  /**
   * Next Page Url
   *
   * URL of the next page, or null when this is the last page.
   */
  next_page_url?: string | null;
};

/**
 * PaymentMethodRequirement
 */
export type PaymentMethodRequirement = {
  value?: PaymentMethodValue | null;
  unsatisfied?: Unsatisfied | null;
};

/**
 * PaymentMethodValue
 */
export type PaymentMethodValue = {
  card?: CardPaymentMethodValue | null;
};

/**
 * PhoneNumberRequirement
 */
export type PhoneNumberRequirement = {
  value?: PhoneNumberValue | null;
  unsatisfied?: Unsatisfied | null;
};

/**
 * PhoneNumberValue
 */
export type PhoneNumberValue = {
  /**
   * Value
   */
  value: string;
};

/**
 * PlanRequirement
 */
export type PlanRequirement = {
  value?: PlanValue | null;
  unsatisfied?: Unsatisfied | null;
};

/**
 * PlanValue
 */
export type PlanValue = {
  /**
   * Group Uuid
   */
  group_uuid: string;
  /**
   * Name
   */
  name: string;
};

/**
 * PreliminaryAddress
 *
 * Preliminary address is not as constrained as a final address since it is
 * provided by the the app and may not be formatted the same.
 *
 * Some fields may be empty strings if only a partial address was provided.
 */
export type PreliminaryAddress = {
  /**
   * Address 1
   *
   * First line of the street address.
   */
  address_1: string;
  /**
   * Address 2
   *
   * Second line of the street address, such as a unit or apartment.
   */
  address_2?: string | null;
  /**
   * City
   *
   * City of the address.
   */
  city: string;
  /**
   * State
   *
   * State of the address.
   */
  state: string;
  /**
   * Postal Code
   *
   * Postal code of the address.
   */
  postal_code: string;
};

/**
 * Requirements
 */
export type Requirements = {
  service_address?: ServiceAddressRequirement | null;
  utility_account_number?: UtilityAccountNumberRequirement | null;
  name?: NameRequirement | null;
  dob?: DobRequirement | null;
  ssn?: SsnRequirement | null;
  phone_number?: PhoneNumberRequirement | null;
  email?: EmailRequirement | null;
  payment_method?: PaymentMethodRequirement | null;
  plan?: PlanRequirement | null;
  service_event?: ServiceEventRequirement | null;
  service_start_date?: ServiceStartDateRequirement | null;
  acceptance?: AcceptanceRequirement | null;
  utility?: UtilityRequirement | null;
  broker_terms_agreement?: BrokerTermsAgreementRequirement | null;
};

/**
 * ServiceAddressRequirement
 */
export type ServiceAddressRequirement = {
  value?: AddressValue | null;
  unsatisfied?: Unsatisfied | null;
};

/**
 * ServiceEventRequirement
 */
export type ServiceEventRequirement = {
  value?: ServiceEventValue | null;
  unsatisfied?: Unsatisfied | null;
};

/**
 * ServiceEventValue
 */
export type ServiceEventValue = {
  /**
   * Value
   */
  value: string;
};

/**
 * ServiceStartDateRequirement
 */
export type ServiceStartDateRequirement = {
  value?: ServiceStartDateValue | null;
  unsatisfied?: Unsatisfied | null;
};

/**
 * ServiceStartDateValue
 */
export type ServiceStartDateValue = {
  /**
   * Value
   */
  value: string;
};

/**
 * SsnRequirement
 */
export type SsnRequirement = {
  value?: SsnValue | null;
  unsatisfied?: Unsatisfied | null;
};

/**
 * SsnValue
 */
export type SsnValue = {
  /**
   * Provided
   */
  provided?: boolean;
};

/**
 * Stage
 */
export enum Stage {
  Enrollment = "enrollment",
  Renewal = "renewal",
}

/**
 * StatusType
 */
export enum StatusType {
  Created = "created",
  Applying = "applying",
  Reviewing = "reviewing",
  NeedsAttention = "needs_attention",
  Scheduled = "scheduled",
  Activating = "activating",
  Active = "active",
  Cancelled = "cancelled",
}

/**
 * Unsatisfied
 */
export type Unsatisfied = {
  reason: UnsatisfiedReason;
  /**
   * Message
   */
  message?: string | null;
};

/**
 * UnsatisfiedReason
 */
export enum UnsatisfiedReason {
  NotProvided = "not_provided",
  AddressDidNotResolve = "address_did_not_resolve",
  AddressIncomplete = "address_incomplete",
  SsnDidNotResolve = "ssn_did_not_resolve",
  PlanNoLongerExists = "plan_no_longer_exists",
  RatesExpired = "rates_expired",
  RatesUnavailable = "rates_unavailable",
  NotAccepted = "not_accepted",
}

/**
 * Utility
 */
export type Utility = {
  /**
   * The utility's unique identifier.
   */
  id: UtilityIdentifier;
  /**
   * Short Name
   *
   * The utility's short name.
   */
  short_name: string;
  /**
   * Full Name
   *
   * The utility's full legal name.
   */
  full_name: string;
  /**
   * Name
   *
   * Deprecated, use `id` instead.
   *
   * @deprecated
   */
  name: string;
  /**
   * Display Name
   *
   * Deprecated, use `short_name` instead.
   *
   * @deprecated
   */
  display_name: string;
};

/**
 * UtilityAccountNumberRequirement
 */
export type UtilityAccountNumberRequirement = {
  value?: UtilityAccountNumberValue | null;
  unsatisfied?: Unsatisfied | null;
};

/**
 * UtilityAccountNumberValue
 */
export type UtilityAccountNumberValue = {
  /**
   * Value
   */
  value: string;
};

/**
 * UtilityIdentifier
 */
export enum UtilityIdentifier {
  AepCentral = "AEP_CENTRAL",
  AepNorth = "AEP_NORTH",
  Centerpoint = "CENTERPOINT",
  Lubbock = "LUBBOCK",
  Oncor = "ONCOR",
  Tnmp = "TNMP",
  Peco = "PECO",
  Ppl = "PPL",
}

/**
 * UtilityRequirement
 */
export type UtilityRequirement = {
  value?: UtilityValue | null;
  unsatisfied?: Unsatisfied | null;
};

/**
 * UtilityValue
 */
export type UtilityValue = {
  utility: Utility;
};

/**
 * AccountCreateResponse
 */
export type AccountCreateResponse = {
  /**
   * Uuid
   *
   * Unique identifier of the account.
   */
  uuid: string;
  /**
   * External Id
   *
   * Partner-assigned identifier for the account.
   */
  external_id?: string | null;
  /**
   * Account Number
   *
   * Light account number for the account.
   */
  account_number?: string | null;
  /**
   * Current high-level status of the account.
   */
  status: StatusType;
  /**
   * Detailed status reason for the account.
   */
  status_reason: AccountStatus;
  /**
   * Created At
   *
   * When the account was created.
   */
  created_at: string;
  /**
   * First Name
   *
   * Customer's first name.
   */
  first_name: string;
  /**
   * Last Name
   *
   * Customer's last name.
   */
  last_name: string;
  /**
   * Email
   *
   * Customer's email address.
   */
  email: string;
  /**
   * Phone Number
   *
   * Customer's phone number.
   */
  phone_number?: string | null;
  /**
   * Enrollment progress for the account.
   */
  enrollment?: AccountEnrollment;
  /**
   * Has Client Agent
   *
   * Whether a power-of-attorney client agent is on file for the account.
   */
  has_client_agent?: boolean;
  /**
   * Preliminary service address provided for the account, before it is verified.
   */
  preliminary_address?: PreliminaryAddress | null;
  /**
   * Partner Payment Method On File
   *
   * Deprecated; always null.
   *
   * @deprecated
   */
  partner_payment_method_on_file?: null;
  /**
   * Additional metadata about the account's service location, such as solar and battery details.
   */
  location_metadata?: AccountLocationMetadata | null;
  /**
   * Custom billing and renewal URLs configured for the account.
   */
  account_links?: AccountLinks | null;
  /**
   * Information about a customer opting out of an offer.
   */
  opt_out?: AccountOptOut | null;
  /**
   * Term Month Options
   *
   * Contract terms (months) the customer may choose, resolved for the account's currently selected plan. Request one of these via /plans/request for that same plan; requesting a different plan may reject the term.
   */
  term_month_options?: Array<number>;
  /**
   * Was Created
   *
   * Whether this request created a new account, rather than returning an existing one.
   */
  was_created?: boolean;
};

/**
 * AcceptanceValueInput
 */
export type AcceptanceValueInput = {
  /**
   * Rate Uuid
   */
  rate_uuid: string;
  /**
   * Terms Accepted
   */
  terms_accepted: boolean;
  accepted_by?: AcceptedBy;
};

/**
 * AcceptedBy
 */
export enum AcceptedBy {
  Customer = "customer",
  Partner = "partner",
}

/**
 * AddressValueInput
 */
export type AddressValueInput = {
  /**
   * Address 1
   */
  address_1?: string | null;
  /**
   * Address 2
   */
  address_2?: string | null;
  /**
   * City
   */
  city?: string | null;
  /**
   * State
   */
  state?: string | null;
  /**
   * Postal Code
   */
  postal_code: string;
};

/**
 * BrokerTermsAgreementValueInput
 */
export type BrokerTermsAgreementValueInput = {
  /**
   * Agreed
   */
  agreed: boolean;
};

/**
 * CreateAccountRequest
 */
export type CreateAccountRequest = {
  /**
   * First Name
   *
   * Customer's first name.
   */
  first_name: string;
  /**
   * Last Name
   *
   * Customer's last name.
   */
  last_name: string;
  /**
   * Email
   *
   * Customer's email address.
   */
  email: string;
  /**
   * Phone Number
   *
   * Customer's phone number.
   */
  phone_number?: string | null;
  /**
   * Birth Date
   *
   * Customer's date of birth.
   */
  birth_date?: string | null;
  /**
   * Service Start Date
   *
   * Requested date for service to start.
   */
  service_start_date?: string | null;
  /**
   * Preliminary service address for the account.
   */
  preliminary_address?: PreliminaryAddressInput | null;
  /**
   * Additional metadata about the service location, such as solar and battery details.
   */
  location_metadata?: AccountLocationMetadata | null;
  /**
   * Custom billing and renewal URLs for the account.
   */
  account_links?: AccountLinks | null;
  /**
   * External Id
   *
   * Partner-assigned identifier for the account.
   */
  external_id?: string | null;
  /**
   * Metadata
   *
   * Freeform partner-supplied string metadata. Fields are shallow-merged into the stored object: a value adds or overwrites that key, null removes it, and omitted keys are left unchanged. At most 50 keys, and each key together with its value may be at most 100 characters.
   */
  metadata?: {
    [key: string]: string | null;
  } | null;
  /**
   * Term Month Options
   *
   * Contract terms in months the customer may choose from.
   */
  term_month_options?: Array<number> | null;
  /**
   * Plan
   *
   * UUID of the plan to preselect for the account.
   */
  plan?: string | null;
  /**
   * Broker Terms Agreed
   *
   * Whether the customer has been shown, and agreed to, the broker terms of service. Pass `true` only if the customer has already agreed.
   */
  broker_terms_agreed?: boolean;
  enrollment_requirements?: RequirementsPatch | null;
};

/**
 * DobValueInput
 */
export type DobValueInput = {
  /**
   * Value
   */
  value: string;
};

/**
 * EmailValueInput
 */
export type EmailValueInput = {
  /**
   * Value
   */
  value: string;
};

/**
 * NameValueInput
 */
export type NameValueInput = {
  /**
   * First
   */
  first: string;
  /**
   * Last
   */
  last: string;
};

/**
 * PaymentMethodValueInput
 */
export type PaymentMethodValueInput = {
  /**
   * Value
   */
  value: string;
};

/**
 * PhoneNumberValueInput
 */
export type PhoneNumberValueInput = {
  /**
   * Value
   */
  value: string;
};

/**
 * PlanValueInput
 */
export type PlanValueInput = {
  /**
   * Group Uuid
   */
  group_uuid: string;
};

/**
 * PreliminaryAddressInput
 *
 * Allows for partial addresses, with a minimum of a postal code.
 */
export type PreliminaryAddressInput = {
  /**
   * Address 1
   *
   * First line of the street address.
   */
  address_1?: string | null;
  /**
   * Address 2
   *
   * Second line of the street address, such as a unit or apartment.
   */
  address_2?: string | null;
  /**
   * City
   *
   * City of the address.
   */
  city?: string | null;
  /**
   * State
   *
   * State of the address.
   */
  state?: string | null;
  /**
   * Postal Code
   *
   * Postal code of the address.
   */
  postal_code: string;
};

/**
 * RequirementsPatch
 */
export type RequirementsPatch = {
  service_address?: AddressValueInput | null;
  utility_account_number?: UtilityAccountNumberValue | null;
  service_start_date?: ServiceStartDateValue | null;
  service_event?: ServiceEventValueInput | null;
  name?: NameValueInput | null;
  phone_number?: PhoneNumberValueInput | null;
  email?: EmailValueInput | null;
  dob?: DobValueInput | null;
  plan?: PlanValueInput | null;
  ssn?: SsnValueInput | null;
  payment_method?: PaymentMethodValueInput | null;
  acceptance?: AcceptanceValueInput | null;
  utility?: UtilityValueInput | null;
  broker_terms_agreement?: BrokerTermsAgreementValueInput | null;
};

/**
 * ServiceEvent
 */
export enum ServiceEvent {
  Switch = "switch",
  MoveIn = "move_in",
}

/**
 * ServiceEventValueInput
 */
export type ServiceEventValueInput = {
  value: ServiceEvent;
};

/**
 * SsnValueInput
 */
export type SsnValueInput = {
  /**
   * Value
   */
  value: string;
  /**
   * Agree To Terms
   */
  agree_to_terms: boolean;
};

/**
 * UtilityValueInput
 */
export type UtilityValueInput = {
  value: UtilityIdentifier;
};

/**
 * AccountsCountResponse
 */
export type AccountsCountResponse = {
  /**
   * Count
   *
   * Number of accounts matching the query.
   */
  count: number;
};

/**
 * UpdateAccountRequest
 */
export type UpdateAccountRequest = {
  /**
   * First Name
   *
   * Customer's first name.
   */
  first_name?: string | null;
  /**
   * Last Name
   *
   * Customer's last name.
   */
  last_name?: string | null;
  /**
   * Email
   *
   * Customer's email address.
   */
  email?: string | null;
  /**
   * Phone Number
   *
   * Customer's phone number.
   */
  phone_number?: string | null;
  /**
   * Service Start Date
   *
   * Requested date for service to start.
   */
  service_start_date?: string | null;
  /**
   * Preliminary service address for the account.
   */
  preliminary_address?: PreliminaryAddressInput | null;
  /**
   * Additional metadata about the service location, such as solar and battery details.
   */
  location_metadata?: AccountLocationMetadata | null;
  /**
   * Custom billing and renewal URLs for the account.
   */
  account_links?: AccountLinks | null;
  /**
   * External Id
   *
   * Partner-assigned identifier for the account.
   */
  external_id?: string | null;
  /**
   * Metadata
   *
   * Freeform partner-supplied string metadata. Fields are shallow-merged into the stored object: a value adds or overwrites that key, null removes it, and omitted keys are left unchanged. At most 50 keys, and each key together with its value may be at most 100 characters.
   */
  metadata?: {
    [key: string]: string | null;
  } | null;
  /**
   * Term Month Options
   *
   * Contract terms in months the customer may choose from.
   */
  term_month_options?: Array<number> | null;
  /**
   * Plan
   *
   * UUID of the plan to preselect for the account.
   */
  plan?: string | null;
  /**
   * Broker Terms Agreed
   *
   * Whether the customer has been shown, and agreed to, the broker terms of service. Pass `true` only if the customer has already agreed. An agreement can't be revoked, so `false` is ignored.
   */
  broker_terms_agreed?: boolean | null;
};

/**
 * EnrollmentEvidenceResponse
 */
export type EnrollmentEvidenceResponse = {
  /**
   * Pdf Url
   *
   * URL to the PDF documenting evidence of the customer's enrollment authorization.
   */
  pdf_url: string;
};

/**
 * CancelEnrollmentRequest
 */
export type CancelEnrollmentRequest = {
  /**
   * Reason
   *
   * Reason the partner is cancelling the enrollment.
   */
  reason: string;
};

/**
 * RecordClientAgentRequest
 */
export type RecordClientAgentRequest = {
  /**
   * Client Agent Accepted At
   *
   * When the customer accepted the power-of-attorney agreement.
   */
  client_agent_accepted_at?: string | null;
  /**
   * Agreement Pdf
   *
   * Base64-encoded PDF of the power of attorney agreement.
   */
  agreement_pdf?: string | null;
};

/**
 * AppAccountProgramAgreement
 */
export type AppAccountProgramAgreement = {
  terms: ProgramTerms;
  program: Program;
  /**
   * Agreed At
   */
  agreed_at: string;
  /**
   * Account Uuid
   */
  account_uuid: string;
};

/**
 * BatteryDetails
 */
export type BatteryDetails = {
  /**
   * Minimum Capacity Kwh
   *
   * Minimum battery capacity, in kWh, required for the storage credit to apply.
   */
  minimum_capacity_kwh?: string | null;
  /**
   * Monthly Credit Dollars
   *
   * Flat monthly credit for enrolled battery storage, in dollars.
   */
  monthly_credit_dollars?: string | null;
  /**
   * Monthly Credit Per Kwh Dollars
   *
   * Per-kWh monthly credit for enrolled battery storage capacity, in dollars.
   */
  monthly_credit_per_kwh_dollars?: string | null;
};

/**
 * EVChargingDetails
 */
export type EVChargingDetails = {
  /**
   * Base Fee
   *
   * Monthly fee for one enrolled vehicle or charger.
   */
  base_fee?: string | null;
  /**
   * Base Fee Two Device
   *
   * Total monthly fee for two enrolled vehicles or chargers, null if the program takes only one.
   */
  base_fee_two_device?: string | null;
  /**
   * Base Fee Three Device
   *
   * Total monthly fee for three enrolled vehicles or chargers, null if the program takes fewer.
   */
  base_fee_three_device?: string | null;
  /**
   * Max Enrolled Devices
   *
   * Vehicles or chargers a consumer can enroll in the program.
   */
  max_enrolled_devices: number;
  /**
   * Schedules
   *
   * Daily charging windows that define when usage is billed at the on-schedule rate.
   */
  schedules?: Array<EVChargingScheduleSchema>;
  /**
   * On Schedule Rates
   *
   * Pricing tiers applied to usage within the on-schedule charging windows.
   */
  on_schedule_rates?: Array<EVChargingPricingTierSchema>;
  /**
   * Assumed Charging Kwh
   *
   * Assumed monthly EV charging usage, in kWh, used to estimate program cost.
   */
  assumed_charging_kwh: number;
};

/**
 * EVChargingPricingTierSchema
 */
export type EVChargingPricingTierSchema = {
  /**
   * Tier
   *
   * Position of this pricing tier within the schedule, ordered from 1.
   */
  tier: number;
  /**
   * Name
   *
   * Label for this pricing tier.
   */
  name?: string | null;
  /**
   * Cap
   *
   * Maximum on-schedule kWh billed at this tier's rate before usage rolls to the next tier, null if the tier is uncapped.
   */
  cap?: string | null;
  /**
   * Rate
   *
   * Rate in cents per kWh.
   */
  rate: string;
};

/**
 * EVChargingScheduleSchema
 */
export type EVChargingScheduleSchema = {
  /**
   * Name
   *
   * Label for this charging schedule window.
   */
  name?: string | null;
  /**
   * Start Time
   *
   * Start of the charging window, in local time (HH:MM:SS).
   */
  start_time: string;
  /**
   * End Time
   *
   * End of the charging window, in local time (HH:MM:SS).
   */
  end_time: string;
};

/**
 * OwnershipType
 */
export enum OwnershipType {
  Customer = "CUSTOMER",
  Partner = "PARTNER",
}

/**
 * PagedAppAccountProgramAgreement
 */
export type PagedAppAccountProgramAgreement = {
  /**
   * Data
   *
   * The page of results, ordered as the endpoint returns them.
   */
  data: Array<AppAccountProgramAgreement>;
  /**
   * Has More
   *
   * Whether more results exist after this page.
   */
  has_more: boolean;
  /**
   * Limit
   *
   * Maximum number of results returned per page.
   */
  limit: number;
  /**
   * Offset
   *
   * Number of results skipped before this page.
   */
  offset: number;
  /**
   * Next Page Url
   *
   * URL of the next page, or null when this is the last page.
   */
  next_page_url?: string | null;
};

/**
 * Program
 */
export type Program = {
  /**
   * Uuid
   */
  uuid: string;
  /**
   * Name
   */
  name: string;
  /**
   * Indicates whether the device is owned by the customer or partner.
   */
  ownership_type: OwnershipType;
  /**
   * Terms Of Service Url
   */
  terms_of_service_url: string;
  program_type: ProgramType;
  battery_details?: BatteryDetails | null;
  ev_charging?: EVChargingDetails | null;
};

/**
 * ProgramTerms
 */
export type ProgramTerms = {
  /**
   * Uuid
   *
   * Unique identifier for this version of the program terms.
   */
  uuid: string;
  /**
   * Name
   *
   * Name of the program.
   */
  name: string;
  /**
   * Type of program these terms apply to.
   */
  program_type: ProgramType;
  /**
   * Indicates whether the device is owned by the customer or partner.
   */
  ownership_type: OwnershipType;
  /**
   * Terms Of Service Url
   *
   * URL to the PDF terms of service for this program version.
   */
  terms_of_service_url: string;
  /**
   * Battery program economics, null if this program does not include battery storage.
   */
  battery_details?: BatteryDetails | null;
  /**
   * EV charging program economics, null if this program does not include EV charging.
   */
  ev_charging?: EVChargingDetails | null;
};

/**
 * ProgramType
 */
export enum ProgramType {
  Battery = "BATTERY",
  Evcharging = "EVCHARGING",
}

/**
 * AppAccountDevice
 */
export type AppAccountDevice = {
  /**
   * Uuid
   *
   * The device's unique identifier.
   */
  uuid: string;
  /**
   * Device Type
   *
   * The type of device.
   */
  device_type: "BATTERY" | "EVSE" | "VEHICLE";
  /**
   * The device's partner-facing status.
   */
  status: PartnerDeviceStatus;
  /**
   * The battery's state and details, present when the device is a battery.
   */
  battery_details?: BatteryState | null;
  /**
   * The EVSE's details, present when the device is an EVSE.
   */
  charger_details?: EVSEDetails | null;
  /**
   * The vehicle's details, present when the device is a vehicle.
   */
  vehicle_details?: VehicleDetails | null;
  /**
   * Device program enrollment and compliance status.
   */
  program?: DeviceProgram | null;
  /**
   * Account Uuid
   *
   * The unique identifier of the account that owns this device.
   */
  account_uuid: string;
};

/**
 * BatteryState
 */
export type BatteryState = {
  /**
   * Identifier
   *
   * The battery's unique identifier.
   */
  identifier: string;
  /**
   * Manufacturer
   *
   * The battery's manufacturer.
   */
  manufacturer: string;
  /**
   * Model
   *
   * The battery's model name.
   */
  model: string;
  /**
   * Capacity Kwh
   *
   * The battery's total energy storage capacity, in kWh.
   */
  capacity_kwh?: string | null;
  /**
   * Is Reachable
   *
   * Whether the battery is currently online and reachable.
   */
  is_reachable?: boolean;
  /**
   * Reserve Soc
   *
   * The battery's configured backup reserve state of charge, as a percentage.
   */
  reserve_soc?: number | null;
  /**
   * Can Import
   *
   * Whether the battery is permitted to charge from the grid.
   */
  can_import?: boolean;
  /**
   * Can Export
   *
   * Whether the battery is permitted to export energy to the grid.
   */
  can_export?: boolean;
  /**
   * Last Synced At
   *
   * The UTC timestamp when the battery's state was last synced from the manufacturer.
   */
  last_synced_at: string | null;
};

/**
 * ComplianceItem
 *
 * Single item in a program compliance checklist.
 */
export type ComplianceItem = {
  /**
   * The compliance requirement being evaluated.
   */
  field: FieldName;
  /**
   * Label
   *
   * The human-readable label for the compliance requirement.
   */
  label?: string | null;
  /**
   * Expected
   *
   * The value required for this compliance item to be satisfied.
   */
  expected: boolean | number | number | string | null;
  /**
   * Actual
   *
   * The value most recently observed for this compliance item.
   */
  actual: boolean | number | number | string | null;
};

/**
 * DeviceProgram
 *
 * Device program enrollment with compliance status.
 */
export type DeviceProgram = {
  /**
   * Uuid
   *
   * The device program's unique identifier.
   */
  uuid: string;
  /**
   * Is Compliant
   *
   * Whether the device currently satisfies all requirements of the program.
   */
  is_compliant: boolean;
  /**
   * Compliance Checklist
   *
   * The compliance checklist items evaluated for the device's program enrollment.
   */
  compliance_checklist?: Array<ComplianceItem>;
};

/**
 * EVSEDetails
 */
export type EVSEDetails = {
  /**
   * Identifier
   *
   * The EVSE's unique identifier.
   */
  identifier: string;
  /**
   * Manufacturer
   *
   * The EVSE's manufacturer.
   */
  manufacturer: string;
  /**
   * Model
   *
   * The EVSE's model name.
   */
  model: string;
};

/**
 * FieldName
 */
export enum FieldName {
  ReserveSoc = "reserve_soc",
  CanImport = "can_import",
  CanExport = "can_export",
  IsReachable = "is_reachable",
}

/**
 * VehicleDetails
 */
export type VehicleDetails = {
  /**
   * Identifier
   *
   * The vehicle's unique identifier (VIN).
   */
  identifier: string;
  /**
   * Manufacturer
   *
   * The vehicle's manufacturer.
   */
  manufacturer: string;
  /**
   * Model
   *
   * The vehicle's model name.
   */
  model: string;
};

/**
 * CreateBatteryRequest
 *
 * Request to create battery devices by manufacturer identifier.
 *
 * The identifier value depends on the manufacturer:
 * - **tesla**: The `energy_site_id` from the Tesla API
 * - **solaredge**: The `siteId` from the SolarEdge API
 * - **enphase**: The `system_id` from the Enphase API
 */
export type CreateBatteryRequest = {
  /**
   * Manufacturer
   *
   * The device manufacturer name.
   */
  manufacturer:
    | "tesla"
    | "solaredge"
    | "enphase"
    | "sigenergy"
    | "franklin"
    | "ecoflow"
    | "duracell"
    | "foxess"
    | "sonnen"
    | "generac";
  /**
   * Identifier
   *
   * Manufacturer-specific site identifier. For Tesla: energy_site_id. For SolarEdge: siteId. For Enphase: system_id.
   */
  identifier: string;
  /**
   * Preliminary Capacity Kwh
   *
   * The total kWh capacity for all batteries at this location. This value is only used as a placeholder until Light has an authorized connection to the battery.
   */
  preliminary_capacity_kwh: number | string;
};

/**
 * CreateEVSERequest
 *
 * Request to create an EVSE (Electric Vehicle Supply Equipment) device by manufacturer identifier.
 */
export type CreateEVSERequest = {
  /**
   * Manufacturer
   *
   * The EVSE manufacturer name.
   */
  manufacturer: "emporia";
  /**
   * Identifier
   *
   * Manufacturer-specific charger identifier.
   */
  identifier: string;
};

/**
 * CreateVehicleRequest
 *
 * Request to create a Vehicle device by manufacturer identifier.
 */
export type CreateVehicleRequest = {
  /**
   * Manufacturer
   *
   * The vehicle manufacturer name.
   */
  manufacturer: "hyundai" | "volvo" | "rivian" | "genesis";
  /**
   * Identifier
   *
   * VIN or manufacturer-specific vehicle unique identifier.
   */
  identifier: string;
};

/**
 * PagedAppAccountDevice
 */
export type PagedAppAccountDevice = {
  /**
   * Data
   *
   * The page of results, ordered as the endpoint returns them.
   */
  data: Array<AppAccountDevice>;
  /**
   * Has More
   *
   * Whether more results exist after this page.
   */
  has_more: boolean;
  /**
   * Limit
   *
   * Maximum number of results returned per page.
   */
  limit: number;
  /**
   * Offset
   *
   * Number of results skipped before this page.
   */
  offset: number;
  /**
   * Next Page Url
   *
   * URL of the next page, or null when this is the last page.
   */
  next_page_url?: string | null;
};

/**
 * DeviceLinkResponse
 */
export type DeviceLinkResponse = {
  /**
   * Connect Link
   */
  connect_link: string;
};

/**
 * DeviceLinkRequest
 */
export type DeviceLinkRequest = {
  /**
   * Device Type
   */
  device_type?: "BATTERY" | "EVSE" | "VEHICLE" | null;
  /**
   * Manufacturer
   */
  manufacturer?:
    | "tesla"
    | "solaredge"
    | "enphase"
    | "sigenergy"
    | "franklin"
    | "ecoflow"
    | "duracell"
    | "foxess"
    | "sonnen"
    | "generac"
    | null;
  /**
   * Redirect Link
   */
  redirect_link: string;
};

/**
 * ChargingEventType
 */
export enum ChargingEventType {
  ChargingStarted = "charging_started",
  ChargingEnded = "charging_ended",
  Charging = "charging",
  Idle = "idle",
  VehiclePluggedIn = "vehicle_plugged_in",
  VehicleUnplugged = "vehicle_unplugged",
}

/**
 * ChargingStatus
 *
 * Request to create a Vehicle Charging Event.
 */
export type ChargingStatus = {
  /**
   * Timestamp
   *
   * UNIX epoch (seconds) at which the status was observed. Must be within a year of the current time.
   */
  timestamp: number;
  /**
   * Soc
   *
   * Vehicle state of charge, as a percentage between 0 and 100. Rounded to two decimal places.
   */
  soc: number | string;
  /**
   * Charge Rate Kw
   *
   * Instantaneous charge rate in kW, rounded to three decimal places. Null when not charging. Negative when the vehicle is discharging.
   */
  charge_rate_kw?: number | string | null;
  /**
   * The charging state observed at `timestamp`. `charging_started` and `charging_ended` are required on every charge transition; `charging` and `idle` are optional samples between transitions.
   */
  status: ChargingEventType;
};

/**
 * AddressSearchResponse
 */
export type AddressSearchResponse = {
  /**
   * Address 1
   *
   * First line of the street address.
   */
  address_1: string;
  /**
   * Address 2
   *
   * Second line of the street address, such as a unit or apartment.
   */
  address_2?: string | null;
  /**
   * City
   *
   * City of the address.
   */
  city: string;
  /**
   * State
   *
   * Two-letter state code of the address.
   */
  state: string;
  /**
   * Postal Code
   *
   * Five-digit postal code of the address.
   */
  postal_code: string;
  /**
   * Esi Id
   *
   * ESI ID (meter identifier) for this address.
   */
  esi_id: string;
  /**
   * Alias
   *
   * Mailing-address form of this address when it differs from the utility service address.
   */
  alias?: string | null;
  /**
   * Utility
   *
   * The unique identifier for the utility serving this meter.
   */
  utility?: string | null;
};

/**
 * AddressSearchRequest
 */
export type AddressSearchRequest = {
  /**
   * Query
   *
   * Free-text address search query.
   */
  query: string;
  /**
   * Postal Code
   *
   * Optional postal code to narrow the search.
   */
  postal_code?: string | null;
};

/**
 * EligibilityResponse
 */
export type EligibilityResponse = {
  /**
   * Eligibility Likelihood
   *
   * Likelihood the address can enroll, from 0 to 100.
   */
  eligibility_likelihood: number;
  /**
   * Utilities
   *
   * Utilities (TDSPs) that may serve the address.
   */
  utilities: Array<Utility>;
  /**
   * Potential Non Choice Utilities
   *
   * Non-choice utilities that may serve part of this postal code, and so may not be able to enroll. A postal-code level approximation returned alongside `utilities`, not confirmation for a specific address.
   */
  potential_non_choice_utilities?: Array<NonChoiceUtility>;
};

/**
 * NonChoiceUtility
 *
 * A utility outside the Texas retail choice market, which may be municipally
 * owned, an electric cooperative, or investor-owned.
 */
export type NonChoiceUtility = {
  /**
   * Name
   *
   * Machine-readable identifier of the utility.
   */
  name: string;
  /**
   * Display Name
   *
   * Display name of the utility.
   */
  display_name: string;
  /**
   * Website
   *
   * Utility's website URL, when available.
   */
  website?: string | null;
};

/**
 * EligibilityRequest
 */
export type EligibilityRequest = {
  /**
   * Postal Code
   *
   * Postal code of the service location.
   */
  postal_code: string;
  /**
   * Address 1
   *
   * First line of the street address.
   */
  address_1?: string | null;
  /**
   * Address 2
   *
   * Second line of the street address, such as a unit or apartment.
   */
  address_2?: string | null;
  /**
   * City
   *
   * City of the address.
   */
  city?: string | null;
  /**
   * State
   *
   * Two-letter state code of the address.
   */
  state?: string | null;
  /**
   * Utility Number
   *
   * Utility meter identifier (ESI ID) for the location.
   */
  utility_number?: string | null;
};

/**
 * BenchmarkPlanItem
 */
export type BenchmarkPlanItem = {
  /**
   * The plan being benchmarked.
   */
  plan: PendingPlan;
  /**
   * Benchmarks
   *
   * Savings benchmarks for the plan at different usage levels.
   */
  benchmarks: Array<PlanBenchmark>;
};

/**
 * BenchmarksResponse
 */
export type BenchmarksResponse = {
  /**
   * Available Plans
   *
   * Plans available for the address, each with savings benchmarks.
   */
  available_plans: Array<BenchmarkPlanItem>;
};

/**
 * PendingPlan
 */
export type PendingPlan = {
  /**
   * Term Months
   *
   * Contract term length in months.
   */
  term_months: number;
  /**
   * Default Term Month Options
   *
   * The term lengths offered by default for this plan: its `term_months` plus any additional options, deduplicated. Not exhaustive — any term within the plan's range can also be quoted on request.
   */
  default_term_month_options: Array<number>;
  /**
   * Avg Cents Per Kwh 500
   *
   * Average all-in rate in cents per kWh at 500 kWh of monthly usage.
   */
  avg_cents_per_kwh_500: string;
  /**
   * Avg Cents Per Kwh 1000
   *
   * Average all-in rate in cents per kWh at 1,000 kWh of monthly usage.
   */
  avg_cents_per_kwh_1000: string;
  /**
   * Avg Cents Per Kwh 2000
   *
   * Average all-in rate in cents per kWh at 2,000 kWh of monthly usage.
   */
  avg_cents_per_kwh_2000: string;
  /**
   * Energy Rate Cents Per Kwh
   *
   * Energy charge in cents per kWh.
   */
  energy_rate_cents_per_kwh: string;
  /**
   * Tdu Name
   *
   * Full name of the utility (TDSP) delivering power.
   */
  tdu_name: string;
  /**
   * Tdu Shortname
   *
   * Short name of the utility (TDSP) delivering power.
   */
  tdu_shortname: string;
  /**
   * Delivery Rate Cents Per Kwh
   *
   * Utility (TDSP) delivery charge in cents per kWh.
   */
  delivery_rate_cents_per_kwh: string;
  /**
   * Delivery Base Monthly Dollars
   *
   * Utility (TDSP) fixed monthly delivery charge in dollars.
   */
  delivery_base_monthly_dollars: string;
  /**
   * Plan Monthly Cost Dollars
   *
   * Fixed monthly base charge in dollars.
   */
  plan_monthly_cost_dollars: string;
  /**
   * Early Termination Fee Dollars
   *
   * Fee in dollars for ending the contract early.
   */
  early_termination_fee_dollars: string;
  /**
   * Assumed Unlimited Kwh Usage
   *
   * Monthly kWh usage assumed for an unlimited-usage plan.
   */
  assumed_unlimited_kwh_usage: number;
  /**
   * Storage Monthly Credit Dollars
   *
   * Monthly bill credit in dollars for enrolled storage.
   */
  storage_monthly_credit_dollars: string;
  /**
   * Storage Monthly Credit Per Kwh Dollars
   *
   * Bill credit in dollars per stored kWh.
   */
  storage_monthly_credit_per_kwh_dollars?: string | null;
  /**
   * Assumed Storage Kwh
   *
   * Storage capacity in kWh assumed for the credit calculation.
   */
  assumed_storage_kwh?: string;
  /**
   * Solar Preactivation Credit Percent
   *
   * Credit percentage applied before solar activation.
   */
  solar_preactivation_credit_percent: number;
  /**
   * Export Rate Cents Per Kwh
   *
   * Credit in cents per kWh for energy exported to the grid.
   */
  export_rate_cents_per_kwh: string;
  /**
   * Assumed Export Kwh
   *
   * Monthly kWh assumed to be exported to the grid.
   */
  assumed_export_kwh: number;
  /**
   * Has Solar Buyback
   *
   * Whether the plan buys back exported solar energy.
   */
  has_solar_buyback: boolean;
  /**
   * Renewable Percent
   *
   * Share of the plan's energy from renewable sources, as a percentage.
   */
  renewable_percent?: number;
  /**
   * Device Program Terms
   *
   * Device program terms attached to the plan.
   */
  device_program_terms: Array<ProgramTerms>;
  /**
   * Ev Monthly Cost Dollars
   *
   * Deprecated; always null.
   *
   * @deprecated
   */
  ev_monthly_cost_dollars?: string | null;
  /**
   * Ev Charge Hour Start
   *
   * Deprecated; always null.
   *
   * @deprecated
   */
  ev_charge_hour_start?: number | null;
  /**
   * Ev Charge Hour End
   *
   * Deprecated; always null.
   *
   * @deprecated
   */
  ev_charge_hour_end?: number | null;
  /**
   * Earliest Start Date
   *
   * Earliest date service can start on this plan.
   */
  earliest_start_date: string;
  /**
   * Latest Start Date
   *
   * Latest date service can start on this plan.
   */
  latest_start_date: string;
};

/**
 * PlanBenchmark
 */
export type PlanBenchmark = {
  /**
   * Term Months
   *
   * Contract term length in months.
   */
  term_months: number;
  /**
   * Default Term Month Options
   *
   * The term lengths offered by default for this plan: its `term_months` plus any additional options, deduplicated. Not exhaustive — any term within the plan's range can also be quoted on request.
   */
  default_term_month_options: Array<number>;
  /**
   * Avg Cents Per Kwh 500
   *
   * Average all-in rate in cents per kWh at 500 kWh of monthly usage.
   */
  avg_cents_per_kwh_500: string;
  /**
   * Avg Cents Per Kwh 1000
   *
   * Average all-in rate in cents per kWh at 1,000 kWh of monthly usage.
   */
  avg_cents_per_kwh_1000: string;
  /**
   * Avg Cents Per Kwh 2000
   *
   * Average all-in rate in cents per kWh at 2,000 kWh of monthly usage.
   */
  avg_cents_per_kwh_2000: string;
  /**
   * Energy Rate Cents Per Kwh
   *
   * Energy charge in cents per kWh.
   */
  energy_rate_cents_per_kwh: string;
  /**
   * Tdu Name
   *
   * Full name of the utility (TDSP) delivering power.
   */
  tdu_name: string;
  /**
   * Tdu Shortname
   *
   * Short name of the utility (TDSP) delivering power.
   */
  tdu_shortname: string;
  /**
   * Delivery Rate Cents Per Kwh
   *
   * Utility (TDSP) delivery charge in cents per kWh.
   */
  delivery_rate_cents_per_kwh: string;
  /**
   * Delivery Base Monthly Dollars
   *
   * Utility (TDSP) fixed monthly delivery charge in dollars.
   */
  delivery_base_monthly_dollars: string;
  /**
   * Plan Monthly Cost Dollars
   *
   * Fixed monthly base charge in dollars.
   */
  plan_monthly_cost_dollars: string;
  /**
   * Early Termination Fee Dollars
   *
   * Fee in dollars for ending the contract early.
   */
  early_termination_fee_dollars: string;
  /**
   * Assumed Unlimited Kwh Usage
   *
   * Monthly kWh usage assumed for an unlimited-usage plan.
   */
  assumed_unlimited_kwh_usage: number;
  /**
   * Storage Monthly Credit Dollars
   *
   * Monthly bill credit in dollars for enrolled storage.
   */
  storage_monthly_credit_dollars: string;
  /**
   * Storage Monthly Credit Per Kwh Dollars
   *
   * Bill credit in dollars per stored kWh.
   */
  storage_monthly_credit_per_kwh_dollars?: string | null;
  /**
   * Assumed Storage Kwh
   *
   * Storage capacity in kWh assumed for the credit calculation.
   */
  assumed_storage_kwh?: string;
  /**
   * Solar Preactivation Credit Percent
   *
   * Credit percentage applied before solar activation.
   */
  solar_preactivation_credit_percent: number;
  /**
   * Export Rate Cents Per Kwh
   *
   * Credit in cents per kWh for energy exported to the grid.
   */
  export_rate_cents_per_kwh: string;
  /**
   * Assumed Export Kwh
   *
   * Monthly kWh assumed to be exported to the grid.
   */
  assumed_export_kwh: number;
  /**
   * Has Solar Buyback
   *
   * Whether the plan buys back exported solar energy.
   */
  has_solar_buyback: boolean;
  /**
   * Renewable Percent
   *
   * Share of the plan's energy from renewable sources, as a percentage.
   */
  renewable_percent?: number;
  /**
   * Device Program Terms
   *
   * Device program terms attached to the plan.
   */
  device_program_terms: Array<ProgramTerms>;
  /**
   * Ev Monthly Cost Dollars
   *
   * Deprecated; always null.
   *
   * @deprecated
   */
  ev_monthly_cost_dollars?: string | null;
  /**
   * Ev Charge Hour Start
   *
   * Deprecated; always null.
   *
   * @deprecated
   */
  ev_charge_hour_start?: number | null;
  /**
   * Ev Charge Hour End
   *
   * Deprecated; always null.
   *
   * @deprecated
   */
  ev_charge_hour_end?: number | null;
  /**
   * Monthly Dollar Savings 500 Kwh
   *
   * Estimated monthly savings in dollars at 500 kWh of usage.
   */
  monthly_dollar_savings_500_kwh: string;
  /**
   * Monthly Dollar Savings 1000 Kwh
   *
   * Estimated monthly savings in dollars at 1,000 kWh of usage.
   */
  monthly_dollar_savings_1000_kwh: string;
  /**
   * Monthly Dollar Savings 2000 Kwh
   *
   * Estimated monthly savings in dollars at 2,000 kWh of usage.
   */
  monthly_dollar_savings_2000_kwh: string;
};

/**
 * BenchmarksRequest
 */
export type BenchmarksRequest = {
  /**
   * Postal Code
   *
   * Postal code of the service location.
   */
  postal_code: string;
  /**
   * Address 1
   *
   * First line of the street address.
   */
  address_1?: string | null;
  /**
   * Address 2
   *
   * Second line of the street address, such as a unit or apartment.
   */
  address_2?: string | null;
  /**
   * City
   *
   * City of the address.
   */
  city?: string | null;
  /**
   * State
   *
   * Two-letter state code of the address.
   */
  state?: string | null;
  /**
   * Utility Number
   *
   * Utility meter identifier (ESI ID) for the location.
   */
  utility_number?: string | null;
};

/**
 * ComparablePlan
 */
export type ComparablePlan = {
  /**
   * Provider Name
   *
   * Name of the retail electricity provider offering the plan.
   */
  provider_name: string;
  /**
   * Plan Name
   *
   * Name of the plan as published by the provider.
   */
  plan_name: string;
  /**
   * Plan Display Name
   *
   * Provider and plan name combined for display.
   */
  plan_display_name: string;
  /**
   * Energy Rate Cents Per Kwh
   *
   * Energy charge in cents per kWh. Null when the plan does not publish a flat energy rate.
   */
  energy_rate_cents_per_kwh?: string | null;
  /**
   * Plan Monthly Cost Dollars
   *
   * Fixed monthly base charge in dollars. Null when the plan does not publish one.
   */
  plan_monthly_cost_dollars?: string | null;
  /**
   * Export Rate Cents Per Kwh
   *
   * Credit in cents per kWh for energy exported back to the grid. Null for plans without solar buyback.
   */
  export_rate_cents_per_kwh?: string | null;
  /**
   * Assumed Export Kwh
   *
   * Monthly excess kWh the plan assumes you export back to the grid. Only published by solar plans, so null otherwise.
   */
  assumed_export_kwh?: number | null;
  /**
   * Ev Charging Assumed Charging Kwh
   *
   * Monthly EV charging usage in kWh the plan's EV pricing assumes. Null for plans without an EV charging program.
   */
  ev_charging_assumed_charging_kwh?: string | null;
  /**
   * Ev Charging Energy Rate Cents Per Kwh
   *
   * Energy charge in cents per kWh during the plan's EV charging window. Null for plans without an EV charging program.
   */
  ev_charging_energy_rate_cents_per_kwh?: string | null;
  /**
   * Ev Charging Schedule
   *
   * Time window during which the EV charging rate applies. Null for plans without an EV charging program.
   */
  ev_charging_schedule?: string | null;
  /**
   * Ev Charging Includes Tdsp
   *
   * Whether the EV charging rate includes TDSP delivery charges. Null for plans without an EV charging program.
   */
  ev_charging_includes_tdsp?: boolean | null;
  /**
   * Avg Cents Per Kwh 500
   *
   * Average all-in rate in cents per kWh at 500 kWh of monthly usage. Null when not published.
   */
  avg_cents_per_kwh_500?: string | null;
  /**
   * Avg Cents Per Kwh 1000
   *
   * Average all-in rate in cents per kWh at 1,000 kWh of monthly usage. Null when not published.
   */
  avg_cents_per_kwh_1000?: string | null;
  /**
   * Avg Cents Per Kwh 2000
   *
   * Average all-in rate in cents per kWh at 2,000 kWh of monthly usage. Null when not published.
   */
  avg_cents_per_kwh_2000?: string | null;
  /**
   * Refresh Date
   *
   * The date we last refreshed this plan's details from the provider.
   */
  refresh_date: string;
};

/**
 * AlternativePlan
 */
export type AlternativePlan = {
  /**
   * Plan Group Uuid
   *
   * Unique identifier of the alternative plan group.
   */
  plan_group_uuid: string;
  /**
   * Name
   *
   * Display name of the alternative plan.
   */
  name: string;
  /**
   * Suggestion Text
   *
   * Static text to show before the action.
   */
  suggestion_text: string;
  /**
   * Action Text
   *
   * Text of the link to the alternative plan.
   */
  action_text: string;
};

/**
 * CustomPlanResponse
 */
export type CustomPlanResponse = {
  /**
   * Term Months
   *
   * Contract term length in months.
   */
  term_months: number;
  /**
   * Default Term Month Options
   *
   * The term lengths offered by default for this plan: its `term_months` plus any additional options, deduplicated. Not exhaustive — any term within the plan's range can also be quoted on request.
   */
  default_term_month_options: Array<number>;
  /**
   * Avg Cents Per Kwh 500
   *
   * Average all-in rate in cents per kWh at 500 kWh of monthly usage.
   */
  avg_cents_per_kwh_500: string;
  /**
   * Avg Cents Per Kwh 1000
   *
   * Average all-in rate in cents per kWh at 1,000 kWh of monthly usage.
   */
  avg_cents_per_kwh_1000: string;
  /**
   * Avg Cents Per Kwh 2000
   *
   * Average all-in rate in cents per kWh at 2,000 kWh of monthly usage.
   */
  avg_cents_per_kwh_2000: string;
  /**
   * Energy Rate Cents Per Kwh
   *
   * Energy charge in cents per kWh.
   */
  energy_rate_cents_per_kwh: string;
  /**
   * Tdu Name
   *
   * Full name of the utility (TDSP) delivering power.
   */
  tdu_name: string;
  /**
   * Tdu Shortname
   *
   * Short name of the utility (TDSP) delivering power.
   */
  tdu_shortname: string;
  /**
   * Delivery Rate Cents Per Kwh
   *
   * Utility (TDSP) delivery charge in cents per kWh.
   */
  delivery_rate_cents_per_kwh: string;
  /**
   * Delivery Base Monthly Dollars
   *
   * Utility (TDSP) fixed monthly delivery charge in dollars.
   */
  delivery_base_monthly_dollars: string;
  /**
   * Plan Monthly Cost Dollars
   *
   * Fixed monthly base charge in dollars.
   */
  plan_monthly_cost_dollars: string;
  /**
   * Early Termination Fee Dollars
   *
   * Fee in dollars for ending the contract early.
   */
  early_termination_fee_dollars: string;
  /**
   * Assumed Unlimited Kwh Usage
   *
   * Monthly kWh usage assumed for an unlimited-usage plan.
   */
  assumed_unlimited_kwh_usage: number;
  /**
   * Storage Monthly Credit Dollars
   *
   * Monthly bill credit in dollars for enrolled storage.
   */
  storage_monthly_credit_dollars: string;
  /**
   * Storage Monthly Credit Per Kwh Dollars
   *
   * Bill credit in dollars per stored kWh.
   */
  storage_monthly_credit_per_kwh_dollars?: string | null;
  /**
   * Assumed Storage Kwh
   *
   * Storage capacity in kWh assumed for the credit calculation.
   */
  assumed_storage_kwh?: string;
  /**
   * Solar Preactivation Credit Percent
   *
   * Credit percentage applied before solar activation.
   */
  solar_preactivation_credit_percent: number;
  /**
   * Export Rate Cents Per Kwh
   *
   * Credit in cents per kWh for energy exported to the grid.
   */
  export_rate_cents_per_kwh: string;
  /**
   * Assumed Export Kwh
   *
   * Monthly kWh assumed to be exported to the grid.
   */
  assumed_export_kwh: number;
  /**
   * Has Solar Buyback
   *
   * Whether the plan buys back exported solar energy.
   */
  has_solar_buyback: boolean;
  /**
   * Renewable Percent
   *
   * Share of the plan's energy from renewable sources, as a percentage.
   */
  renewable_percent?: number;
  /**
   * Device Program Terms
   *
   * Device program terms attached to the plan.
   */
  device_program_terms: Array<ProgramTerms>;
  /**
   * Ev Monthly Cost Dollars
   *
   * Deprecated; always null.
   *
   * @deprecated
   */
  ev_monthly_cost_dollars?: string | null;
  /**
   * Ev Charge Hour Start
   *
   * Deprecated; always null.
   *
   * @deprecated
   */
  ev_charge_hour_start?: number | null;
  /**
   * Ev Charge Hour End
   *
   * Deprecated; always null.
   *
   * @deprecated
   */
  ev_charge_hour_end?: number | null;
  /**
   * Earliest Start Date
   *
   * Earliest date service can start on this plan.
   */
  earliest_start_date: string;
  /**
   * Latest Start Date
   *
   * Latest date service can start on this plan.
   */
  latest_start_date: string;
  /**
   * Uuid
   *
   * Unique identifier of the plan (rate).
   */
  uuid: string;
  /**
   * Group Uuid
   *
   * Unique identifier of the plan group.
   */
  group_uuid: string;
  /**
   * Is Default For App
   *
   * Whether this is the app's default plan.
   */
  is_default_for_app?: boolean | null;
  /**
   * Name
   *
   * Display name of the plan.
   */
  name: string;
  /**
   * Links to the plan's rate documents.
   */
  links: RateLinks;
  /**
   * Plan Links
   *
   * Named links to the plan's rate documents.
   */
  plan_links?: Array<PlanLink> | null;
  /**
   * Usage Profile Uuid
   *
   * UUID of the most recent completed usage profile for this ESI ID (if available within the past 30 days).
   */
  usage_profile_uuid?: string | null;
  /**
   * Presentation content for rendering the plan.
   */
  presentation: PlanPresentation;
  /**
   * Alternative Plans
   *
   * Other plans to offer from this plan's enrollment offer step, or null if there are none.
   */
  alternative_plans?: Array<AlternativePlan> | null;
  /**
   * Partner Commission Per Month Dollars
   *
   * Partner commission in dollars per month for the plan.
   */
  partner_commission_per_month_dollars: string;
};

/**
 * PlanHeadline
 */
export type PlanHeadline = {
  /**
   * Id
   *
   * Stable key for the logic that produced the headline.
   */
  id: string;
  /**
   * Headline
   *
   * Short headline for the plan.
   */
  headline: string;
  /**
   * Description
   *
   * Optional longer description of the plan.
   */
  description: string | null;
};

/**
 * PlanLink
 */
export type PlanLink = {
  /**
   * Name
   *
   * Full display name of the rate document.
   */
  name: string;
  /**
   * Short Name
   *
   * Short display name of the rate document.
   */
  short_name: string;
  /**
   * Url
   *
   * URL to the rate document PDF.
   */
  url: string;
};

/**
 * PlanPresentation
 */
export type PlanPresentation = {
  /**
   * Headline block summarizing the plan.
   */
  headline: PlanHeadline;
  /**
   * Value Propositions
   *
   * List of value propositions associated with the plan.
   */
  value_propositions: Array<PlanValueProp>;
};

/**
 * PlanValueProp
 */
export type PlanValueProp = {
  /**
   * Id
   *
   * Stable key for the value proposition.
   */
  id: string;
  /**
   * Text
   *
   * Value proposition text shown to the customer.
   */
  text: string;
  /**
   * Icon
   *
   * Enum of icon to display along side the value proposition.
   */
  icon:
    | "battery-charging"
    | "battery-full"
    | "car"
    | "check-circle"
    | "clock"
    | "cloud-lightning"
    | "coins"
    | "heart-hand"
    | "layers"
    | "leaf"
    | "piggy-bank"
    | "sun"
    | "tag"
    | "tool"
    | "truck";
  /**
   * Callouts
   *
   * A list of additional notes to call out from the text.
   */
  callouts: Array<PlanValuePropCallout>;
};

/**
 * PlanValuePropCallout
 */
export type PlanValuePropCallout = {
  /**
   * Text
   *
   * Callout text explaining the highlighted portion of the value proposition.
   */
  text: string;
  /**
   * Matches
   *
   * Snippet of text from the value proposition to match the callout to.
   */
  matches: string;
};

/**
 * RateLinks
 */
export type RateLinks = {
  /**
   * Efl
   *
   * URL to the plan's Electricity Facts Label (EFL) PDF.
   */
  efl: string;
  /**
   * Tos
   *
   * URL to the plan's Terms of Service PDF.
   */
  tos: string;
  /**
   * Yrac
   *
   * URL to the plan's Your Rights as a Customer (YRAC) PDF.
   */
  yrac: string;
};

/**
 * CustomPlanRequest
 */
export type CustomPlanRequest = {
  /**
   * Esi Id
   *
   * ESI ID (meter identifier) for the service location.
   */
  esi_id?: string | null;
  /**
   * Postal Code
   *
   * Postal code of the service location.
   */
  postal_code?: string | null;
  /**
   * Utility
   *
   * Identifier of the utility (TDSP) serving the location.
   */
  utility?: string | null;
  /**
   * Plan Group
   *
   * UUID of the plan group to request a rate for.
   */
  plan_group?: string | null;
  /**
   * Term Months
   *
   * Contract term length in months being requested.
   */
  term_months?: number | null;
  /**
   * Energy Rate Cents Per Kwh
   *
   * Will be rounded to nearest 2 decimal places.
   */
  energy_rate_cents_per_kwh?: number | string | null;
  /**
   * Partner Commission Per Month Dollars
   *
   * Will be rounded to nearest 2 decimal places.
   */
  partner_commission_per_month_dollars?: number | string | null;
};

/**
 * OptOutReasonsQuery
 */
export type OptOutReasonsQuery = {
  /**
   * Survey filter. Returns a curated list of reasons for that flow. One of `enrollment_switch`, `enrollment_move_in`, or `renewal`. Omit to return every defined reason.
   */
  filter?: OptOutFilter | null;
};

/**
 * OptOutReasonOption
 */
export type OptOutReasonOption = {
  /**
   * Reason
   *
   * Machine-readable code for the opt-out reason.
   */
  reason: string;
  /**
   * Label
   *
   * Human-readable text for the opt-out reason.
   */
  label: string;
  /**
   * Byline
   *
   * Optional copy shown when the customer selects this reason to help prevent churn. Includes the app's configured support phone number when relevant.
   */
  byline?: string | null;
  /**
   * Follow Up Placeholder
   *
   * When present, the UI should show a text input after the customer selects this reason. The placeholder text guides what detail to collect. Submit the customer's response in the `detail` field of the opt-out request.
   */
  follow_up_placeholder?: string | null;
};

/**
 * DisallowedDateResponse
 *
 * Response containing a single disallowed date with reason.
 */
export type DisallowedDateResponse = {
  /**
   * Date
   *
   * Date that is not allowed for service start.
   */
  date: string;
  /**
   * Reason
   *
   * Reason the date is disallowed (weekend or holiday).
   */
  reason: "weekend" | "holiday";
  /**
   * Name
   *
   * Holiday name if applicable.
   */
  name?: string | null;
};

/**
 * DisallowedDatesResponse
 *
 * Response containing all disallowed dates for the upcoming period.
 */
export type DisallowedDatesResponse = {
  /**
   * Disallowed Dates
   *
   * List of dates that cannot be selected for service start.
   */
  disallowed_dates: Array<DisallowedDateResponse>;
};

/**
 * ListContractsQuery
 */
export type ListContractsQuery = {
  /**
   * State
   *
   * Filters to contracts in this state.
   */
  state?: "active" | "ended" | "expired" | "queued" | null;
  /**
   * Account Uuid
   *
   * Filters to contracts for this account.
   */
  account_uuid?: string | null;
  /**
   * Location Uuid
   *
   * Filters to contracts for this service location.
   */
  location_uuid?: string | null;
  /**
   * Plan Group Uuid
   *
   * Filters to contracts on this plan.
   */
  plan_group_uuid?: string | null;
};

/**
 * ContractResponse
 */
export type ContractResponse = {
  /**
   * Term Months
   *
   * Contract term length in months.
   */
  term_months: number;
  /**
   * Default Term Month Options
   *
   * The term lengths offered by default for this plan: its `term_months` plus any additional options, deduplicated. Not exhaustive — any term within the plan's range can also be quoted on request.
   */
  default_term_month_options: Array<number>;
  /**
   * Avg Cents Per Kwh 500
   *
   * Average all-in rate in cents per kWh at 500 kWh of monthly usage.
   */
  avg_cents_per_kwh_500: string;
  /**
   * Avg Cents Per Kwh 1000
   *
   * Average all-in rate in cents per kWh at 1,000 kWh of monthly usage.
   */
  avg_cents_per_kwh_1000: string;
  /**
   * Avg Cents Per Kwh 2000
   *
   * Average all-in rate in cents per kWh at 2,000 kWh of monthly usage.
   */
  avg_cents_per_kwh_2000: string;
  /**
   * Energy Rate Cents Per Kwh
   *
   * Energy charge in cents per kWh.
   */
  energy_rate_cents_per_kwh: string;
  /**
   * Tdu Name
   *
   * Full name of the utility (TDSP) delivering power.
   */
  tdu_name: string;
  /**
   * Tdu Shortname
   *
   * Short name of the utility (TDSP) delivering power.
   */
  tdu_shortname: string;
  /**
   * Delivery Rate Cents Per Kwh
   *
   * Utility (TDSP) delivery charge in cents per kWh.
   */
  delivery_rate_cents_per_kwh: string;
  /**
   * Delivery Base Monthly Dollars
   *
   * Utility (TDSP) fixed monthly delivery charge in dollars.
   */
  delivery_base_monthly_dollars: string;
  /**
   * Plan Monthly Cost Dollars
   *
   * Fixed monthly base charge in dollars.
   */
  plan_monthly_cost_dollars: string;
  /**
   * Early Termination Fee Dollars
   *
   * Fee in dollars for ending the contract early.
   */
  early_termination_fee_dollars: string;
  /**
   * Assumed Unlimited Kwh Usage
   *
   * Monthly kWh usage assumed for an unlimited-usage plan.
   */
  assumed_unlimited_kwh_usage: number;
  /**
   * Storage Monthly Credit Dollars
   *
   * Monthly bill credit in dollars for enrolled storage.
   */
  storage_monthly_credit_dollars: string;
  /**
   * Storage Monthly Credit Per Kwh Dollars
   *
   * Bill credit in dollars per stored kWh.
   */
  storage_monthly_credit_per_kwh_dollars?: string | null;
  /**
   * Assumed Storage Kwh
   *
   * Storage capacity in kWh assumed for the credit calculation.
   */
  assumed_storage_kwh?: string;
  /**
   * Solar Preactivation Credit Percent
   *
   * Credit percentage applied before solar activation.
   */
  solar_preactivation_credit_percent: number;
  /**
   * Export Rate Cents Per Kwh
   *
   * Credit in cents per kWh for energy exported to the grid.
   */
  export_rate_cents_per_kwh: string;
  /**
   * Assumed Export Kwh
   *
   * Monthly kWh assumed to be exported to the grid.
   */
  assumed_export_kwh: number;
  /**
   * Has Solar Buyback
   *
   * Whether the plan buys back exported solar energy.
   */
  has_solar_buyback: boolean;
  /**
   * Renewable Percent
   *
   * Share of the plan's energy from renewable sources, as a percentage.
   */
  renewable_percent?: number;
  /**
   * Device Program Terms
   *
   * Device program terms attached to the plan.
   */
  device_program_terms: Array<ProgramTerms>;
  /**
   * Ev Monthly Cost Dollars
   *
   * Deprecated; always null.
   *
   * @deprecated
   */
  ev_monthly_cost_dollars?: string | null;
  /**
   * Ev Charge Hour Start
   *
   * Deprecated; always null.
   *
   * @deprecated
   */
  ev_charge_hour_start?: number | null;
  /**
   * Ev Charge Hour End
   *
   * Deprecated; always null.
   *
   * @deprecated
   */
  ev_charge_hour_end?: number | null;
  /**
   * Uuid
   *
   * The contract's unique identifier.
   */
  uuid: string;
  /**
   * Location Uuid
   *
   * The unique identifier of the service location.
   */
  location_uuid: string;
  /**
   * Account Uuid
   *
   * The unique identifier of the account.
   */
  account_uuid: string;
  /**
   * Plan Uuid
   *
   * The unique identifier of the plan.
   */
  plan_uuid: string;
  /**
   * Plan Group Uuid
   *
   * The unique identifier of the plan group.
   */
  plan_group_uuid: string;
  /**
   * Name
   *
   * The name of the plan.
   */
  name: string;
  /**
   * State
   *
   * The contract's current state.
   */
  state: string;
  /**
   * Start Date
   *
   * The date the contract begins service.
   */
  start_date: string;
  /**
   * End Date
   *
   * The date the contract ended prematurely, null if the contract has not ended early.
   */
  end_date?: string | null;
  /**
   * Expiration Date
   *
   * The date the contract's term expires.
   */
  expiration_date: string;
  /**
   * Partner Commission Dollars
   *
   * The monthly commission paid to the partner, in dollars.
   */
  partner_commission_dollars: string | null;
  /**
   * Affiliate Commission Dollars
   *
   * The monthly commission paid to the affiliate, in dollars.
   */
  affiliate_commission_dollars: string | null;
};

/**
 * PagedContractResponse
 */
export type PagedContractResponse = {
  /**
   * Data
   *
   * The page of results, ordered as the endpoint returns them.
   */
  data: Array<ContractResponse>;
  /**
   * Has More
   *
   * Whether more results exist after this page.
   */
  has_more: boolean;
  /**
   * Limit
   *
   * Maximum number of results returned per page.
   */
  limit: number;
  /**
   * Offset
   *
   * Number of results skipped before this page.
   */
  offset: number;
  /**
   * Next Page Url
   *
   * URL of the next page, or null when this is the last page.
   */
  next_page_url?: string | null;
};

/**
 * DeviceManufacturer
 */
export type DeviceManufacturer = {
  /**
   * Name
   */
  name: string;
  /**
   * Display Name
   */
  display_name: string;
  /**
   * Supported Devices
   */
  supported_devices: Array<string>;
  /**
   * Auth Type
   */
  auth_type: string;
};

/**
 * UsageProfile
 *
 * Response schema for usage profile resource.
 */
export type UsageProfile = {
  /**
   * Uuid
   *
   * UUID of the usage profile.
   */
  uuid: string;
  /**
   * Status
   *
   * Status: pending, completed, or failed.
   */
  status: string;
  /**
   * Utility Account Number
   *
   * ESI ID / utility account number.
   */
  utility_account_number?: string | null;
  /**
   * Error Message
   *
   * Error message if status is 'failed'.
   */
  error_message?: string | null;
  /**
   * Created At
   *
   * When the usage profile was created.
   */
  created_at: string;
  /**
   * Start Date
   *
   * Start date of interval data.
   */
  start_date?: string | null;
  /**
   * End Date
   *
   * End date of interval data.
   */
  end_date?: string | null;
};

/**
 * UsageProfileDayData
 *
 * Single day of interval data for usage profile upload.
 */
export type UsageProfileDayData = {
  /**
   * Date
   *
   * Date in ISO format YYYY-MM-DD.
   */
  date: string;
  /**
   * Import
   *
   * Import intervals in kWh. Typically 96 values (15-min intervals), but 92 for DST spring forward or 100 for DST fall back.
   */
  import: Array<number | string>;
  /**
   * Export
   *
   * Export intervals in kWh. Optional, defaults to zeros if not provided.
   */
  export?: Array<number | string> | null;
};

/**
 * UsageProfileUploadRequest
 *
 * Request schema for usage profile upload.
 */
export type UsageProfileUploadRequest = {
  /**
   * Data
   *
   * Array of daily interval data. At least 1 year of data required. Each day must have 92-100 intervals (typically 96 for non-DST days).
   */
  data: Array<UsageProfileDayData>;
  /**
   * Utility Account Number
   *
   * ESI ID / utility account number associated with the usage profile.
   */
  utility_account_number: string;
  /**
   * Timezone
   *
   * Timezone for interval data. Currently only America/Chicago is supported.
   */
  timezone?: string;
  /**
   * Interval Minutes
   *
   * Minutes per interval. Currently only 15 is supported.
   */
  interval_minutes?: number;
};

/**
 * UsageProfileFetchRequest
 */
export type UsageProfileFetchRequest = {
  /**
   * Comparison Invoice Uuid
   *
   * UUID of the comparison invoice to fetch usage data for.
   */
  comparison_invoice_uuid: string;
  /**
   * User Authorizes Usage Fetch
   *
   * Whether the user has authorized the usage fetch. In Texas, this is using Smart Meter Texas to fetch usage data.
   */
  user_authorizes_usage_fetch: boolean;
};

/**
 * AccountUpdateRequest
 */
export type AccountUpdateRequest = {
  /**
   * First Name
   *
   * Customer's first name.
   */
  first_name?: string | null;
  /**
   * Last Name
   *
   * Customer's last name.
   */
  last_name?: string | null;
  /**
   * Email
   *
   * Customer's email address.
   */
  email?: string | null;
  /**
   * Phone Number
   *
   * Customer's phone number.
   */
  phone_number?: string | null;
  /**
   * Birth Date
   *
   * Customer's date of birth.
   */
  birth_date?: string | null;
  enrollment_requirements?: RequirementsPatch | null;
};

/**
 * CreatePaymentSessionResponse
 */
export type CreatePaymentSessionResponse = {
  /**
   * Provider
   *
   * Payment provider for the session.
   */
  provider?: "stripe";
  /**
   * Client Secret
   *
   * Client secret used to complete the payment on the provider.
   */
  client_secret: string;
};

/**
 * PaymentMethodAddedRequest
 */
export type PaymentMethodAddedRequest = {
  /**
   * Payment Method Id
   *
   * Identifier of the payment method to add.
   */
  payment_method_id?: string | null;
};

/**
 * PaymentMethodResponse
 */
export type PaymentMethodResponse = {
  /**
   * Type
   *
   * Type of the payment method.
   */
  type: string;
  /**
   * Card Brand
   *
   * Card brand.
   */
  card_brand: string;
  /**
   * Card Last4
   *
   * Last four digits of the card number.
   */
  card_last4: string;
  /**
   * Card Exp Month
   *
   * Card expiration month.
   */
  card_exp_month: number;
  /**
   * Card Exp Year
   *
   * Card expiration year.
   */
  card_exp_year: number;
  /**
   * Card Postal Code
   *
   * Billing postal code for the card, when available.
   */
  card_postal_code: string | null;
};

/**
 * BillingAddressResponse
 */
export type BillingAddressResponse = {
  /**
   * Address 1
   *
   * First line of the street address.
   */
  address_1: string;
  /**
   * Address 2
   *
   * Second line of the street address, such as a unit or apartment.
   */
  address_2?: string | null;
  /**
   * City
   *
   * City of the address.
   */
  city: string;
  /**
   * State
   *
   * Two-letter state code of the address.
   */
  state: string;
  /**
   * Postal Code
   *
   * Five-digit postal code of the address.
   */
  postal_code: string;
  /**
   * Same As Service Address
   *
   * Whether the billing address is the same as the service address.
   */
  same_as_service_address: boolean;
};

/**
 * Address
 */
export type Address = {
  /**
   * Address 1
   *
   * First line of the street address.
   */
  address_1: string;
  /**
   * Address 2
   *
   * Second line of the street address, such as a unit or apartment.
   */
  address_2?: string | null;
  /**
   * City
   *
   * City of the address.
   */
  city: string;
  /**
   * State
   *
   * Two-letter state code of the address.
   */
  state: string;
  /**
   * Postal Code
   *
   * Five-digit postal code of the address.
   */
  postal_code: string;
};

/**
 * UpdateBillingAddressRequest
 */
export type UpdateBillingAddressRequest = {
  /**
   * Same As Service Address
   *
   * Whether the billing address is the same as the service address.
   */
  same_as_service_address?: boolean;
  /**
   * Billing address, required when it differs from the service address.
   */
  address?: Address | null;
};

/**
 * InvoiceResponse
 */
export type InvoiceResponse = {
  /**
   * Number
   *
   * Invoice number.
   */
  number: string;
  /**
   * Invoice Date
   *
   * Date the invoice was issued.
   */
  invoice_date: string;
  /**
   * Payment Due Date
   *
   * Date payment is due.
   */
  payment_due_date: string;
  /**
   * Billing Period Start
   *
   * First date of the billing period.
   */
  billing_period_start: string;
  /**
   * Billing Period End
   *
   * Last date of the billing period.
   */
  billing_period_end: string;
  /**
   * Subtotal Cents
   *
   * Subtotal before miscellaneous charges, in cents.
   */
  subtotal_cents?: number | null;
  /**
   * Subtotal
   *
   * Subtotal before miscellaneous charges, in dollars.
   */
  subtotal?: string | null;
  /**
   * Misc Total Cents
   *
   * Total of miscellaneous charges, in cents.
   */
  misc_total_cents?: number | null;
  /**
   * Misc Total
   *
   * Total of miscellaneous charges, in dollars.
   */
  misc_total?: string | null;
  /**
   * Total Cents
   *
   * Invoice total in cents.
   */
  total_cents: number;
  /**
   * Total
   *
   * Invoice total in dollars.
   */
  total: string;
  /**
   * Total Kwh
   *
   * Total energy billed on the invoice, in kWh.
   */
  total_kwh: string;
  /**
   * Exported Kwh
   *
   * Total energy exported to the grid during the billing period, in kWh.
   */
  exported_kwh: string;
  /**
   * Avg Cents Per Kwh
   *
   * Average all-in rate on the invoice, in cents per kWh.
   */
  avg_cents_per_kwh: string;
  /**
   * Pdf
   *
   * URL to the invoice PDF.
   */
  pdf: string;
  /**
   * Paid At
   *
   * When the invoice was paid.
   */
  paid_at?: string | null;
  /**
   * Voided At
   *
   * When the invoice was voided.
   */
  voided_at?: string | null;
  /**
   * Is Deferred
   *
   * Whether the invoice's payment is deferred.
   */
  is_deferred: boolean;
  /**
   * Rebill Invoice
   *
   * Number of the invoice that rebills this one, when applicable.
   */
  rebill_invoice?: string | null;
  /**
   * Last Payment Failed At
   *
   * When the most recent payment attempt failed.
   */
  last_payment_failed_at?: string | null;
};

/**
 * PagedInvoiceResponse
 */
export type PagedInvoiceResponse = {
  /**
   * Data
   *
   * The page of results, ordered as the endpoint returns them.
   */
  data: Array<InvoiceResponse>;
  /**
   * Has More
   *
   * Whether more results exist after this page.
   */
  has_more: boolean;
  /**
   * Limit
   *
   * Maximum number of results returned per page.
   */
  limit: number;
  /**
   * Offset
   *
   * Number of results skipped before this page.
   */
  offset: number;
  /**
   * Next Page Url
   *
   * URL of the next page, or null when this is the last page.
   */
  next_page_url?: string | null;
};

/**
 * InvoiceChargeGroupResponse
 */
export type InvoiceChargeGroupResponse = {
  /**
   * Key
   *
   * Machine-readable key for the charge group.
   */
  key: string;
  /**
   * Name
   *
   * Display name of the charge group.
   */
  name: string;
  /**
   * Total Cents
   *
   * Total of the group's charges, in cents.
   */
  total_cents: number;
  /**
   * Total
   *
   * Total of the group's charges, in dollars.
   */
  total: string;
  /**
   * Charges
   *
   * Individual charges in the group.
   */
  charges: Array<InvoiceChargeResponse>;
};

/**
 * InvoiceChargeResponse
 */
export type InvoiceChargeResponse = {
  /**
   * Category
   *
   * Category of the charge.
   */
  category: string;
  /**
   * Type
   *
   * Specific type of the charge.
   */
  type: string;
  /**
   * Taxable
   *
   * Whether the charge is taxable.
   */
  taxable: boolean;
  /**
   * Description
   *
   * Human-readable description of the charge.
   */
  description: string;
  /**
   * Quantity Text
   *
   * Quantity for the charge, formatted for display.
   */
  quantity_text: string;
  /**
   * Rate Text
   *
   * Rate for the charge, formatted for display.
   */
  rate_text: string;
  /**
   * Amount Cents
   *
   * Charge amount in cents.
   */
  amount_cents: number;
  /**
   * Amount
   *
   * Charge amount in dollars.
   */
  amount: string;
};

/**
 * InvoiceDetailResponse
 */
export type InvoiceDetailResponse = {
  /**
   * Number
   *
   * Invoice number.
   */
  number: string;
  /**
   * Invoice Date
   *
   * Date the invoice was issued.
   */
  invoice_date: string;
  /**
   * Payment Due Date
   *
   * Date payment is due.
   */
  payment_due_date: string;
  /**
   * Billing Period Start
   *
   * First date of the billing period.
   */
  billing_period_start: string;
  /**
   * Billing Period End
   *
   * Last date of the billing period.
   */
  billing_period_end: string;
  /**
   * Subtotal Cents
   *
   * Subtotal before miscellaneous charges, in cents.
   */
  subtotal_cents?: number | null;
  /**
   * Subtotal
   *
   * Subtotal before miscellaneous charges, in dollars.
   */
  subtotal?: string | null;
  /**
   * Misc Total Cents
   *
   * Total of miscellaneous charges, in cents.
   */
  misc_total_cents?: number | null;
  /**
   * Misc Total
   *
   * Total of miscellaneous charges, in dollars.
   */
  misc_total?: string | null;
  /**
   * Total Cents
   *
   * Invoice total in cents.
   */
  total_cents: number;
  /**
   * Total
   *
   * Invoice total in dollars.
   */
  total: string;
  /**
   * Total Kwh
   *
   * Total energy billed on the invoice, in kWh.
   */
  total_kwh: string;
  /**
   * Exported Kwh
   *
   * Total energy exported to the grid during the billing period, in kWh.
   */
  exported_kwh: string;
  /**
   * Avg Cents Per Kwh
   *
   * Average all-in rate on the invoice, in cents per kWh.
   */
  avg_cents_per_kwh: string;
  /**
   * Pdf
   *
   * URL to the invoice PDF.
   */
  pdf: string;
  /**
   * Paid At
   *
   * When the invoice was paid.
   */
  paid_at?: string | null;
  /**
   * Voided At
   *
   * When the invoice was voided.
   */
  voided_at?: string | null;
  /**
   * Is Deferred
   *
   * Whether the invoice's payment is deferred.
   */
  is_deferred: boolean;
  /**
   * Rebill Invoice
   *
   * Number of the invoice that rebills this one, when applicable.
   */
  rebill_invoice?: string | null;
  /**
   * Last Payment Failed At
   *
   * When the most recent payment attempt failed.
   */
  last_payment_failed_at?: string | null;
  /**
   * Charge Groups
   *
   * Charges on the invoice, grouped by category.
   */
  charge_groups: Array<InvoiceChargeGroupResponse>;
  /**
   * Payment Failures
   *
   * Failed payment attempts for the invoice, newest first.
   */
  payment_failures: Array<PaymentFailureResponse>;
};

/**
 * PaymentFailureResponse
 */
export type PaymentFailureResponse = {
  /**
   * Failed At
   *
   * When the payment attempt failed.
   */
  failed_at: string;
  /**
   * Amount Cents
   *
   * Failed payment amount in cents.
   */
  amount_cents: number;
  /**
   * Amount
   *
   * Failed payment amount in dollars.
   */
  amount: string;
  /**
   * Reason
   *
   * Machine-readable code for why the payment failed.
   */
  reason: string;
  /**
   * Description
   *
   * Human-readable explanation of the payment failure.
   */
  description: string;
};

/**
 * PayInvoiceRequest
 */
export type PayInvoiceRequest = {
  /**
   * Allow Payment Before Due Date
   *
   * Whether to allow paying the invoice before its due date.
   */
  allow_payment_before_due_date?: boolean | null;
};

/**
 * ComparisonInvoiceResponse
 */
export type ComparisonInvoiceResponse = {
  /**
   * Uuid
   *
   * Unique identifier of the uploaded comparison invoice.
   */
  uuid: string;
  /**
   * Created At
   *
   * When the comparison invoice was uploaded.
   */
  created_at: string;
  /**
   * File
   *
   * URL to the uploaded invoice PDF.
   */
  file: string;
  /**
   * Processing Error
   *
   * Error message when processing failed, null otherwise.
   */
  processing_error?: string | null;
  /**
   * Processed At
   *
   * When the invoice finished processing.
   */
  processed_at?: string | null;
};

/**
 * SimpleCompareQuery
 */
export type SimpleCompareQuery = {
  /**
   * Plan Group
   *
   * Identifier of the plan to compare against the customer's current plan.
   */
  plan_group?: string | null;
  /**
   * Uuid
   *
   * UUID of a specific comparison invoice to use; the most recent is used when omitted.
   */
  uuid?: string | null;
};

/**
 * ComparisonInvoiceComparedWithPlan
 */
export type ComparisonInvoiceComparedWithPlan = {
  /**
   * Group Uuid
   *
   * The UUID of the plan group being compared against.
   */
  group_uuid: string;
  /**
   * Name
   *
   * The name of the plan being compared against.
   */
  name: string;
  /**
   * Total Dollars
   *
   * The estimated total amount that the same usage would cost on this plan.
   */
  total_dollars: string;
  /**
   * Usage Profile Uuid
   *
   * UUID of the most recent completed usage profile for this ESI ID (if available within the past 30 days).
   */
  usage_profile_uuid?: string | null;
  /**
   * Energy Charge Cents Per Kwh
   *
   * The base energy charge in cents per kWh for this plan. This is only the energy component and does not include TDU delivery charges, base fees, usage credits, time-of-use variations, taxes, or other surcharges that factor into the total plan cost. Not recommended for plan comparison as it is just one component of pricing.
   */
  energy_charge_cents_per_kwh?: string | null;
};

/**
 * ComparisonInvoiceParsedInvoiceData
 */
export type ComparisonInvoiceParsedInvoiceData = {
  /**
   * Total Dollars
   *
   * The total amount of the comparison invoice uploaded.
   */
  total_dollars: string;
  /**
   * Total Kwh
   *
   * The total kWh of the comparison invoice uploaded.
   */
  total_kwh: string;
  /**
   * Contract End Date
   *
   * The end date of the contract. If None, then the customer is likely on a month-to-month plan.
   */
  contract_end_date?: string | null;
  /**
   * Invoice Date
   *
   * The date of the invoice. If None, then the invoice date is not known.
   */
  invoice_date?: string | null;
  /**
   * Provider Display Name
   *
   * The name of the Retail Electric Provider (REP) from the comparison invoice. If None, then the provider name is not known.
   */
  provider_display_name?: string | null;
  /**
   * Is Average Billing
   *
   * Whether the customer is enrolled in an average or budget billing program. If None, then it is not known.
   */
  is_average_billing?: boolean | null;
  /**
   * Energy Charge Cents Per Kwh
   *
   * The energy charge in cents per kWh parsed from the customer's invoice. This is only the energy component of the rate and does not include TDU delivery charges, base fees, usage credits, taxes, or other surcharges. Not recommended for plan comparison as it is just one component of pricing.
   */
  energy_charge_cents_per_kwh?: string | null;
  /**
   * Plan Name
   *
   * The name of the electricity plan from the customer's invoice. Some invoices do not include a plan name.
   */
  plan_name?: string | null;
  /**
   * Billing Period Start
   *
   * The start date of the billing period on the invoice.
   */
  billing_period_start?: string | null;
  /**
   * Billing Period End
   *
   * The end date of the billing period on the invoice.
   */
  billing_period_end?: string | null;
  /**
   * Avg Cents Per Kwh
   *
   * The average price per kWh as printed on the customer's invoice. This is the literal value from the invoice (e.g., 'the average price you paid for electricity service'), not a calculated value.
   */
  avg_cents_per_kwh?: string | null;
};

/**
 * ComparisonInvoiceSimpleCompareResponse
 */
export type ComparisonInvoiceSimpleCompareResponse = {
  /**
   * Processing Error
   *
   * Error message when processing failed, null otherwise.
   */
  processing_error?: string | null;
  /**
   * The parsed data from the comparison invoice. If the invoice is not parsed successfully, this will be None.
   */
  parsed_invoice_data?: ComparisonInvoiceParsedInvoiceData | null;
  /**
   * The plan comparison data. If the invoice is not parsed successfully, this will be None.
   */
  plan_comparison?: ComparisonInvoiceComparedWithPlan | null;
};

/**
 * AnnualCompareQuery
 */
export type AnnualCompareQuery = {
  /**
   * Plan Group
   *
   * Identifier of the plan to compare against the customer's current plan.
   */
  plan_group?: string | null;
  /**
   * Uuid
   *
   * UUID of a specific comparison invoice to use; the most recent is used when omitted.
   */
  uuid?: string | null;
};

/**
 * AnnualCompareAnnualTotals
 */
export type AnnualCompareAnnualTotals = {
  /**
   * Annual Import Kwh
   *
   * Projected annual energy imported from the grid, in kWh.
   */
  annual_import_kwh: string;
  /**
   * Annual Current Plan Dollars
   *
   * Estimated annual cost on the current plan, in dollars.
   */
  annual_current_plan_dollars: string;
  /**
   * Annual Offered Plan Dollars
   *
   * Estimated annual cost on Light's offered plan, in dollars.
   */
  annual_offered_plan_dollars: string;
};

/**
 * AnnualCompareMonthResponse
 */
export type AnnualCompareMonthResponse = {
  /**
   * Month
   *
   * Month number (1=Jan, 12=Dec).
   */
  month: number;
  /**
   * Import Kwh
   *
   * Projected import kWh for this month.
   */
  import_kwh: string;
  /**
   * Current Plan Dollars
   *
   * Estimated cost on current plan.
   */
  current_plan_dollars: string;
  /**
   * Offered Plan Dollars
   *
   * Estimated cost on Light's plan.
   */
  offered_plan_dollars: string;
};

/**
 * AnnualCompareResponse
 */
export type AnnualCompareResponse = {
  /**
   * Processing Error
   *
   * Error message when processing failed, null otherwise.
   */
  processing_error?: string | null;
  /**
   * Months
   *
   * Monthly breakdown (12 entries, Jan-Dec).
   */
  months?: Array<AnnualCompareMonthResponse>;
  /**
   * Annual totals for kWh and cost on both plans.
   */
  annual_totals?: AnnualCompareAnnualTotals | null;
  /**
   * The parsed data from the comparison invoice.
   */
  parsed_invoice_data?: ComparisonInvoiceParsedInvoiceData | null;
  /**
   * Summary plan comparison (annual totals).
   */
  plan_comparison?: ComparisonInvoiceComparedWithPlan | null;
};

/**
 * ESIIDSearchRequest
 */
export type ESIIDSearchRequest = {
  /**
   * Esi Id
   *
   * ESI ID (meter identifier) to look up.
   */
  esi_id: string;
};

/**
 * IdentityRequest
 */
export type IdentityRequest = {
  /**
   * Ssn Last4
   *
   * Last four digits of the customer's Social Security number.
   */
  ssn_last4?: string | null;
  /**
   * Mothers Maiden Name
   *
   * Customer's mother's maiden name, used for identity verification.
   */
  mothers_maiden_name?: string | null;
  /**
   * Birth City
   *
   * City where the customer was born, used for identity verification.
   */
  birth_city?: string | null;
  /**
   * Birth Date
   *
   * Customer's date of birth.
   */
  birth_date?: string | null;
  /**
   * Drivers License Number
   *
   * Customer's driver's license number.
   */
  drivers_license_number?: string | null;
};

/**
 * CreditCheckRequest
 */
export type CreditCheckRequest = {
  /**
   * Ssn
   *
   * Customer's full Social Security number.
   */
  ssn: string;
  /**
   * Agree To Terms
   *
   * Whether the customer agreed to the credit-check terms.
   */
  agree_to_terms: boolean;
};

/**
 * PlanGroupResponse
 */
export type PlanGroupResponse = {
  /**
   * Uuid
   *
   * Unique identifier of the plan group.
   */
  uuid: string;
  /**
   * Name
   *
   * Display name of the plan group.
   */
  name: string;
  /**
   * Is Default For App
   *
   * Whether this is the app's default plan group.
   */
  is_default_for_app: boolean;
  /**
   * Term Months
   *
   * Contract term length in months.
   */
  term_months: number;
  /**
   * Default Term Month Options
   *
   * The term lengths offered by default for this plan: its `term_months` plus any additional options, deduplicated. Not exhaustive — any term within the plan's range can also be quoted on request.
   */
  default_term_month_options: Array<number>;
  /**
   * Renewable Percent
   *
   * Share of the plan's energy from renewable sources, as a percentage.
   */
  renewable_percent: number;
  /**
   * Early Termination Fee Dollars
   *
   * Fee in dollars for ending the contract early.
   */
  early_termination_fee_dollars: string;
  /**
   * Has Solar Buyback
   *
   * Whether the plan buys back exported solar energy.
   */
  has_solar_buyback?: boolean;
  /**
   * State
   *
   * Current state of the plan group.
   */
  state: string;
  /**
   * Plan Monthly Cost Dollars
   *
   * Fixed monthly base charge in dollars.
   */
  plan_monthly_cost_dollars?: string | null;
  /**
   * Storage Monthly Credit Dollars
   *
   * Monthly bill credit in dollars for enrolled storage.
   */
  storage_monthly_credit_dollars?: string | null;
  /**
   * Solar Preactivation Credit Percent
   *
   * Credit percentage applied before solar activation.
   */
  solar_preactivation_credit_percent?: number;
  /**
   * Renewal Rate Group Uuid
   *
   * UUID of the plan group this one renews into, when set.
   */
  renewal_rate_group_uuid?: string | null;
  /**
   * Device Program Terms
   *
   * Device program terms attached to the plan group.
   */
  device_program_terms?: Array<ProgramTerms>;
  /**
   * Alternative Plans
   *
   * Other plans to offer from this plan's enrollment offer step, or null if there are none.
   */
  alternative_plans?: Array<AlternativePlan> | null;
  /**
   * Has Unlimited Ev Charging
   *
   * Deprecated; always false.
   *
   * @deprecated
   */
  has_unlimited_ev_charging?: boolean;
  /**
   * Ev Monthly Cost Dollars
   *
   * Deprecated; always null.
   *
   * @deprecated
   */
  ev_monthly_cost_dollars?: string | null;
  /**
   * Ev Charge Hour Start
   *
   * Deprecated; always null.
   *
   * @deprecated
   */
  ev_charge_hour_start?: number | null;
  /**
   * Ev Charge Hour End
   *
   * Deprecated; always null.
   *
   * @deprecated
   */
  ev_charge_hour_end?: number | null;
};

/**
 * PlanResponse
 */
export type PlanResponse = {
  /**
   * Term Months
   *
   * Contract term length in months.
   */
  term_months: number;
  /**
   * Default Term Month Options
   *
   * The term lengths offered by default for this plan: its `term_months` plus any additional options, deduplicated. Not exhaustive — any term within the plan's range can also be quoted on request.
   */
  default_term_month_options: Array<number>;
  /**
   * Avg Cents Per Kwh 500
   *
   * Average all-in rate in cents per kWh at 500 kWh of monthly usage.
   */
  avg_cents_per_kwh_500: string;
  /**
   * Avg Cents Per Kwh 1000
   *
   * Average all-in rate in cents per kWh at 1,000 kWh of monthly usage.
   */
  avg_cents_per_kwh_1000: string;
  /**
   * Avg Cents Per Kwh 2000
   *
   * Average all-in rate in cents per kWh at 2,000 kWh of monthly usage.
   */
  avg_cents_per_kwh_2000: string;
  /**
   * Energy Rate Cents Per Kwh
   *
   * Energy charge in cents per kWh.
   */
  energy_rate_cents_per_kwh: string;
  /**
   * Tdu Name
   *
   * Full name of the utility (TDSP) delivering power.
   */
  tdu_name: string;
  /**
   * Tdu Shortname
   *
   * Short name of the utility (TDSP) delivering power.
   */
  tdu_shortname: string;
  /**
   * Delivery Rate Cents Per Kwh
   *
   * Utility (TDSP) delivery charge in cents per kWh.
   */
  delivery_rate_cents_per_kwh: string;
  /**
   * Delivery Base Monthly Dollars
   *
   * Utility (TDSP) fixed monthly delivery charge in dollars.
   */
  delivery_base_monthly_dollars: string;
  /**
   * Plan Monthly Cost Dollars
   *
   * Fixed monthly base charge in dollars.
   */
  plan_monthly_cost_dollars: string;
  /**
   * Early Termination Fee Dollars
   *
   * Fee in dollars for ending the contract early.
   */
  early_termination_fee_dollars: string;
  /**
   * Assumed Unlimited Kwh Usage
   *
   * Monthly kWh usage assumed for an unlimited-usage plan.
   */
  assumed_unlimited_kwh_usage: number;
  /**
   * Storage Monthly Credit Dollars
   *
   * Monthly bill credit in dollars for enrolled storage.
   */
  storage_monthly_credit_dollars: string;
  /**
   * Storage Monthly Credit Per Kwh Dollars
   *
   * Bill credit in dollars per stored kWh.
   */
  storage_monthly_credit_per_kwh_dollars?: string | null;
  /**
   * Assumed Storage Kwh
   *
   * Storage capacity in kWh assumed for the credit calculation.
   */
  assumed_storage_kwh?: string;
  /**
   * Solar Preactivation Credit Percent
   *
   * Credit percentage applied before solar activation.
   */
  solar_preactivation_credit_percent: number;
  /**
   * Export Rate Cents Per Kwh
   *
   * Credit in cents per kWh for energy exported to the grid.
   */
  export_rate_cents_per_kwh: string;
  /**
   * Assumed Export Kwh
   *
   * Monthly kWh assumed to be exported to the grid.
   */
  assumed_export_kwh: number;
  /**
   * Has Solar Buyback
   *
   * Whether the plan buys back exported solar energy.
   */
  has_solar_buyback: boolean;
  /**
   * Renewable Percent
   *
   * Share of the plan's energy from renewable sources, as a percentage.
   */
  renewable_percent?: number;
  /**
   * Device Program Terms
   *
   * Device program terms attached to the plan.
   */
  device_program_terms: Array<ProgramTerms>;
  /**
   * Ev Monthly Cost Dollars
   *
   * Deprecated; always null.
   *
   * @deprecated
   */
  ev_monthly_cost_dollars?: string | null;
  /**
   * Ev Charge Hour Start
   *
   * Deprecated; always null.
   *
   * @deprecated
   */
  ev_charge_hour_start?: number | null;
  /**
   * Ev Charge Hour End
   *
   * Deprecated; always null.
   *
   * @deprecated
   */
  ev_charge_hour_end?: number | null;
  /**
   * Earliest Start Date
   *
   * Earliest date service can start on this plan.
   */
  earliest_start_date: string;
  /**
   * Latest Start Date
   *
   * Latest date service can start on this plan.
   */
  latest_start_date: string;
  /**
   * Uuid
   *
   * Unique identifier of the plan (rate).
   */
  uuid: string;
  /**
   * Group Uuid
   *
   * Unique identifier of the plan group.
   */
  group_uuid: string;
  /**
   * Is Default For App
   *
   * Whether this is the app's default plan.
   */
  is_default_for_app?: boolean | null;
  /**
   * Name
   *
   * Display name of the plan.
   */
  name: string;
  /**
   * Links to the plan's rate documents.
   */
  links: RateLinks;
  /**
   * Plan Links
   *
   * Named links to the plan's rate documents.
   */
  plan_links?: Array<PlanLink> | null;
  /**
   * Usage Profile Uuid
   *
   * UUID of the most recent completed usage profile for this ESI ID (if available within the past 30 days).
   */
  usage_profile_uuid?: string | null;
  /**
   * Presentation content for rendering the plan.
   */
  presentation: PlanPresentation;
  /**
   * Alternative Plans
   *
   * Other plans to offer from this plan's enrollment offer step, or null if there are none.
   */
  alternative_plans?: Array<AlternativePlan> | null;
};

/**
 * PlanRequest
 */
export type PlanRequest = {
  /**
   * Esi Id
   *
   * ESI ID (meter identifier) for the service location.
   */
  esi_id?: string | null;
  /**
   * Postal Code
   *
   * Postal code of the service location.
   */
  postal_code?: string | null;
  /**
   * Utility
   *
   * Identifier of the utility (TDSP) serving the location.
   */
  utility?: string | null;
  /**
   * Plan Group
   *
   * UUID of the plan group to request a rate for.
   */
  plan_group?: string | null;
  /**
   * Term Months
   *
   * Contract term length in months being requested.
   */
  term_months?: number | null;
};

/**
 * AcceptPlanRequest
 */
export type AcceptPlanRequest = {
  /**
   * Plan Uuid
   *
   * UUID of the plan to accept.
   */
  plan_uuid: string;
  /**
   * Esi Id
   *
   * ESI ID (meter identifier) for the service location.
   */
  esi_id: string;
  /**
   * Service Start Date
   *
   * Date service should start.
   */
  service_start_date: string;
  /**
   * Type of service event for the enrollment.
   */
  service_event?: ServiceEvent | null;
  /**
   * Terms Accepted
   *
   * Whether the customer accepted the plan terms.
   */
  terms_accepted: boolean;
  /**
   * Who accepted the plan.
   */
  accepted_by?: AcceptedBy;
};

/**
 * SupportCallbackRequest
 */
export type SupportCallbackRequest = {
  /**
   * Phone Number
   */
  phone_number: string;
  /**
   * Context
   */
  context: string;
};

/**
 * SupportCallbackAvailabilityResponse
 */
export type SupportCallbackAvailabilityResponse = {
  /**
   * Available Until
   */
  available_until?: string | null;
};

/**
 * CustomerEnrollmentOptOutRequest
 */
export type CustomerEnrollmentOptOutRequest = {
  /**
   * Reason
   *
   * Machine-readable reason code from `GET /enroll/opt-out/reasons`, `other`, or a partner-defined code.
   */
  reason?: string | null;
  /**
   * Label
   *
   * Human-readable reason text shown to the customer.
   */
  label?: string | null;
  /**
   * Plan Uuids
   *
   * UUIDs of plans shown to the customer.
   */
  plan_uuids?: Array<string> | null;
  /**
   * Which enrollment flow the customer opted out of. One of `enrollment_switch`, `enrollment_move_in`, or `renewal`.
   */
  flow?: OptOutFilter | null;
  /**
   * Detail
   *
   * Free-text follow-up the customer provided after selecting a reason.
   */
  detail?: string | null;
};

/**
 * AuthorizeVehicleResponse
 */
export type AuthorizeVehicleResponse = {
  /**
   * Url
   */
  url: string;
};

/**
 * AuthorizeVehicleRequest
 */
export type AuthorizeVehicleRequest = {
  /**
   * Redirect Url
   */
  redirect_url: string;
};

/**
 * PagedVehicle
 */
export type PagedVehicle = {
  /**
   * Data
   *
   * The page of results, ordered as the endpoint returns them.
   */
  data: Array<Vehicle>;
  /**
   * Has More
   *
   * Whether more results exist after this page.
   */
  has_more: boolean;
  /**
   * Limit
   *
   * Maximum number of results returned per page.
   */
  limit: number;
  /**
   * Offset
   *
   * Number of results skipped before this page.
   */
  offset: number;
  /**
   * Next Page Url
   *
   * URL of the next page, or null when this is the last page.
   */
  next_page_url?: string | null;
};

/**
 * Vehicle
 */
export type Vehicle = {
  /**
   * Vehicle Id
   */
  vehicle_id: string;
  /**
   * Vin
   */
  vin?: string | null;
  /**
   * Year
   */
  year?: number | null;
  /**
   * Brand
   */
  brand?: string | null;
  /**
   * Model
   */
  model?: string | null;
  /**
   * Enrolled At
   */
  enrolled_at?: string | null;
};

/**
 * LocationPlan
 */
export type LocationPlan = {
  /**
   * Term Months
   *
   * Contract term length in months.
   */
  term_months: number;
  /**
   * Default Term Month Options
   *
   * The term lengths offered by default for this plan: its `term_months` plus any additional options, deduplicated. Not exhaustive — any term within the plan's range can also be quoted on request.
   */
  default_term_month_options: Array<number>;
  /**
   * Avg Cents Per Kwh 500
   *
   * Average all-in rate in cents per kWh at 500 kWh of monthly usage.
   */
  avg_cents_per_kwh_500: string;
  /**
   * Avg Cents Per Kwh 1000
   *
   * Average all-in rate in cents per kWh at 1,000 kWh of monthly usage.
   */
  avg_cents_per_kwh_1000: string;
  /**
   * Avg Cents Per Kwh 2000
   *
   * Average all-in rate in cents per kWh at 2,000 kWh of monthly usage.
   */
  avg_cents_per_kwh_2000: string;
  /**
   * Energy Rate Cents Per Kwh
   *
   * Energy charge in cents per kWh.
   */
  energy_rate_cents_per_kwh: string;
  /**
   * Tdu Name
   *
   * Full name of the utility (TDSP) delivering power.
   */
  tdu_name: string;
  /**
   * Tdu Shortname
   *
   * Short name of the utility (TDSP) delivering power.
   */
  tdu_shortname: string;
  /**
   * Delivery Rate Cents Per Kwh
   *
   * Utility (TDSP) delivery charge in cents per kWh.
   */
  delivery_rate_cents_per_kwh: string;
  /**
   * Delivery Base Monthly Dollars
   *
   * Utility (TDSP) fixed monthly delivery charge in dollars.
   */
  delivery_base_monthly_dollars: string;
  /**
   * Plan Monthly Cost Dollars
   *
   * Fixed monthly base charge in dollars.
   */
  plan_monthly_cost_dollars: string;
  /**
   * Early Termination Fee Dollars
   *
   * Fee in dollars for ending the contract early.
   */
  early_termination_fee_dollars: string;
  /**
   * Assumed Unlimited Kwh Usage
   *
   * Monthly kWh usage assumed for an unlimited-usage plan.
   */
  assumed_unlimited_kwh_usage: number;
  /**
   * Storage Monthly Credit Dollars
   *
   * Monthly bill credit in dollars for enrolled storage.
   */
  storage_monthly_credit_dollars: string;
  /**
   * Storage Monthly Credit Per Kwh Dollars
   *
   * Bill credit in dollars per stored kWh.
   */
  storage_monthly_credit_per_kwh_dollars?: string | null;
  /**
   * Assumed Storage Kwh
   *
   * Storage capacity in kWh assumed for the credit calculation.
   */
  assumed_storage_kwh?: string;
  /**
   * Solar Preactivation Credit Percent
   *
   * Credit percentage applied before solar activation.
   */
  solar_preactivation_credit_percent: number;
  /**
   * Export Rate Cents Per Kwh
   *
   * Credit in cents per kWh for energy exported to the grid.
   */
  export_rate_cents_per_kwh: string;
  /**
   * Assumed Export Kwh
   *
   * Monthly kWh assumed to be exported to the grid.
   */
  assumed_export_kwh: number;
  /**
   * Has Solar Buyback
   *
   * Whether the plan buys back exported solar energy.
   */
  has_solar_buyback: boolean;
  /**
   * Renewable Percent
   *
   * Share of the plan's energy from renewable sources, as a percentage.
   */
  renewable_percent?: number;
  /**
   * Device Program Terms
   *
   * Device program terms attached to the plan.
   */
  device_program_terms: Array<ProgramTerms>;
  /**
   * Ev Monthly Cost Dollars
   *
   * Deprecated; always null.
   *
   * @deprecated
   */
  ev_monthly_cost_dollars?: string | null;
  /**
   * Ev Charge Hour Start
   *
   * Deprecated; always null.
   *
   * @deprecated
   */
  ev_charge_hour_start?: number | null;
  /**
   * Ev Charge Hour End
   *
   * Deprecated; always null.
   *
   * @deprecated
   */
  ev_charge_hour_end?: number | null;
  /**
   * Uuid
   *
   * Unique identifier of the plan (rate).
   */
  uuid: string;
  /**
   * Group Uuid
   *
   * Unique identifier of the plan group.
   */
  group_uuid: string;
  /**
   * Is Default For App
   *
   * Whether this is the app's default plan.
   */
  is_default_for_app?: boolean | null;
  /**
   * The type of plan the consumer is on. `TERM` is a fixed-rate plan for a set number of months. `MONTH_TO_MONTH` is a variable-rate plan with no fixed term. `FALLBACK` is a plan that expired contracts are automatically moved onto.
   */
  plan_type?: PlanType | null;
  /**
   * Contract Start
   *
   * Start date of the contract.
   */
  contract_start: string;
  /**
   * Contract End
   *
   * End date of the contract.
   */
  contract_end: string;
  /**
   * Is Active
   *
   * Whether the contract is currently active.
   */
  is_active: boolean;
  /**
   * Is Queued
   *
   * Whether the contract is queued to start in the future.
   */
  is_queued: boolean;
  /**
   * Name
   *
   * Display name of the plan.
   */
  name: string;
};

/**
 * PagedLocationPlan
 */
export type PagedLocationPlan = {
  /**
   * Data
   *
   * The page of results, ordered as the endpoint returns them.
   */
  data: Array<LocationPlan>;
  /**
   * Has More
   *
   * Whether more results exist after this page.
   */
  has_more: boolean;
  /**
   * Limit
   *
   * Maximum number of results returned per page.
   */
  limit: number;
  /**
   * Offset
   *
   * Number of results skipped before this page.
   */
  offset: number;
  /**
   * Next Page Url
   *
   * URL of the next page, or null when this is the last page.
   */
  next_page_url?: string | null;
};

/**
 * DocumentsResponse
 */
export type DocumentsResponse = {
  /**
   * Uuid
   *
   * Unique identifier of the contract these documents belong to.
   */
  uuid: string;
  /**
   * Plan Name
   *
   * Name of the plan these documents belong to.
   */
  plan_name: string;
  /**
   * Efl
   *
   * URL to the plan's Electricity Facts Label (EFL) PDF.
   */
  efl: string;
  /**
   * Tos
   *
   * URL to the plan's Terms of Service PDF.
   */
  tos: string;
  /**
   * Yrac
   *
   * URL to the plan's Your Rights as a Customer (YRAC) PDF.
   */
  yrac: string;
  /**
   * Contract Start
   *
   * Start date of the contract these documents cover.
   */
  contract_start: string;
  /**
   * Contract End
   *
   * End date of the contract these documents cover.
   */
  contract_end: string;
  /**
   * Is Active
   *
   * Whether this contract is currently active.
   */
  is_active: boolean;
  /**
   * Is Queued
   *
   * Whether this contract is queued to start in the future.
   */
  is_queued: boolean;
};

/**
 * PagedDocumentsResponse
 */
export type PagedDocumentsResponse = {
  /**
   * Data
   *
   * The page of results, ordered as the endpoint returns them.
   */
  data: Array<DocumentsResponse>;
  /**
   * Has More
   *
   * Whether more results exist after this page.
   */
  has_more: boolean;
  /**
   * Limit
   *
   * Maximum number of results returned per page.
   */
  limit: number;
  /**
   * Offset
   *
   * Number of results skipped before this page.
   */
  offset: number;
  /**
   * Next Page Url
   *
   * URL of the next page, or null when this is the last page.
   */
  next_page_url?: string | null;
};

/**
 * SupportInfoResponse
 */
export type SupportInfoResponse = {
  /**
   * Support Phone
   *
   * Support phone number the customer can call, when configured.
   */
  support_phone?: string | null;
  /**
   * Support Email
   *
   * Support email address the customer can contact, when configured.
   */
  support_email?: string | null;
  /**
   * Tdu Name
   *
   * Full name of the utility (TDSP) delivering power to the service location.
   */
  tdu_name: string;
  /**
   * Tdu Shortname
   *
   * Short name of the utility (TDSP) delivering power to the service location.
   */
  tdu_shortname: string;
  /**
   * Tdu Phone
   *
   * Phone number for the utility (TDSP) serving the location.
   */
  tdu_phone: string;
};

/**
 * CancelServiceRequest
 */
export type CancelServiceRequest = {
  /**
   * Final Service Date
   *
   * The final date of service. Must be within 30 days of today.
   */
  final_service_date: string;
  /**
   * Reason
   *
   * Optional informative reason for cancelling service.
   */
  reason?: string | null;
};

/**
 * UsagePeriodResponse
 */
export type UsagePeriodResponse = {
  /**
   * Period Start
   *
   * First date of the usage period.
   */
  period_start: string;
  /**
   * Period End
   *
   * Last date of the usage period.
   */
  period_end: string;
  /**
   * Total Kwh
   *
   * Total energy used during the period, in kWh.
   */
  total_kwh: string;
};

/**
 * MonthlyUsageQuery
 */
export type MonthlyUsageQuery = {
  /**
   * Year
   *
   * Calendar year to return usage for.
   */
  year?: number | null;
};

/**
 * MonthUsageData
 */
export type MonthUsageData = {
  /**
   * Month
   *
   * Calendar month the usage covers.
   */
  month: string;
  /**
   * Consumption
   *
   * Energy consumed in the month, in the response units.
   */
  consumption: string;
  /**
   * Generation
   *
   * Energy generated in the month, in the response units.
   */
  generation: string;
  /**
   * Vehicle Charging
   *
   * Energy used for vehicle charging in the month, in the response units.
   */
  vehicle_charging: string;
  /**
   * Eligible Vehicle Charging
   *
   * Vehicle charging energy eligible for program credit, in the response units.
   */
  eligible_vehicle_charging: string;
};

/**
 * MonthlyUsageDataResponse
 */
export type MonthlyUsageDataResponse = {
  /**
   * Year
   *
   * Year the data covers.
   */
  year: number;
  /**
   * Units
   *
   * Unit of the usage values.
   */
  units: string;
  /**
   * Months
   *
   * Per-month usage for the year.
   */
  months: Array<MonthUsageData>;
  /**
   * Next
   *
   * URL for the next year of monthly usage, when available.
   */
  next?: string | null;
  /**
   * Previous
   *
   * URL for the previous year of monthly usage, when available.
   */
  previous?: string | null;
};

/**
 * DailyUsageQuery
 */
export type DailyUsageQuery = {
  /**
   * Month
   *
   * Calendar month (1-12) to return usage for.
   */
  month?: number | null;
  /**
   * Year
   *
   * Calendar year to return usage for.
   */
  year?: number | null;
};

/**
 * DailyUsageDataResponse
 */
export type DailyUsageDataResponse = {
  /**
   * Month
   *
   * Month number the data covers.
   */
  month: number;
  /**
   * Year
   *
   * Year the data covers.
   */
  year: number;
  /**
   * Units
   *
   * Unit of the usage values.
   */
  units: string;
  /**
   * Days
   *
   * Per-day usage for the month.
   */
  days: Array<DayUsageData>;
  /**
   * Next
   *
   * URL for the next month of daily usage, when available.
   */
  next?: string | null;
  /**
   * Previous
   *
   * URL for the previous month of daily usage, when available.
   */
  previous?: string | null;
};

/**
 * DayUsageData
 */
export type DayUsageData = {
  /**
   * Date
   *
   * Calendar date of the usage.
   */
  date: string;
  /**
   * Consumption
   *
   * Energy consumed on the day, in the response units.
   */
  consumption: string;
  /**
   * Generation
   *
   * Energy generated on the day, in the response units.
   */
  generation: string;
  /**
   * Vehicle Charging
   *
   * Energy used for vehicle charging on the day, in the response units.
   */
  vehicle_charging: string;
  /**
   * Eligible Vehicle Charging
   *
   * Vehicle charging energy eligible for program credit, in the response units.
   */
  eligible_vehicle_charging: string;
};

/**
 * IntervalUsageQuery
 */
export type IntervalUsageQuery = {
  /**
   * Month
   *
   * Calendar month (1-12) to return usage for.
   */
  month?: number | null;
  /**
   * Year
   *
   * Calendar year to return usage for.
   */
  year?: number | null;
};

/**
 * DayIntervalUsageData
 */
export type DayIntervalUsageData = {
  /**
   * Date
   *
   * Calendar date of the readings.
   */
  date: string;
  /**
   * Consumption
   *
   * Per-interval energy consumption for the day, in the response units.
   */
  consumption: Array<string>;
  /**
   * Generation
   *
   * Per-interval energy generation for the day, in the response units.
   */
  generation: Array<string>;
};

/**
 * IntervalUsageDataResponse
 */
export type IntervalUsageDataResponse = {
  /**
   * Month
   *
   * Month number the data covers.
   */
  month: number;
  /**
   * Year
   *
   * Year the data covers.
   */
  year: number;
  /**
   * Units
   *
   * Unit of the usage values.
   */
  units: string;
  /**
   * Interval Minutes
   *
   * Length of each interval, in minutes.
   */
  interval_minutes: number;
  /**
   * Days
   *
   * Per-day interval usage for the month.
   */
  days: Array<DayIntervalUsageData>;
  /**
   * Next
   *
   * URL for the next month of interval usage, when available.
   */
  next?: string | null;
  /**
   * Previous
   *
   * URL for the previous month of interval usage, when available.
   */
  previous?: string | null;
};

/**
 * PlanHighlight
 *
 * A ready-to-render reason-to-renew for a plan. Lower ranking = more emphasis.
 */
export type PlanHighlight = {
  /**
   * Id
   *
   * Stable key for the highlight type (rollover_credits, bill_change, term).
   */
  id: string;
  /**
   * Ranking
   *
   * Display priority among this plan's highlights; lower number = more emphasis.
   */
  ranking: number;
  /**
   * Label
   *
   * Short label for the highlight.
   */
  label?: string | null;
  /**
   * Summary
   *
   * Brief summary of the highlight.
   */
  summary?: string | null;
  /**
   * Description
   *
   * Longer description of the highlight.
   */
  description?: string | null;
  /**
   * Hint
   *
   * Supplementary hint text for the highlight.
   */
  hint?: string | null;
};

/**
 * RenewalPlanResponse
 */
export type RenewalPlanResponse = {
  /**
   * Term Months
   *
   * Contract term length in months.
   */
  term_months: number;
  /**
   * Default Term Month Options
   *
   * The term lengths offered by default for this plan: its `term_months` plus any additional options, deduplicated. Not exhaustive — any term within the plan's range can also be quoted on request.
   */
  default_term_month_options: Array<number>;
  /**
   * Avg Cents Per Kwh 500
   *
   * Average all-in rate in cents per kWh at 500 kWh of monthly usage.
   */
  avg_cents_per_kwh_500: string;
  /**
   * Avg Cents Per Kwh 1000
   *
   * Average all-in rate in cents per kWh at 1,000 kWh of monthly usage.
   */
  avg_cents_per_kwh_1000: string;
  /**
   * Avg Cents Per Kwh 2000
   *
   * Average all-in rate in cents per kWh at 2,000 kWh of monthly usage.
   */
  avg_cents_per_kwh_2000: string;
  /**
   * Energy Rate Cents Per Kwh
   *
   * Energy charge in cents per kWh.
   */
  energy_rate_cents_per_kwh: string;
  /**
   * Tdu Name
   *
   * Full name of the utility (TDSP) delivering power.
   */
  tdu_name: string;
  /**
   * Tdu Shortname
   *
   * Short name of the utility (TDSP) delivering power.
   */
  tdu_shortname: string;
  /**
   * Delivery Rate Cents Per Kwh
   *
   * Utility (TDSP) delivery charge in cents per kWh.
   */
  delivery_rate_cents_per_kwh: string;
  /**
   * Delivery Base Monthly Dollars
   *
   * Utility (TDSP) fixed monthly delivery charge in dollars.
   */
  delivery_base_monthly_dollars: string;
  /**
   * Plan Monthly Cost Dollars
   *
   * Fixed monthly base charge in dollars.
   */
  plan_monthly_cost_dollars: string;
  /**
   * Early Termination Fee Dollars
   *
   * Fee in dollars for ending the contract early.
   */
  early_termination_fee_dollars: string;
  /**
   * Assumed Unlimited Kwh Usage
   *
   * Monthly kWh usage assumed for an unlimited-usage plan.
   */
  assumed_unlimited_kwh_usage: number;
  /**
   * Storage Monthly Credit Dollars
   *
   * Monthly bill credit in dollars for enrolled storage.
   */
  storage_monthly_credit_dollars: string;
  /**
   * Storage Monthly Credit Per Kwh Dollars
   *
   * Bill credit in dollars per stored kWh.
   */
  storage_monthly_credit_per_kwh_dollars?: string | null;
  /**
   * Assumed Storage Kwh
   *
   * Storage capacity in kWh assumed for the credit calculation.
   */
  assumed_storage_kwh?: string;
  /**
   * Solar Preactivation Credit Percent
   *
   * Credit percentage applied before solar activation.
   */
  solar_preactivation_credit_percent: number;
  /**
   * Export Rate Cents Per Kwh
   *
   * Credit in cents per kWh for energy exported to the grid.
   */
  export_rate_cents_per_kwh: string;
  /**
   * Assumed Export Kwh
   *
   * Monthly kWh assumed to be exported to the grid.
   */
  assumed_export_kwh: number;
  /**
   * Has Solar Buyback
   *
   * Whether the plan buys back exported solar energy.
   */
  has_solar_buyback: boolean;
  /**
   * Renewable Percent
   *
   * Share of the plan's energy from renewable sources, as a percentage.
   */
  renewable_percent?: number;
  /**
   * Device Program Terms
   *
   * Device program terms attached to the plan.
   */
  device_program_terms: Array<ProgramTerms>;
  /**
   * Ev Monthly Cost Dollars
   *
   * Deprecated; always null.
   *
   * @deprecated
   */
  ev_monthly_cost_dollars?: string | null;
  /**
   * Ev Charge Hour Start
   *
   * Deprecated; always null.
   *
   * @deprecated
   */
  ev_charge_hour_start?: number | null;
  /**
   * Ev Charge Hour End
   *
   * Deprecated; always null.
   *
   * @deprecated
   */
  ev_charge_hour_end?: number | null;
  /**
   * Earliest Start Date
   *
   * Earliest date service can start on this plan.
   */
  earliest_start_date: string;
  /**
   * Latest Start Date
   *
   * Latest date service can start on this plan.
   */
  latest_start_date: string;
  /**
   * Uuid
   *
   * Unique identifier of the plan (rate).
   */
  uuid: string;
  /**
   * Group Uuid
   *
   * Unique identifier of the plan group.
   */
  group_uuid: string;
  /**
   * Is Default For App
   *
   * Whether this is the app's default plan.
   */
  is_default_for_app?: boolean | null;
  /**
   * Name
   *
   * Display name of the plan.
   */
  name: string;
  /**
   * Links to the plan's rate documents.
   */
  links: RateLinks;
  /**
   * Plan Links
   *
   * Named links to the plan's rate documents.
   */
  plan_links?: Array<PlanLink> | null;
  /**
   * Usage Profile Uuid
   *
   * UUID of the most recent completed usage profile for this ESI ID (if available within the past 30 days).
   */
  usage_profile_uuid?: string | null;
  /**
   * Presentation content for rendering the plan.
   */
  presentation: PlanPresentation;
  /**
   * Alternative Plans
   *
   * Other plans to offer from this plan's enrollment offer step, or null if there are none.
   */
  alternative_plans?: Array<AlternativePlan> | null;
  /**
   * Renewal Highlights
   *
   * Ready-to-render reasons to renew for this plan, ordered by ranking (lower number = more emphasis). Empty when no highlight applies.
   */
  renewal_highlights: Array<PlanHighlight>;
};

/**
 * AcceptRenewalPlanRequest
 */
export type AcceptRenewalPlanRequest = {
  /**
   * Plan Uuid
   *
   * UUID of the renewal plan to accept.
   */
  plan_uuid: string;
  /**
   * Service Start Date
   *
   * Date the renewal should start.
   */
  service_start_date: string;
  /**
   * Terms Accepted
   *
   * Whether the customer accepted the renewal terms.
   */
  terms_accepted: boolean;
};

/**
 * AcceptSwitchPlanRequest
 */
export type AcceptSwitchPlanRequest = {
  /**
   * Service Start Date
   */
  service_start_date: string;
  /**
   * Terms Accepted
   */
  terms_accepted: boolean;
};

/**
 * PagedProgramAgreement
 */
export type PagedProgramAgreement = {
  /**
   * Data
   *
   * The page of results, ordered as the endpoint returns them.
   */
  data: Array<ProgramAgreement>;
  /**
   * Has More
   *
   * Whether more results exist after this page.
   */
  has_more: boolean;
  /**
   * Limit
   *
   * Maximum number of results returned per page.
   */
  limit: number;
  /**
   * Offset
   *
   * Number of results skipped before this page.
   */
  offset: number;
  /**
   * Next Page Url
   *
   * URL of the next page, or null when this is the last page.
   */
  next_page_url?: string | null;
};

/**
 * ProgramAgreement
 */
export type ProgramAgreement = {
  terms: ProgramTerms;
  program: Program;
  /**
   * Agreed At
   */
  agreed_at: string;
};

/**
 * AccountDeviceLinkRequest
 */
export type AccountDeviceLinkRequest = {
  /**
   * Device Type
   */
  device_type?: "BATTERY" | "EVSE" | "VEHICLE" | null;
  /**
   * Manufacturer
   */
  manufacturer?:
    | "tesla"
    | "solaredge"
    | "enphase"
    | "sigenergy"
    | "franklin"
    | "ecoflow"
    | "duracell"
    | "foxess"
    | "sonnen"
    | "generac"
    | null;
};

/**
 * Device
 */
export type Device = {
  /**
   * Uuid
   *
   * The device's unique identifier.
   */
  uuid: string;
  /**
   * Device Type
   *
   * The type of device.
   */
  device_type: "BATTERY" | "EVSE" | "VEHICLE";
  /**
   * The device's partner-facing status.
   */
  status: PartnerDeviceStatus;
  /**
   * The battery's state and details, present when the device is a battery.
   */
  battery_details?: BatteryState | null;
  /**
   * The EVSE's details, present when the device is an EVSE.
   */
  charger_details?: EVSEDetails | null;
  /**
   * The vehicle's details, present when the device is a vehicle.
   */
  vehicle_details?: VehicleDetails | null;
  /**
   * Device program enrollment and compliance status.
   */
  program?: DeviceProgram | null;
};

/**
 * DeviceEnrollment
 */
export type DeviceEnrollment = {
  /**
   * Enrolled On
   */
  enrolled_on: string;
  /**
   * Device Uuid
   */
  device_uuid: string;
  /**
   * Program Uuid
   */
  program_uuid: string;
  /**
   * Agreed At
   */
  agreed_at: string;
  /**
   * Terms Uuid
   */
  terms_uuid: string;
};

/**
 * EnrollDevice
 */
export type EnrollDevice = {
  /**
   * Program Uuid
   */
  program_uuid: string;
};

/**
 * PagedDevice
 */
export type PagedDevice = {
  /**
   * Data
   *
   * The page of results, ordered as the endpoint returns them.
   */
  data: Array<Device>;
  /**
   * Has More
   *
   * Whether more results exist after this page.
   */
  has_more: boolean;
  /**
   * Limit
   *
   * Maximum number of results returned per page.
   */
  limit: number;
  /**
   * Offset
   *
   * Number of results skipped before this page.
   */
  offset: number;
  /**
   * Next Page Url
   *
   * URL of the next page, or null when this is the last page.
   */
  next_page_url?: string | null;
};

/**
 * SimulateExpiringRequest
 */
export type SimulateExpiringRequest = {
  /**
   * Days Before Expiration
   */
  days_before_expiration: number;
};

export type appsApiAppAccountRecordEnrollmentFunnelEventData = {
  /**
   * Payload
   */
  body:
    | ({
        event: "plans_viewed";
      } & PlansViewedEventRequest)
    | ({
        event: "enrollment_opt_out";
      } & EnrollmentOptOutEventRequest);
  path?: never;
  query?: never;
  url: "/v1/app/accounts/events";
};

export type appsApiAppAccountRecordEnrollmentFunnelEventResponses = {
  /**
   * Created
   */
  201: EnrollmentEventResponse;
};

export type appsApiAppAccountRecordEnrollmentFunnelEventResponse =
  appsApiAppAccountRecordEnrollmentFunnelEventResponses[keyof appsApiAppAccountRecordEnrollmentFunnelEventResponses];

export type appsApiAppAccountCreateAccountTokenData = {
  body?: never;
  path: {
    /**
     * Account Uuid
     *
     * UUID of the account.
     */
    account_uuid: string;
  };
  query?: never;
  url: "/v1/app/accounts/{account_uuid}/token";
};

export type appsApiAppAccountCreateAccountTokenResponses = {
  /**
   * OK
   */
  200: AccountTokenResponse;
};

export type appsApiAppAccountCreateAccountTokenResponse =
  appsApiAppAccountCreateAccountTokenResponses[keyof appsApiAppAccountCreateAccountTokenResponses];

export type appsApiAppAccountCreateFlowLoginData = {
  body: CreateFlowLoginRequest;
  path: {
    /**
     * Account Uuid
     *
     * UUID of the account.
     */
    account_uuid: string;
  };
  query?: never;
  url: "/v1/app/accounts/{account_uuid}/flow-login";
};

export type appsApiAppAccountCreateFlowLoginResponses = {
  /**
   * OK
   */
  200: FlowLoginResponse;
};

export type appsApiAppAccountCreateFlowLoginResponse =
  appsApiAppAccountCreateFlowLoginResponses[keyof appsApiAppAccountCreateFlowLoginResponses];

export type appsApiAppAccountCreateAppLoginData = {
  body: CreateAppLoginRequest;
  path: {
    /**
     * Account Uuid
     *
     * UUID of the account.
     */
    account_uuid: string;
  };
  query?: never;
  url: "/v1/app/accounts/{account_uuid}/app-login";
};

export type appsApiAppAccountCreateAppLoginResponses = {
  /**
   * OK
   */
  200: AppLoginResponse;
};

export type appsApiAppAccountCreateAppLoginResponse =
  appsApiAppAccountCreateAppLoginResponses[keyof appsApiAppAccountCreateAppLoginResponses];

export type appsApiAppAccountListAccountsData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Filter
     *
     * Account lifecycle states to filter by.
     */
    filter?: Array<string> | null;
    /**
     * Search
     *
     * Free-text search over account name and email.
     */
    search?: string | null;
    /**
     * Email
     *
     * Filters to accounts with this email address.
     */
    email?: string | null;
    /**
     * Plan Group Uuid
     *
     * Filters to accounts on these plans.
     */
    plan_group_uuid?: Array<string> | null;
    /**
     * External Id
     *
     * Filters to the account with this partner-assigned external ID.
     */
    external_id?: string | null;
    /**
     * Filters to accounts on plans of this type.
     */
    plan_type?: PlanType | null;
    /**
     * Include Fallback Plans
     *
     * Whether `plan_group_uuid` also matches accounts on the plan's fallback plan.
     */
    include_fallback_plans?: boolean;
    /**
     * Device Type
     *
     * Narrows `device_status` and `missing_program_device` to these device types. On its own it narrows both, so it returns accounts whose device is of these types as well as accounts on a program expecting one with nothing enrolled.
     */
    device_type?: Array<DeviceType> | null;
    /**
     * Device Status
     *
     * Only accounts whose device is in one of these statuses. `inactive` matches a device that was unenrolled or decommissioned, which is narrower than `missing_program_device` — that also covers accounts which never connected one.
     */
    device_status?: Array<PartnerDeviceStatus> | null;
    /**
     * Missing Program Device
     *
     * Only accounts on a device program with no device currently enrolled. Combines with `device_status` as a union, and with `device_type` by the types the program expects.
     */
    missing_program_device?: boolean;
    /**
     * Expiring In Days
     *
     * Only accounts whose active term contract ends within this many days and whose consumer has not accepted a renewal offer yet. Contracts already ended, or cut short because the location is leaving service, are excluded. Also orders the list by soonest end date first.
     */
    expiring_in_days?: number | null;
    /**
     * Limit
     *
     * Maximum number of results to return per page.
     */
    limit?: number;
    /**
     * Offset
     *
     * Number of results to skip before the returned page.
     */
    offset?: number;
  };
  url: "/v1/app/accounts";
};

export type appsApiAppAccountListAccountsResponses = {
  /**
   * OK
   */
  200: PagedAppAccountResponse;
};

export type appsApiAppAccountListAccountsResponse =
  appsApiAppAccountListAccountsResponses[keyof appsApiAppAccountListAccountsResponses];

export type appsApiAppAccountCreateAccountData = {
  body: CreateAccountRequest;
  path?: never;
  query?: never;
  url: "/v1/app/accounts";
};

export type appsApiAppAccountCreateAccountResponses = {
  /**
   * OK
   */
  200: AccountCreateResponse;
  /**
   * Created
   */
  201: AccountCreateResponse;
};

export type appsApiAppAccountCreateAccountResponse =
  appsApiAppAccountCreateAccountResponses[keyof appsApiAppAccountCreateAccountResponses];

export type appsApiAppAccountGetAccountsCountData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Filter
     *
     * Account lifecycle states to filter by.
     */
    filter?: Array<string> | null;
    /**
     * Search
     *
     * Free-text search over account name and email.
     */
    search?: string | null;
    /**
     * Email
     *
     * Filters to accounts with this email address.
     */
    email?: string | null;
    /**
     * Plan Group Uuid
     *
     * Filters to accounts on these plans.
     */
    plan_group_uuid?: Array<string> | null;
    /**
     * External Id
     *
     * Filters to the account with this partner-assigned external ID.
     */
    external_id?: string | null;
    /**
     * Filters to accounts on plans of this type.
     */
    plan_type?: PlanType | null;
    /**
     * Include Fallback Plans
     *
     * Whether `plan_group_uuid` also matches accounts on the plan's fallback plan.
     */
    include_fallback_plans?: boolean;
    /**
     * Device Type
     *
     * Narrows `device_status` and `missing_program_device` to these device types. On its own it narrows both, so it returns accounts whose device is of these types as well as accounts on a program expecting one with nothing enrolled.
     */
    device_type?: Array<DeviceType> | null;
    /**
     * Device Status
     *
     * Only accounts whose device is in one of these statuses. `inactive` matches a device that was unenrolled or decommissioned, which is narrower than `missing_program_device` — that also covers accounts which never connected one.
     */
    device_status?: Array<PartnerDeviceStatus> | null;
    /**
     * Missing Program Device
     *
     * Only accounts on a device program with no device currently enrolled. Combines with `device_status` as a union, and with `device_type` by the types the program expects.
     */
    missing_program_device?: boolean;
    /**
     * Expiring In Days
     *
     * Only accounts whose active term contract ends within this many days and whose consumer has not accepted a renewal offer yet. Contracts already ended, or cut short because the location is leaving service, are excluded. Also orders the list by soonest end date first.
     */
    expiring_in_days?: number | null;
  };
  url: "/v1/app/accounts/count";
};

export type appsApiAppAccountGetAccountsCountResponses = {
  /**
   * OK
   */
  200: AccountsCountResponse;
};

export type appsApiAppAccountGetAccountsCountResponse =
  appsApiAppAccountGetAccountsCountResponses[keyof appsApiAppAccountGetAccountsCountResponses];

export type appsApiAppAccountGetAccountData = {
  body?: never;
  path: {
    /**
     * Account Uuid
     *
     * UUID of the account.
     */
    account_uuid: string;
  };
  query?: never;
  url: "/v1/app/accounts/{account_uuid}";
};

export type appsApiAppAccountGetAccountResponses = {
  /**
   * OK
   */
  200: AppAccountResponse;
};

export type appsApiAppAccountGetAccountResponse =
  appsApiAppAccountGetAccountResponses[keyof appsApiAppAccountGetAccountResponses];

export type appsApiAppAccountUpdateAccountData = {
  body: UpdateAccountRequest;
  path: {
    /**
     * Account Uuid
     *
     * UUID of the account.
     */
    account_uuid: string;
  };
  query?: never;
  url: "/v1/app/accounts/{account_uuid}";
};

export type appsApiAppAccountUpdateAccountResponses = {
  /**
   * OK
   */
  200: AppAccountResponse;
};

export type appsApiAppAccountUpdateAccountResponse =
  appsApiAppAccountUpdateAccountResponses[keyof appsApiAppAccountUpdateAccountResponses];

export type appsApiAppAccountGetEnrollmentEvidenceData = {
  body?: never;
  path: {
    /**
     * Account Uuid
     *
     * UUID of the account.
     */
    account_uuid: string;
  };
  query?: never;
  url: "/v1/app/accounts/{account_uuid}/enrollment-evidence";
};

export type appsApiAppAccountGetEnrollmentEvidenceResponses = {
  /**
   * OK
   */
  200: EnrollmentEvidenceResponse;
};

export type appsApiAppAccountGetEnrollmentEvidenceResponse =
  appsApiAppAccountGetEnrollmentEvidenceResponses[keyof appsApiAppAccountGetEnrollmentEvidenceResponses];

export type appsApiAppAccountCancelEnrollmentData = {
  body: CancelEnrollmentRequest;
  path: {
    /**
     * Account Uuid
     *
     * UUID of the account.
     */
    account_uuid: string;
  };
  query?: never;
  url: "/v1/app/accounts/{account_uuid}/cancel-enrollment";
};

export type appsApiAppAccountCancelEnrollmentResponses = {
  /**
   * OK
   */
  200: AppAccountResponse;
};

export type appsApiAppAccountCancelEnrollmentResponse =
  appsApiAppAccountCancelEnrollmentResponses[keyof appsApiAppAccountCancelEnrollmentResponses];

export type appsApiAppAccountRecordClientAgentData = {
  body: RecordClientAgentRequest;
  path: {
    /**
     * Account Uuid
     *
     * UUID of the account.
     */
    account_uuid: string;
  };
  query?: never;
  url: "/v1/app/accounts/{account_uuid}/record-client-agent";
};

export type appsApiAppAccountRecordClientAgentResponses = {
  /**
   * OK
   */
  200: AppAccountResponse;
};

export type appsApiAppAccountRecordClientAgentResponse =
  appsApiAppAccountRecordClientAgentResponses[keyof appsApiAppAccountRecordClientAgentResponses];

export type appsApiProgramsAppAccountListProgramAgreementsData = {
  body?: never;
  path: {
    /**
     * Account Uuid
     */
    account_uuid: string;
  };
  query?: {
    /**
     * Limit
     *
     * Maximum number of results to return per page.
     */
    limit?: number;
    /**
     * Offset
     *
     * Number of results to skip before the returned page.
     */
    offset?: number;
  };
  url: "/v1/app/accounts/{account_uuid}/programs/agreements";
};

export type appsApiProgramsAppAccountListProgramAgreementsResponses = {
  /**
   * OK
   */
  200: PagedAppAccountProgramAgreement;
};

export type appsApiProgramsAppAccountListProgramAgreementsResponse =
  appsApiProgramsAppAccountListProgramAgreementsResponses[keyof appsApiProgramsAppAccountListProgramAgreementsResponses];

export type appsApiDevicesAppAccountDevicesCreateBatteryData = {
  body: CreateBatteryRequest;
  path: {
    /**
     * Account Uuid
     *
     * UUID of the account.
     */
    account_uuid: string;
  };
  query?: never;
  url: "/v1/app/accounts/{account_uuid}/devices/battery";
};

export type appsApiDevicesAppAccountDevicesCreateBatteryResponses = {
  /**
   * OK
   */
  200: AppAccountDevice;
};

export type appsApiDevicesAppAccountDevicesCreateBatteryResponse =
  appsApiDevicesAppAccountDevicesCreateBatteryResponses[keyof appsApiDevicesAppAccountDevicesCreateBatteryResponses];

export type appsApiDevicesAppAccountDevicesCreateEvseData = {
  body: CreateEVSERequest;
  path: {
    /**
     * Account Uuid
     *
     * UUID of the account.
     */
    account_uuid: string;
  };
  query?: never;
  url: "/v1/app/accounts/{account_uuid}/devices/charger";
};

export type appsApiDevicesAppAccountDevicesCreateEvseResponses = {
  /**
   * OK
   */
  200: AppAccountDevice;
};

export type appsApiDevicesAppAccountDevicesCreateEvseResponse =
  appsApiDevicesAppAccountDevicesCreateEvseResponses[keyof appsApiDevicesAppAccountDevicesCreateEvseResponses];

export type appsApiDevicesAppAccountDevicesCreateVehicleData = {
  body: CreateVehicleRequest;
  path: {
    /**
     * Account Uuid
     *
     * UUID of the account.
     */
    account_uuid: string;
  };
  query?: never;
  url: "/v1/app/accounts/{account_uuid}/devices/vehicle";
};

export type appsApiDevicesAppAccountDevicesCreateVehicleResponses = {
  /**
   * OK
   */
  200: AppAccountDevice;
};

export type appsApiDevicesAppAccountDevicesCreateVehicleResponse =
  appsApiDevicesAppAccountDevicesCreateVehicleResponses[keyof appsApiDevicesAppAccountDevicesCreateVehicleResponses];

export type appsApiDevicesAppAccountDevicesListAccountDevicesData = {
  body?: never;
  path: {
    /**
     * Account Uuid
     */
    account_uuid: string;
  };
  query?: {
    /**
     * Limit
     *
     * Maximum number of results to return per page.
     */
    limit?: number;
    /**
     * Offset
     *
     * Number of results to skip before the returned page.
     */
    offset?: number;
  };
  url: "/v1/app/accounts/{account_uuid}/devices";
};

export type appsApiDevicesAppAccountDevicesListAccountDevicesResponses = {
  /**
   * OK
   */
  200: PagedAppAccountDevice;
};

export type appsApiDevicesAppAccountDevicesListAccountDevicesResponse =
  appsApiDevicesAppAccountDevicesListAccountDevicesResponses[keyof appsApiDevicesAppAccountDevicesListAccountDevicesResponses];

export type appsApiDevicesAppAccountDevicesLinkAppAccountDeviceData = {
  body: DeviceLinkRequest;
  path: {
    /**
     * Account Uuid
     */
    account_uuid: string;
  };
  query?: never;
  url: "/v1/app/accounts/{account_uuid}/devices/link";
};

export type appsApiDevicesAppAccountDevicesLinkAppAccountDeviceResponses = {
  /**
   * OK
   */
  200: DeviceLinkResponse;
};

export type appsApiDevicesAppAccountDevicesLinkAppAccountDeviceResponse =
  appsApiDevicesAppAccountDevicesLinkAppAccountDeviceResponses[keyof appsApiDevicesAppAccountDevicesLinkAppAccountDeviceResponses];

export type appsApiDevicesAppAccountDevicesDiscoverLinkedDevicesData = {
  body?: never;
  path: {
    /**
     * Account Uuid
     */
    account_uuid: string;
  };
  query?: never;
  url: "/v1/app/accounts/{account_uuid}/devices/discover";
};

export type appsApiDevicesAppAccountDevicesDiscoverLinkedDevicesResponses = {
  /**
   * No Content
   */
  204: void;
};

export type appsApiDevicesAppAccountDevicesDiscoverLinkedDevicesResponse =
  appsApiDevicesAppAccountDevicesDiscoverLinkedDevicesResponses[keyof appsApiDevicesAppAccountDevicesDiscoverLinkedDevicesResponses];

export type appsApiDevicesAppAccountDevicesSyncDeviceData = {
  body?: never;
  path: {
    /**
     * Account Uuid
     */
    account_uuid: string;
    /**
     * Device Uuid
     */
    device_uuid: string;
  };
  query?: never;
  url: "/v1/app/accounts/{account_uuid}/devices/{device_uuid}/sync";
};

export type appsApiDevicesAppAccountDevicesSyncDeviceResponses = {
  /**
   * OK
   */
  200: AppAccountDevice;
};

export type appsApiDevicesAppAccountDevicesSyncDeviceResponse =
  appsApiDevicesAppAccountDevicesSyncDeviceResponses[keyof appsApiDevicesAppAccountDevicesSyncDeviceResponses];

export type appsApiDevicesTelemetryAddDeviceTelemetryData = {
  body: ChargingStatus;
  path: {
    /**
     * Account Uuid
     */
    account_uuid: string;
    /**
     * Device Uuid
     */
    device_uuid: string;
  };
  query?: never;
  url: "/v1/app/accounts/{account_uuid}/devices/vehicle/{device_uuid}/telemetry";
};

export type appsApiDevicesTelemetryAddDeviceTelemetryResponses = {
  /**
   * No Content
   */
  204: void;
};

export type appsApiDevicesTelemetryAddDeviceTelemetryResponse =
  appsApiDevicesTelemetryAddDeviceTelemetryResponses[keyof appsApiDevicesTelemetryAddDeviceTelemetryResponses];

export type appsApiAppEnrollAddressSearchData = {
  body: AddressSearchRequest;
  path?: never;
  query?: never;
  url: "/v1/app/accounts/enroll/address-search";
};

export type appsApiAppEnrollAddressSearchResponses = {
  /**
   * Response
   *
   * OK
   */
  200: Array<AddressSearchResponse>;
};

export type appsApiAppEnrollAddressSearchResponse =
  appsApiAppEnrollAddressSearchResponses[keyof appsApiAppEnrollAddressSearchResponses];

export type appsApiAppEnrollAddressSearchComponentsData = {
  body: PreliminaryAddress;
  path?: never;
  query?: never;
  url: "/v1/app/accounts/enroll/full-address-search";
};

export type appsApiAppEnrollAddressSearchComponentsResponses = {
  /**
   * Response
   *
   * OK
   */
  200: Array<AddressSearchResponse>;
};

export type appsApiAppEnrollAddressSearchComponentsResponse =
  appsApiAppEnrollAddressSearchComponentsResponses[keyof appsApiAppEnrollAddressSearchComponentsResponses];

export type appsApiAppEnrollCheckEligibilityData = {
  body: EligibilityRequest;
  path?: never;
  query?: never;
  url: "/v1/app/accounts/enroll/eligibility";
};

export type appsApiAppEnrollCheckEligibilityResponses = {
  /**
   * OK
   */
  200: EligibilityResponse;
};

export type appsApiAppEnrollCheckEligibilityResponse =
  appsApiAppEnrollCheckEligibilityResponses[keyof appsApiAppEnrollCheckEligibilityResponses];

export type appsApiAppEnrollGetBenchmarksData = {
  body: BenchmarksRequest;
  path?: never;
  query?: never;
  url: "/v1/app/accounts/enroll/benchmarks";
};

export type appsApiAppEnrollGetBenchmarksResponses = {
  /**
   * OK
   */
  200: BenchmarksResponse;
};

export type appsApiAppEnrollGetBenchmarksResponse =
  appsApiAppEnrollGetBenchmarksResponses[keyof appsApiAppEnrollGetBenchmarksResponses];

export type appsApiAppEnrollGetComparableData = {
  body: BenchmarksRequest;
  path?: never;
  query?: never;
  url: "/v1/app/accounts/enroll/comparable";
};

export type appsApiAppEnrollGetComparableResponses = {
  /**
   * Response
   *
   * OK
   */
  200: Array<ComparablePlan>;
};

export type appsApiAppEnrollGetComparableResponse =
  appsApiAppEnrollGetComparableResponses[keyof appsApiAppEnrollGetComparableResponses];

export type appsApiAppEnrollRequestPlansData = {
  body: CustomPlanRequest;
  path?: never;
  query?: never;
  url: "/v1/app/accounts/enroll/plans/request";
};

export type appsApiAppEnrollRequestPlansResponses = {
  /**
   * Response
   *
   * OK
   */
  200: Array<CustomPlanResponse>;
};

export type appsApiAppEnrollRequestPlansResponse =
  appsApiAppEnrollRequestPlansResponses[keyof appsApiAppEnrollRequestPlansResponses];

export type appsApiAppEnrollListEnrollmentOptOutReasonsData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Survey filter. Returns a curated list of reasons for that flow. One of `enrollment_switch`, `enrollment_move_in`, or `renewal`. Omit to return every defined reason.
     */
    filter?: OptOutFilter | null;
  };
  url: "/v1/app/accounts/enroll/opt-out/reasons";
};

export type appsApiAppEnrollListEnrollmentOptOutReasonsResponses = {
  /**
   * Response
   *
   * OK
   */
  200: Array<OptOutReasonOption>;
};

export type appsApiAppEnrollListEnrollmentOptOutReasonsResponse =
  appsApiAppEnrollListEnrollmentOptOutReasonsResponses[keyof appsApiAppEnrollListEnrollmentOptOutReasonsResponses];

export type appsApiAppEnrollGetDisallowedDatesData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/v1/app/accounts/enroll/disallowed-dates";
};

export type appsApiAppEnrollGetDisallowedDatesResponses = {
  /**
   * OK
   */
  200: DisallowedDatesResponse;
};

export type appsApiAppEnrollGetDisallowedDatesResponse =
  appsApiAppEnrollGetDisallowedDatesResponses[keyof appsApiAppEnrollGetDisallowedDatesResponses];

export type appsApiAppContractsListContractsData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * State
     *
     * Filters to contracts in this state.
     */
    state?: "active" | "ended" | "expired" | "queued" | null;
    /**
     * Account Uuid
     *
     * Filters to contracts for this account.
     */
    account_uuid?: string | null;
    /**
     * Location Uuid
     *
     * Filters to contracts for this service location.
     */
    location_uuid?: string | null;
    /**
     * Plan Group Uuid
     *
     * Filters to contracts on this plan.
     */
    plan_group_uuid?: string | null;
    /**
     * Limit
     *
     * Maximum number of results to return per page.
     */
    limit?: number;
    /**
     * Offset
     *
     * Number of results to skip before the returned page.
     */
    offset?: number;
  };
  url: "/v1/app/contracts";
};

export type appsApiAppContractsListContractsResponses = {
  /**
   * OK
   */
  200: PagedContractResponse;
};

export type appsApiAppContractsListContractsResponse =
  appsApiAppContractsListContractsResponses[keyof appsApiAppContractsListContractsResponses];

export type appsApiProgramsAppAppRequestData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/v1/app/programs/request";
};

export type appsApiProgramsAppAppRequestResponses = {
  /**
   * Response
   *
   * OK
   */
  200: Array<Program>;
};

export type appsApiProgramsAppAppRequestResponse =
  appsApiProgramsAppAppRequestResponses[keyof appsApiProgramsAppAppRequestResponses];

export type appsApiProgramsAppProgramsListAppProgramAgreementsData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Account Ids
     */
    account_ids?: Array<string> | null;
    /**
     * Limit
     *
     * Maximum number of results to return per page.
     */
    limit?: number;
    /**
     * Offset
     *
     * Number of results to skip before the returned page.
     */
    offset?: number;
  };
  url: "/v1/app/programs/agreements";
};

export type appsApiProgramsAppProgramsListAppProgramAgreementsResponses = {
  /**
   * OK
   */
  200: PagedAppAccountProgramAgreement;
};

export type appsApiProgramsAppProgramsListAppProgramAgreementsResponse =
  appsApiProgramsAppProgramsListAppProgramAgreementsResponses[keyof appsApiProgramsAppProgramsListAppProgramAgreementsResponses];

export type appsApiDevicesAppDevicesListManufacturersData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Device Type
     */
    device_type?: "BATTERY" | "EVSE" | "VEHICLE" | null;
  };
  url: "/v1/app/devices/manufacturers";
};

export type appsApiDevicesAppDevicesListManufacturersResponses = {
  /**
   * Response
   *
   * OK
   */
  200: Array<DeviceManufacturer>;
};

export type appsApiDevicesAppDevicesListManufacturersResponse =
  appsApiDevicesAppDevicesListManufacturersResponses[keyof appsApiDevicesAppDevicesListManufacturersResponses];

export type appsApiDevicesAppDevicesGetDeviceData = {
  body?: never;
  path: {
    /**
     * Device Uuid
     */
    device_uuid: string;
  };
  query?: never;
  url: "/v1/app/devices/{device_uuid}";
};

export type appsApiDevicesAppDevicesGetDeviceResponses = {
  /**
   * OK
   */
  200: AppAccountDevice;
};

export type appsApiDevicesAppDevicesGetDeviceResponse =
  appsApiDevicesAppDevicesGetDeviceResponses[keyof appsApiDevicesAppDevicesGetDeviceResponses];

export type appsApiDevicesAppDevicesListAppDevicesData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Account Ids
     */
    account_ids?: Array<string> | null;
    /**
     * Limit
     *
     * Maximum number of results to return per page.
     */
    limit?: number;
    /**
     * Offset
     *
     * Number of results to skip before the returned page.
     */
    offset?: number;
  };
  url: "/v1/app/devices";
};

export type appsApiDevicesAppDevicesListAppDevicesResponses = {
  /**
   * OK
   */
  200: PagedAppAccountDevice;
};

export type appsApiDevicesAppDevicesListAppDevicesResponse =
  appsApiDevicesAppDevicesListAppDevicesResponses[keyof appsApiDevicesAppDevicesListAppDevicesResponses];

export type appsApiUsageProfilesApiUploadUsageProfileData = {
  body: UsageProfileUploadRequest;
  path?: never;
  query?: never;
  url: "/v1/app/usage-profiles/upload";
};

export type appsApiUsageProfilesApiUploadUsageProfileResponses = {
  /**
   * Accepted
   */
  202: UsageProfile;
};

export type appsApiUsageProfilesApiUploadUsageProfileResponse =
  appsApiUsageProfilesApiUploadUsageProfileResponses[keyof appsApiUsageProfilesApiUploadUsageProfileResponses];

export type appsApiUsageProfilesApiFetchUsageProfileData = {
  body: UsageProfileFetchRequest;
  path?: never;
  query?: never;
  url: "/v1/app/usage-profiles/fetch";
};

export type appsApiUsageProfilesApiFetchUsageProfileResponses = {
  /**
   * Accepted
   */
  202: UsageProfile;
};

export type appsApiUsageProfilesApiFetchUsageProfileResponse =
  appsApiUsageProfilesApiFetchUsageProfileResponses[keyof appsApiUsageProfilesApiFetchUsageProfileResponses];

export type appsApiUsageProfilesApiGetUsageProfileData = {
  body?: never;
  path: {
    /**
     * Usage Profile Uuid
     *
     * UUID of the usage profile.
     */
    usage_profile_uuid: string;
  };
  query?: never;
  url: "/v1/app/usage-profiles/{usage_profile_uuid}";
};

export type appsApiUsageProfilesApiGetUsageProfileResponses = {
  /**
   * OK
   */
  200: UsageProfile;
};

export type appsApiUsageProfilesApiGetUsageProfileResponse =
  appsApiUsageProfilesApiGetUsageProfileResponses[keyof appsApiUsageProfilesApiGetUsageProfileResponses];

export type appsApiAccountGetAccountData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/v1/account";
};

export type appsApiAccountGetAccountResponses = {
  /**
   * OK
   */
  200: AppAccountResponse;
};

export type appsApiAccountGetAccountResponse =
  appsApiAccountGetAccountResponses[keyof appsApiAccountGetAccountResponses];

export type appsApiAccountUpdateAccountData = {
  body: AccountUpdateRequest;
  path?: never;
  query?: never;
  url: "/v1/account";
};

export type appsApiAccountUpdateAccountResponses = {
  /**
   * OK
   */
  200: AppAccountResponse;
};

export type appsApiAccountUpdateAccountResponse =
  appsApiAccountUpdateAccountResponses[keyof appsApiAccountUpdateAccountResponses];

export type appsApiAccountBillingCreateSessionData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/v1/account/billing/payment-session";
};

export type appsApiAccountBillingCreateSessionResponses = {
  /**
   * OK
   */
  200: CreatePaymentSessionResponse;
};

export type appsApiAccountBillingCreateSessionResponse =
  appsApiAccountBillingCreateSessionResponses[keyof appsApiAccountBillingCreateSessionResponses];

export type appsApiAccountBillingEnsurePaymentMethodAddedData = {
  body: PaymentMethodAddedRequest;
  path?: never;
  query?: never;
  url: "/v1/account/billing/ensure-payment-method-added";
};

export type appsApiAccountBillingEnsurePaymentMethodAddedResponses = {
  /**
   * Response
   *
   * OK
   */
  200: {
    [key: string]: unknown;
  };
};

export type appsApiAccountBillingEnsurePaymentMethodAddedResponse =
  appsApiAccountBillingEnsurePaymentMethodAddedResponses[keyof appsApiAccountBillingEnsurePaymentMethodAddedResponses];

export type appsApiAccountBillingApiGetPaymentMethodData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/v1/account/billing/payment-method";
};

export type appsApiAccountBillingApiGetPaymentMethodResponses = {
  /**
   * OK
   */
  200: PaymentMethodResponse;
};

export type appsApiAccountBillingApiGetPaymentMethodResponse =
  appsApiAccountBillingApiGetPaymentMethodResponses[keyof appsApiAccountBillingApiGetPaymentMethodResponses];

export type appsApiAccountBillingGetBillingAddressData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/v1/account/billing/address";
};

export type appsApiAccountBillingGetBillingAddressErrors = {
  /**
   * Response
   *
   * Not Found
   */
  404: {
    [key: string]: unknown;
  };
};

export type appsApiAccountBillingGetBillingAddressError =
  appsApiAccountBillingGetBillingAddressErrors[keyof appsApiAccountBillingGetBillingAddressErrors];

export type appsApiAccountBillingGetBillingAddressResponses = {
  /**
   * OK
   */
  200: BillingAddressResponse;
};

export type appsApiAccountBillingGetBillingAddressResponse =
  appsApiAccountBillingGetBillingAddressResponses[keyof appsApiAccountBillingGetBillingAddressResponses];

export type appsApiAccountBillingUpdateBillingAddressData = {
  body: UpdateBillingAddressRequest;
  path?: never;
  query?: never;
  url: "/v1/account/billing/address";
};

export type appsApiAccountBillingUpdateBillingAddressResponses = {
  /**
   * Response
   *
   * OK
   */
  200: {
    [key: string]: unknown;
  };
};

export type appsApiAccountBillingUpdateBillingAddressResponse =
  appsApiAccountBillingUpdateBillingAddressResponses[keyof appsApiAccountBillingUpdateBillingAddressResponses];

export type appsApiAccountBillingGetInvoicesData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Limit
     *
     * Maximum number of results to return per page.
     */
    limit?: number;
    /**
     * Offset
     *
     * Number of results to skip before the returned page.
     */
    offset?: number;
  };
  url: "/v1/account/billing/invoices";
};

export type appsApiAccountBillingGetInvoicesResponses = {
  /**
   * OK
   */
  200: PagedInvoiceResponse;
};

export type appsApiAccountBillingGetInvoicesResponse =
  appsApiAccountBillingGetInvoicesResponses[keyof appsApiAccountBillingGetInvoicesResponses];

export type appsApiAccountBillingGetInvoiceData = {
  body?: never;
  path: {
    /**
     * Invoice Number
     *
     * Invoice number.
     */
    invoice_number: string;
  };
  query?: never;
  url: "/v1/account/billing/invoices/{invoice_number}";
};

export type appsApiAccountBillingGetInvoiceResponses = {
  /**
   * OK
   */
  200: InvoiceDetailResponse;
};

export type appsApiAccountBillingGetInvoiceResponse =
  appsApiAccountBillingGetInvoiceResponses[keyof appsApiAccountBillingGetInvoiceResponses];

export type appsApiAccountBillingPayInvoiceData = {
  body: PayInvoiceRequest;
  path: {
    /**
     * Invoice Number
     *
     * Invoice number.
     */
    invoice_number: string;
  };
  query?: never;
  url: "/v1/account/billing/invoices/{invoice_number}/pay";
};

export type appsApiAccountBillingPayInvoiceResponses = {
  /**
   * No Content
   */
  204: void;
};

export type appsApiAccountBillingPayInvoiceResponse =
  appsApiAccountBillingPayInvoiceResponses[keyof appsApiAccountBillingPayInvoiceResponses];

export type appsApiAccountComparisonInvoiceGetComparisonInvoiceData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/v1/account/comparison-invoice";
};

export type appsApiAccountComparisonInvoiceGetComparisonInvoiceResponses = {
  /**
   * OK
   */
  200: ComparisonInvoiceResponse;
};

export type appsApiAccountComparisonInvoiceGetComparisonInvoiceResponse =
  appsApiAccountComparisonInvoiceGetComparisonInvoiceResponses[keyof appsApiAccountComparisonInvoiceGetComparisonInvoiceResponses];

export type appsApiAccountComparisonInvoiceUploadComparisonInvoiceData = {
  /**
   * FileParams
   */
  body: {
    /**
     * File
     */
    file: Blob | File;
  };
  path?: never;
  query?: never;
  url: "/v1/account/comparison-invoice";
};

export type appsApiAccountComparisonInvoiceUploadComparisonInvoiceResponses = {
  /**
   * OK
   */
  200: ComparisonInvoiceResponse;
};

export type appsApiAccountComparisonInvoiceUploadComparisonInvoiceResponse =
  appsApiAccountComparisonInvoiceUploadComparisonInvoiceResponses[keyof appsApiAccountComparisonInvoiceUploadComparisonInvoiceResponses];

export type appsApiAccountComparisonInvoiceSimpleCompareData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Plan Group
     *
     * Identifier of the plan to compare against the customer's current plan.
     */
    plan_group?: string | null;
    /**
     * Uuid
     *
     * UUID of a specific comparison invoice to use; the most recent is used when omitted.
     */
    uuid?: string | null;
  };
  url: "/v1/account/comparison-invoice/simple-compare";
};

export type appsApiAccountComparisonInvoiceSimpleCompareResponses = {
  /**
   * OK
   */
  200: ComparisonInvoiceSimpleCompareResponse;
};

export type appsApiAccountComparisonInvoiceSimpleCompareResponse =
  appsApiAccountComparisonInvoiceSimpleCompareResponses[keyof appsApiAccountComparisonInvoiceSimpleCompareResponses];

export type appsApiAccountComparisonInvoiceAnnualCompareData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Plan Group
     *
     * Identifier of the plan to compare against the customer's current plan.
     */
    plan_group?: string | null;
    /**
     * Uuid
     *
     * UUID of a specific comparison invoice to use; the most recent is used when omitted.
     */
    uuid?: string | null;
  };
  url: "/v1/account/comparison-invoice/annual-compare";
};

export type appsApiAccountComparisonInvoiceAnnualCompareResponses = {
  /**
   * OK
   */
  200: AnnualCompareResponse;
};

export type appsApiAccountComparisonInvoiceAnnualCompareResponse =
  appsApiAccountComparisonInvoiceAnnualCompareResponses[keyof appsApiAccountComparisonInvoiceAnnualCompareResponses];

export type appsApiAccountEnrollAddressSearchData = {
  body: AddressSearchRequest;
  path?: never;
  query?: never;
  url: "/v1/account/enroll/address-search";
};

export type appsApiAccountEnrollAddressSearchResponses = {
  /**
   * Response
   *
   * OK
   */
  200: Array<AddressSearchResponse>;
};

export type appsApiAccountEnrollAddressSearchResponse =
  appsApiAccountEnrollAddressSearchResponses[keyof appsApiAccountEnrollAddressSearchResponses];

export type appsApiAccountEnrollPreliminaryAddressSearchData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/v1/account/enroll/preliminary-address-search";
};

export type appsApiAccountEnrollPreliminaryAddressSearchResponses = {
  /**
   * Response
   *
   * OK
   */
  200: Array<AddressSearchResponse>;
};

export type appsApiAccountEnrollPreliminaryAddressSearchResponse =
  appsApiAccountEnrollPreliminaryAddressSearchResponses[keyof appsApiAccountEnrollPreliminaryAddressSearchResponses];

export type appsApiAccountEnrollEsiIdSearchData = {
  body: ESIIDSearchRequest;
  path?: never;
  query?: never;
  url: "/v1/account/enroll/esi-id-search";
};

export type appsApiAccountEnrollEsiIdSearchResponses = {
  /**
   * OK
   */
  200: AddressSearchResponse;
};

export type appsApiAccountEnrollEsiIdSearchResponse =
  appsApiAccountEnrollEsiIdSearchResponses[keyof appsApiAccountEnrollEsiIdSearchResponses];

export type appsApiAccountEnrollUpdateIdentityData = {
  body: IdentityRequest;
  path?: never;
  query?: never;
  url: "/v1/account/enroll/identity";
};

export type appsApiAccountEnrollUpdateIdentityResponses = {
  /**
   * Response
   *
   * OK
   */
  200: {
    [key: string]: unknown;
  };
};

export type appsApiAccountEnrollUpdateIdentityResponse =
  appsApiAccountEnrollUpdateIdentityResponses[keyof appsApiAccountEnrollUpdateIdentityResponses];

export type appsApiAccountEnrollPerformCreditCheckData = {
  body: CreditCheckRequest;
  path?: never;
  query?: never;
  url: "/v1/account/enroll/credit-check";
};

export type appsApiAccountEnrollPerformCreditCheckResponses = {
  /**
   * Response
   *
   * OK
   */
  200: {
    [key: string]: unknown;
  };
};

export type appsApiAccountEnrollPerformCreditCheckResponse =
  appsApiAccountEnrollPerformCreditCheckResponses[keyof appsApiAccountEnrollPerformCreditCheckResponses];

export type appsApiAccountEnrollGetPlanGroupsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/v1/account/enroll/plan-groups";
};

export type appsApiAccountEnrollGetPlanGroupsResponses = {
  /**
   * Response
   *
   * OK
   */
  200: Array<PlanGroupResponse>;
};

export type appsApiAccountEnrollGetPlanGroupsResponse =
  appsApiAccountEnrollGetPlanGroupsResponses[keyof appsApiAccountEnrollGetPlanGroupsResponses];

export type appsApiAccountEnrollRequestPlansData = {
  body: PlanRequest;
  path?: never;
  query?: never;
  url: "/v1/account/enroll/plans/request";
};

export type appsApiAccountEnrollRequestPlansResponses = {
  /**
   * Response
   *
   * OK
   */
  200: Array<PlanResponse>;
};

export type appsApiAccountEnrollRequestPlansResponse =
  appsApiAccountEnrollRequestPlansResponses[keyof appsApiAccountEnrollRequestPlansResponses];

export type appsApiAccountEnrollAcceptPlanData = {
  body: AcceptPlanRequest;
  path?: never;
  query?: never;
  url: "/v1/account/enroll/plans/accept";
};

export type appsApiAccountEnrollAcceptPlanResponses = {
  /**
   * Response
   *
   * Created
   */
  201: {
    [key: string]: unknown;
  };
};

export type appsApiAccountEnrollAcceptPlanResponse =
  appsApiAccountEnrollAcceptPlanResponses[keyof appsApiAccountEnrollAcceptPlanResponses];

export type appsApiAccountEnrollGetPlanData = {
  body?: never;
  path: {
    /**
     * Plan Uuid
     *
     * UUID of the plan.
     */
    plan_uuid: string;
  };
  query?: never;
  url: "/v1/account/enroll/plans/{plan_uuid}";
};

export type appsApiAccountEnrollGetPlanResponses = {
  /**
   * OK
   */
  200: PlanResponse;
};

export type appsApiAccountEnrollGetPlanResponse =
  appsApiAccountEnrollGetPlanResponses[keyof appsApiAccountEnrollGetPlanResponses];

export type appsApiAccountEnrollFinalizeEnrollmentData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/v1/account/enroll/finalize";
};

export type appsApiAccountEnrollFinalizeEnrollmentResponses = {
  /**
   * Response
   *
   * OK
   */
  200: {
    [key: string]: unknown;
  };
};

export type appsApiAccountEnrollFinalizeEnrollmentResponse =
  appsApiAccountEnrollFinalizeEnrollmentResponses[keyof appsApiAccountEnrollFinalizeEnrollmentResponses];

export type appsApiAccountEnrollResetEnrollmentData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/v1/account/enroll/reset";
};

export type appsApiAccountEnrollResetEnrollmentResponses = {
  /**
   * Response
   *
   * OK
   */
  200: {
    [key: string]: unknown;
  };
};

export type appsApiAccountEnrollResetEnrollmentResponse =
  appsApiAccountEnrollResetEnrollmentResponses[keyof appsApiAccountEnrollResetEnrollmentResponses];

export type appsApiAccountEnrollCheckEligibilityData = {
  body: EligibilityRequest;
  path?: never;
  query?: never;
  url: "/v1/account/enroll/eligibility";
};

export type appsApiAccountEnrollCheckEligibilityResponses = {
  /**
   * OK
   */
  200: EligibilityResponse;
};

export type appsApiAccountEnrollCheckEligibilityResponse =
  appsApiAccountEnrollCheckEligibilityResponses[keyof appsApiAccountEnrollCheckEligibilityResponses];

export type appsApiAccountEnrollGetBenchmarksData = {
  body: BenchmarksRequest;
  path?: never;
  query?: never;
  url: "/v1/account/enroll/benchmarks";
};

export type appsApiAccountEnrollGetBenchmarksResponses = {
  /**
   * OK
   */
  200: BenchmarksResponse;
};

export type appsApiAccountEnrollGetBenchmarksResponse =
  appsApiAccountEnrollGetBenchmarksResponses[keyof appsApiAccountEnrollGetBenchmarksResponses];

export type appsApiAccountEnrollSupportCallbackData = {
  body: SupportCallbackRequest;
  path?: never;
  query?: never;
  url: "/v1/account/enroll/support-callback";
};

export type appsApiAccountEnrollSupportCallbackResponses = {
  /**
   * Response
   *
   * OK
   */
  200: {
    [key: string]: unknown;
  };
};

export type appsApiAccountEnrollSupportCallbackResponse =
  appsApiAccountEnrollSupportCallbackResponses[keyof appsApiAccountEnrollSupportCallbackResponses];

export type appsApiAccountEnrollSupportCallbackAvailabilityData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/v1/account/enroll/support-callback-availability";
};

export type appsApiAccountEnrollSupportCallbackAvailabilityResponses = {
  /**
   * OK
   */
  200: SupportCallbackAvailabilityResponse;
};

export type appsApiAccountEnrollSupportCallbackAvailabilityResponse =
  appsApiAccountEnrollSupportCallbackAvailabilityResponses[keyof appsApiAccountEnrollSupportCallbackAvailabilityResponses];

export type appsApiAccountEnrollGetDisallowedDatesData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/v1/account/enroll/disallowed-dates";
};

export type appsApiAccountEnrollGetDisallowedDatesResponses = {
  /**
   * OK
   */
  200: DisallowedDatesResponse;
};

export type appsApiAccountEnrollGetDisallowedDatesResponse =
  appsApiAccountEnrollGetDisallowedDatesResponses[keyof appsApiAccountEnrollGetDisallowedDatesResponses];

export type appsApiAccountEnrollListEnrollmentOptOutReasonsData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Survey filter. Returns a curated list of reasons for that flow. One of `enrollment_switch`, `enrollment_move_in`, or `renewal`. Omit to return every defined reason.
     */
    filter?: OptOutFilter | null;
  };
  url: "/v1/account/enroll/opt-out/reasons";
};

export type appsApiAccountEnrollListEnrollmentOptOutReasonsResponses = {
  /**
   * Response
   *
   * OK
   */
  200: Array<OptOutReasonOption>;
};

export type appsApiAccountEnrollListEnrollmentOptOutReasonsResponse =
  appsApiAccountEnrollListEnrollmentOptOutReasonsResponses[keyof appsApiAccountEnrollListEnrollmentOptOutReasonsResponses];

export type appsApiAccountEnrollRecordEnrollmentOptOutEventData = {
  body: CustomerEnrollmentOptOutRequest;
  path?: never;
  query?: never;
  url: "/v1/account/enroll/opt-out";
};

export type appsApiAccountEnrollRecordEnrollmentOptOutEventResponses = {
  /**
   * Created
   */
  201: EnrollmentEventResponse;
};

export type appsApiAccountEnrollRecordEnrollmentOptOutEventResponse =
  appsApiAccountEnrollRecordEnrollmentOptOutEventResponses[keyof appsApiAccountEnrollRecordEnrollmentOptOutEventResponses];

export type appsApiAccountVehiclesAuthorizeVehicleData = {
  body: AuthorizeVehicleRequest;
  path?: never;
  query?: never;
  url: "/v1/account/vehicles/authorize";
};

export type appsApiAccountVehiclesAuthorizeVehicleResponses = {
  /**
   * OK
   */
  200: AuthorizeVehicleResponse;
};

export type appsApiAccountVehiclesAuthorizeVehicleResponse =
  appsApiAccountVehiclesAuthorizeVehicleResponses[keyof appsApiAccountVehiclesAuthorizeVehicleResponses];

export type appsApiAccountVehiclesCheckVehicleAuthorizedData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/v1/account/vehicles/check-authorized";
};

export type appsApiAccountVehiclesCheckVehicleAuthorizedResponses = {
  /**
   * Response
   *
   * OK
   */
  200: {
    [key: string]: unknown;
  };
};

export type appsApiAccountVehiclesCheckVehicleAuthorizedResponse =
  appsApiAccountVehiclesCheckVehicleAuthorizedResponses[keyof appsApiAccountVehiclesCheckVehicleAuthorizedResponses];

export type appsApiAccountVehiclesListVehiclesData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Limit
     *
     * Maximum number of results to return per page.
     */
    limit?: number;
    /**
     * Offset
     *
     * Number of results to skip before the returned page.
     */
    offset?: number;
  };
  url: "/v1/account/vehicles/";
};

export type appsApiAccountVehiclesListVehiclesResponses = {
  /**
   * OK
   */
  200: PagedVehicle;
};

export type appsApiAccountVehiclesListVehiclesResponse =
  appsApiAccountVehiclesListVehiclesResponses[keyof appsApiAccountVehiclesListVehiclesResponses];

export type appsApiAccountVehiclesEnrollVehiclesData = {
  /**
   * Payload
   */
  body: Array<string>;
  path?: never;
  query?: {
    /**
     * Limit
     *
     * Maximum number of results to return per page.
     */
    limit?: number;
    /**
     * Offset
     *
     * Number of results to skip before the returned page.
     */
    offset?: number;
  };
  url: "/v1/account/vehicles/enroll";
};

export type appsApiAccountVehiclesEnrollVehiclesResponses = {
  /**
   * OK
   */
  200: PagedVehicle;
};

export type appsApiAccountVehiclesEnrollVehiclesResponse =
  appsApiAccountVehiclesEnrollVehiclesResponses[keyof appsApiAccountVehiclesEnrollVehiclesResponses];

export type appsApiAccountLocationsLocationsGetPlanData = {
  body?: never;
  path: {
    /**
     * Location Uuid
     *
     * UUID of the service location.
     */
    location_uuid: string;
  };
  query?: never;
  url: "/v1/account/locations/{location_uuid}/plan";
};

export type appsApiAccountLocationsLocationsGetPlanResponses = {
  /**
   * OK
   */
  200: LocationPlan;
};

export type appsApiAccountLocationsLocationsGetPlanResponse =
  appsApiAccountLocationsLocationsGetPlanResponses[keyof appsApiAccountLocationsLocationsGetPlanResponses];

export type appsApiAccountLocationsLocationsGetAllPlansData = {
  body?: never;
  path: {
    /**
     * Location Uuid
     *
     * UUID of the service location.
     */
    location_uuid: string;
  };
  query?: {
    /**
     * Limit
     *
     * Maximum number of results to return per page.
     */
    limit?: number;
    /**
     * Offset
     *
     * Number of results to skip before the returned page.
     */
    offset?: number;
  };
  url: "/v1/account/locations/{location_uuid}/plans";
};

export type appsApiAccountLocationsLocationsGetAllPlansResponses = {
  /**
   * OK
   */
  200: PagedLocationPlan;
};

export type appsApiAccountLocationsLocationsGetAllPlansResponse =
  appsApiAccountLocationsLocationsGetAllPlansResponses[keyof appsApiAccountLocationsLocationsGetAllPlansResponses];

export type appsApiAccountLocationsLocationsGetActiveDocumentsData = {
  body?: never;
  path: {
    /**
     * Location Uuid
     *
     * UUID of the service location.
     */
    location_uuid: string;
  };
  query?: never;
  url: "/v1/account/locations/{location_uuid}/documents";
};

export type appsApiAccountLocationsLocationsGetActiveDocumentsResponses = {
  /**
   * OK
   */
  200: DocumentsResponse;
};

export type appsApiAccountLocationsLocationsGetActiveDocumentsResponse =
  appsApiAccountLocationsLocationsGetActiveDocumentsResponses[keyof appsApiAccountLocationsLocationsGetActiveDocumentsResponses];

export type appsApiAccountLocationsLocationsGetAllDocumentsData = {
  body?: never;
  path: {
    /**
     * Location Uuid
     *
     * UUID of the service location.
     */
    location_uuid: string;
  };
  query?: {
    /**
     * Limit
     *
     * Maximum number of results to return per page.
     */
    limit?: number;
    /**
     * Offset
     *
     * Number of results to skip before the returned page.
     */
    offset?: number;
  };
  url: "/v1/account/locations/{location_uuid}/all-documents";
};

export type appsApiAccountLocationsLocationsGetAllDocumentsResponses = {
  /**
   * OK
   */
  200: PagedDocumentsResponse;
};

export type appsApiAccountLocationsLocationsGetAllDocumentsResponse =
  appsApiAccountLocationsLocationsGetAllDocumentsResponses[keyof appsApiAccountLocationsLocationsGetAllDocumentsResponses];

export type appsApiAccountLocationsLocationsGetSupportInfoData = {
  body?: never;
  path: {
    /**
     * Location Uuid
     *
     * UUID of the service location.
     */
    location_uuid: string;
  };
  query?: never;
  url: "/v1/account/locations/{location_uuid}/support-info";
};

export type appsApiAccountLocationsLocationsGetSupportInfoResponses = {
  /**
   * OK
   */
  200: SupportInfoResponse;
};

export type appsApiAccountLocationsLocationsGetSupportInfoResponse =
  appsApiAccountLocationsLocationsGetSupportInfoResponses[keyof appsApiAccountLocationsLocationsGetSupportInfoResponses];

export type appsApiAccountLocationsLocationsCancelServiceData = {
  body: CancelServiceRequest;
  path: {
    /**
     * Location Uuid
     *
     * UUID of the service location.
     */
    location_uuid: string;
  };
  query?: never;
  url: "/v1/account/locations/{location_uuid}/cancel-service";
};

export type appsApiAccountLocationsLocationsCancelServiceResponses = {
  /**
   * Response
   *
   * OK
   */
  200: {
    [key: string]: unknown;
  };
};

export type appsApiAccountLocationsLocationsCancelServiceResponse =
  appsApiAccountLocationsLocationsCancelServiceResponses[keyof appsApiAccountLocationsLocationsCancelServiceResponses];

export type appsApiAccountLocationsLocationsGetUsageFromMeterReadsData = {
  body?: never;
  path: {
    /**
     * Location Uuid
     *
     * UUID of the service location.
     */
    location_uuid: string;
  };
  query?: never;
  url: "/v1/account/locations/{location_uuid}/usage/meter-reads";
};

export type appsApiAccountLocationsLocationsGetUsageFromMeterReadsResponses = {
  /**
   * Response
   *
   * OK
   */
  200: Array<UsagePeriodResponse>;
};

export type appsApiAccountLocationsLocationsGetUsageFromMeterReadsResponse =
  appsApiAccountLocationsLocationsGetUsageFromMeterReadsResponses[keyof appsApiAccountLocationsLocationsGetUsageFromMeterReadsResponses];

export type appsApiAccountLocationsLocationsGetMonthlyUsageIntervalsData = {
  body?: never;
  path: {
    /**
     * Location Uuid
     *
     * UUID of the service location.
     */
    location_uuid: string;
  };
  query?: {
    /**
     * Year
     *
     * Calendar year to return usage for.
     */
    year?: number | null;
  };
  url: "/v1/account/locations/{location_uuid}/usage/monthly";
};

export type appsApiAccountLocationsLocationsGetMonthlyUsageIntervalsResponses =
  {
    /**
     * OK
     */
    200: MonthlyUsageDataResponse;
  };

export type appsApiAccountLocationsLocationsGetMonthlyUsageIntervalsResponse =
  appsApiAccountLocationsLocationsGetMonthlyUsageIntervalsResponses[keyof appsApiAccountLocationsLocationsGetMonthlyUsageIntervalsResponses];

export type appsApiAccountLocationsLocationsGetDailyUsageIntervalsData = {
  body?: never;
  path: {
    /**
     * Location Uuid
     *
     * UUID of the service location.
     */
    location_uuid: string;
  };
  query?: {
    /**
     * Month
     *
     * Calendar month (1-12) to return usage for.
     */
    month?: number | null;
    /**
     * Year
     *
     * Calendar year to return usage for.
     */
    year?: number | null;
  };
  url: "/v1/account/locations/{location_uuid}/usage/daily";
};

export type appsApiAccountLocationsLocationsGetDailyUsageIntervalsResponses = {
  /**
   * OK
   */
  200: DailyUsageDataResponse;
};

export type appsApiAccountLocationsLocationsGetDailyUsageIntervalsResponse =
  appsApiAccountLocationsLocationsGetDailyUsageIntervalsResponses[keyof appsApiAccountLocationsLocationsGetDailyUsageIntervalsResponses];

export type appsApiAccountLocationsLocationsGetUsageIntervalsData = {
  body?: never;
  path: {
    /**
     * Location Uuid
     *
     * UUID of the service location.
     */
    location_uuid: string;
  };
  query?: {
    /**
     * Month
     *
     * Calendar month (1-12) to return usage for.
     */
    month?: number | null;
    /**
     * Year
     *
     * Calendar year to return usage for.
     */
    year?: number | null;
  };
  url: "/v1/account/locations/{location_uuid}/usage/intervals";
};

export type appsApiAccountLocationsLocationsGetUsageIntervalsResponses = {
  /**
   * OK
   */
  200: IntervalUsageDataResponse;
};

export type appsApiAccountLocationsLocationsGetUsageIntervalsResponse =
  appsApiAccountLocationsLocationsGetUsageIntervalsResponses[keyof appsApiAccountLocationsLocationsGetUsageIntervalsResponses];

export type appsApiAccountLocationsRenewalPlansRequestPlansData = {
  body?: never;
  path: {
    /**
     * Location Uuid
     *
     * UUID of the service location.
     */
    location_uuid: string;
  };
  query?: never;
  url: "/v1/account/locations/{location_uuid}/renewal-plans/request";
};

export type appsApiAccountLocationsRenewalPlansRequestPlansResponses = {
  /**
   * Response
   *
   * OK
   */
  200: Array<RenewalPlanResponse>;
};

export type appsApiAccountLocationsRenewalPlansRequestPlansResponse =
  appsApiAccountLocationsRenewalPlansRequestPlansResponses[keyof appsApiAccountLocationsRenewalPlansRequestPlansResponses];

export type appsApiAccountLocationsRenewalPlansAcceptPlanData = {
  body: AcceptRenewalPlanRequest;
  path: {
    /**
     * Location Uuid
     *
     * UUID of the service location.
     */
    location_uuid: string;
  };
  query?: never;
  url: "/v1/account/locations/{location_uuid}/renewal-plans/accept";
};

export type appsApiAccountLocationsRenewalPlansAcceptPlanResponses = {
  /**
   * Response
   *
   * Created
   */
  201: {
    [key: string]: unknown;
  };
};

export type appsApiAccountLocationsRenewalPlansAcceptPlanResponse =
  appsApiAccountLocationsRenewalPlansAcceptPlanResponses[keyof appsApiAccountLocationsRenewalPlansAcceptPlanResponses];

export type appsApiAccountLocationsSwitchPlanOffersAcceptSwitchPlanData = {
  body: AcceptSwitchPlanRequest;
  path: {
    /**
     * Location Uuid
     */
    location_uuid: string;
    /**
     * Offer Uuid
     */
    offer_uuid: string;
  };
  query?: never;
  url: "/v1/account/locations/{location_uuid}/switch-plan-offers/{offer_uuid}/accept";
};

export type appsApiAccountLocationsSwitchPlanOffersAcceptSwitchPlanResponses = {
  /**
   * Response
   *
   * Created
   */
  201: {
    [key: string]: unknown;
  };
};

export type appsApiAccountLocationsSwitchPlanOffersAcceptSwitchPlanResponse =
  appsApiAccountLocationsSwitchPlanOffersAcceptSwitchPlanResponses[keyof appsApiAccountLocationsSwitchPlanOffersAcceptSwitchPlanResponses];

export type appsApiProgramsAccountRequestProgramsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/v1/account/programs/request";
};

export type appsApiProgramsAccountRequestProgramsResponses = {
  /**
   * Response
   *
   * OK
   */
  200: Array<Program>;
};

export type appsApiProgramsAccountRequestProgramsResponse =
  appsApiProgramsAccountRequestProgramsResponses[keyof appsApiProgramsAccountRequestProgramsResponses];

export type appsApiProgramsAccountListProgramAgreementsData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Limit
     *
     * Maximum number of results to return per page.
     */
    limit?: number;
    /**
     * Offset
     *
     * Number of results to skip before the returned page.
     */
    offset?: number;
  };
  url: "/v1/account/programs/agreements";
};

export type appsApiProgramsAccountListProgramAgreementsResponses = {
  /**
   * OK
   */
  200: PagedProgramAgreement;
};

export type appsApiProgramsAccountListProgramAgreementsResponse =
  appsApiProgramsAccountListProgramAgreementsResponses[keyof appsApiProgramsAccountListProgramAgreementsResponses];

export type appsApiDevicesAccountDevicesListManufacturersData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Device Type
     */
    device_type?: "BATTERY" | "EVSE" | "VEHICLE" | null;
  };
  url: "/v1/account/devices/manufacturers";
};

export type appsApiDevicesAccountDevicesListManufacturersResponses = {
  /**
   * Response
   *
   * OK
   */
  200: Array<DeviceManufacturer>;
};

export type appsApiDevicesAccountDevicesListManufacturersResponse =
  appsApiDevicesAccountDevicesListManufacturersResponses[keyof appsApiDevicesAccountDevicesListManufacturersResponses];

export type appsApiDevicesAccountDevicesLinkDeviceAccountData = {
  body: AccountDeviceLinkRequest;
  path?: never;
  query?: never;
  url: "/v1/account/devices/link";
};

export type appsApiDevicesAccountDevicesLinkDeviceAccountResponses = {
  /**
   * OK
   */
  200: DeviceLinkResponse;
};

export type appsApiDevicesAccountDevicesLinkDeviceAccountResponse =
  appsApiDevicesAccountDevicesLinkDeviceAccountResponses[keyof appsApiDevicesAccountDevicesLinkDeviceAccountResponses];

export type appsApiDevicesAccountDevicesDiscoverLinkedDevicesData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/v1/account/devices/discover";
};

export type appsApiDevicesAccountDevicesDiscoverLinkedDevicesResponses = {
  /**
   * No Content
   */
  204: void;
};

export type appsApiDevicesAccountDevicesDiscoverLinkedDevicesResponse =
  appsApiDevicesAccountDevicesDiscoverLinkedDevicesResponses[keyof appsApiDevicesAccountDevicesDiscoverLinkedDevicesResponses];

export type appsApiDevicesAccountDevicesGetDeviceData = {
  body?: never;
  path: {
    /**
     * Device Uuid
     */
    device_uuid: string;
  };
  query?: never;
  url: "/v1/account/devices/{device_uuid}";
};

export type appsApiDevicesAccountDevicesGetDeviceResponses = {
  /**
   * OK
   */
  200: Device;
};

export type appsApiDevicesAccountDevicesGetDeviceResponse =
  appsApiDevicesAccountDevicesGetDeviceResponses[keyof appsApiDevicesAccountDevicesGetDeviceResponses];

export type appsApiDevicesAccountDevicesEnrollData = {
  body: EnrollDevice;
  path: {
    /**
     * Device Uuid
     */
    device_uuid: string;
  };
  query?: never;
  url: "/v1/account/devices/{device_uuid}/enroll";
};

export type appsApiDevicesAccountDevicesEnrollResponses = {
  /**
   * OK
   */
  200: DeviceEnrollment;
};

export type appsApiDevicesAccountDevicesEnrollResponse =
  appsApiDevicesAccountDevicesEnrollResponses[keyof appsApiDevicesAccountDevicesEnrollResponses];

export type appsApiDevicesAccountDevicesListAccountDevicesData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Limit
     *
     * Maximum number of results to return per page.
     */
    limit?: number;
    /**
     * Offset
     *
     * Number of results to skip before the returned page.
     */
    offset?: number;
  };
  url: "/v1/account/devices";
};

export type appsApiDevicesAccountDevicesListAccountDevicesResponses = {
  /**
   * OK
   */
  200: PagedDevice;
};

export type appsApiDevicesAccountDevicesListAccountDevicesResponse =
  appsApiDevicesAccountDevicesListAccountDevicesResponses[keyof appsApiDevicesAccountDevicesListAccountDevicesResponses];

export type appsApiAccountSandboxSeedDemoDataData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/v1/account/sandbox/seed-demo-data";
};

export type appsApiAccountSandboxSeedDemoDataResponses = {
  /**
   * Response
   *
   * OK
   */
  200: {
    [key: string]: unknown;
  };
};

export type appsApiAccountSandboxSeedDemoDataResponse =
  appsApiAccountSandboxSeedDemoDataResponses[keyof appsApiAccountSandboxSeedDemoDataResponses];

export type appsApiAccountSandboxSimulateServiceDropData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/v1/account/sandbox/simulate-service-drop";
};

export type appsApiAccountSandboxSimulateServiceDropResponses = {
  /**
   * Response
   *
   * OK
   */
  200: {
    [key: string]: unknown;
  };
};

export type appsApiAccountSandboxSimulateServiceDropResponse =
  appsApiAccountSandboxSimulateServiceDropResponses[keyof appsApiAccountSandboxSimulateServiceDropResponses];

export type appsApiAccountSandboxDeleteDemoDataData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/v1/account/sandbox/delete-demo-data";
};

export type appsApiAccountSandboxDeleteDemoDataResponses = {
  /**
   * Response
   *
   * OK
   */
  200: {
    [key: string]: unknown;
  };
};

export type appsApiAccountSandboxDeleteDemoDataResponse =
  appsApiAccountSandboxDeleteDemoDataResponses[keyof appsApiAccountSandboxDeleteDemoDataResponses];

export type appsApiAccountSandboxDeleteEnrollmentData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/v1/account/sandbox/delete-enrollment";
};

export type appsApiAccountSandboxDeleteEnrollmentResponses = {
  /**
   * Response
   *
   * OK
   */
  200: {
    [key: string]: unknown;
  };
};

export type appsApiAccountSandboxDeleteEnrollmentResponse =
  appsApiAccountSandboxDeleteEnrollmentResponses[keyof appsApiAccountSandboxDeleteEnrollmentResponses];

export type appsApiAccountSandboxSimulateCreditIssueData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/v1/account/sandbox/simulate-credit-issue";
};

export type appsApiAccountSandboxSimulateCreditIssueResponses = {
  /**
   * Response
   *
   * OK
   */
  200: {
    [key: string]: unknown;
  };
};

export type appsApiAccountSandboxSimulateCreditIssueResponse =
  appsApiAccountSandboxSimulateCreditIssueResponses[keyof appsApiAccountSandboxSimulateCreditIssueResponses];

export type appsApiAccountSandboxSimulateEnrollmentSubmittedData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/v1/account/sandbox/simulate-enrollment-submitted";
};

export type appsApiAccountSandboxSimulateEnrollmentSubmittedResponses = {
  /**
   * Response
   *
   * OK
   */
  200: {
    [key: string]: unknown;
  };
};

export type appsApiAccountSandboxSimulateEnrollmentSubmittedResponse =
  appsApiAccountSandboxSimulateEnrollmentSubmittedResponses[keyof appsApiAccountSandboxSimulateEnrollmentSubmittedResponses];

export type appsApiAccountSandboxSimulateServiceStartData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/v1/account/sandbox/simulate-service-start";
};

export type appsApiAccountSandboxSimulateServiceStartResponses = {
  /**
   * Response
   *
   * OK
   */
  200: {
    [key: string]: unknown;
  };
};

export type appsApiAccountSandboxSimulateServiceStartResponse =
  appsApiAccountSandboxSimulateServiceStartResponses[keyof appsApiAccountSandboxSimulateServiceStartResponses];

export type appsApiAccountSandboxSimulateExpiringData = {
  body: SimulateExpiringRequest;
  path?: never;
  query?: never;
  url: "/v1/account/sandbox/simulate-contract-expiring";
};

export type appsApiAccountSandboxSimulateExpiringResponses = {
  /**
   * Response
   *
   * OK
   */
  200: {
    [key: string]: unknown;
  };
};

export type appsApiAccountSandboxSimulateExpiringResponse =
  appsApiAccountSandboxSimulateExpiringResponses[keyof appsApiAccountSandboxSimulateExpiringResponses];

export type vehicleApiHandleWebhookData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/v1/webhooks/enode";
};

export type vehicleApiHandleWebhookResponses = {
  /**
   * No Content
   */
  204: void;
};

export type vehicleApiHandleWebhookResponse =
  vehicleApiHandleWebhookResponses[keyof vehicleApiHandleWebhookResponses];

export type appsDevicesWebhooksFlipHandleWebhookData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/v1/webhooks/flip";
};

export type appsDevicesWebhooksFlipHandleWebhookResponses = {
  /**
   * OK
   */
  200: unknown;
};

export type coreApiPostmarkOutboundWebhookData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/v1/webhooks/postmark/outbound";
};

export type coreApiPostmarkOutboundWebhookResponses = {
  /**
   * No Content
   */
  204: void;
};

export type coreApiPostmarkOutboundWebhookResponse =
  coreApiPostmarkOutboundWebhookResponses[keyof coreApiPostmarkOutboundWebhookResponses];
